import React, { useState , useEffect, useRef, useCallback, useMemo } from 'react';
import Fuse from 'fuse.js';
import { API } from "aws-amplify";
import swal from '@sweetalert/with-react';

import Availability from './Availability';
import EditSectionSecondaryTeacher from './reusable-components/EditSectionSecondaryTeacher';
import { OtherSectionsSearchProvider, useOtherSectionsSearch, resetPersistentSearchState } from '../contexts/OtherSectionsSearchContext';
import Toggle from '../../Toggle';

import loading_gif from "../../../images/balls.gif";

import { createUniqueObjectID, findDeletedValues, findAddedValues, cloneObj, sortArrayOfObjects, useOutsideClick, deleteSection, calculateConflicts, findCourseRequestInStudentRequests } from '../../../js/Helpers';
import { buildPeriodsToShow, getCoursePeriodIdForPeriod, getPeriodNameFromCoursePeriodId, getPeriodTypeFromScheduleType } from '../../../js/PeriodHelpers';
import { getLabelColorClass, getLabelTextColorClass } from '../utils/labelColorUtils';

import '../../../css/hsms/edit_modals.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlusCircle, faTimes, faSearch, faTrashAlt, faSquare, faCheckSquare, faCheck, faUserGraduate, faSortDown, faPeopleArrows, faMinusSquare, faLock, faUnlock, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { faSave, faSquare as farSquare, faObjectUngroup } from '@fortawesome/free-regular-svg-icons';
import { faUserGroupSimple } from '@fortawesome/pro-solid-svg-icons';
import Loader from '../common/Loader';
import { CONFLICT_TYPES, CONFLICT_TYPES_TEXT } from '../../../js/Constants';
import useMultipleRefs from '../hooks/useMultipleRefs';
import { buildCourseSectionLabelMap, buildLabelsById, getVisibleRequestLabels } from '../utils/studentLabelHelpers';
import { useConflictActions } from '../store/magnetboardStore';
import { notifyHSMSDataMutation } from '../../../js/HSMSDataSync';

export default function EditSection({ currentView, currentDay, currentViewType, scheduleType, sectionType, sectionID, dataID, coursePeriodID, toggleEditSection, schoolInfo, scheduleInfo, scheduleStructureData, classroomsLoading, classrooms, coursesLoading, courses, teachersLoading, teachers, studentsLoading, students, sectionsLoading, sections, subsections, labels, setCourses, setStudents, setTeachers, setSections, setSubsections, setLabels, setMatchPercent, setMatchPercentLoading, toggleEditStudentSchedule, allow_hotlinks, filteredResults = [], setFilteredResults = ()=>{}, studentOnlyEditing = false }) {
	const ref = useRef();
	const savedEditStateRef = useRef(null);

	const { setRef, getAllRefs } = useMultipleRefs();
	
	const quarters = useRef(['1', '2', '3', '4']);

	const [isLoading, setIsLoading] = useState(true);
	
	const [periodOptions, setPeriodOptions] = useState([]);
	const [numDaysInCycle, setNumDaysInCycle] = useState([]);

	const [sectionsData, setSectionsData] = useState({});
	const [editingTracker, setEditingTracker] = useState({}); 
	const [searchResults, setSearchResults] = useState({});
	const [sectionNumberDrafts, setSectionNumberDrafts] = useState({});

	const [teachersAvailability, setTeachersAvailability] = useState([]);
	const [studentsAvailability, setStudentsAvailability] = useState([]);
	const [classroomsAvailability, setClassroomsAvailability] = useState([]);
	const [availabilitiesCalculated, setAvailabilitiesCalculated] = useState(false);

	const [sectionsSelectedStudents, setSectionsSelectedStudents] = useState({});
	const [openDropdownSections, setOpenDropdownSections] = useState([]);
	const [sectionsSelectedLockedStudents, setSectionsSelectedLockedStudents] = useState({});

	const [availabilityDataType, setAvailabilityDataType] = useState(null);
	const [availabilityViewID, setAvailabilityViewID] = useState(null);
	const [availabilityDataPosition, setAvailabilityDataPosition] = useState({'top':null, 'left':null, 'right':null});
	
	const [changesMade, setChangesMade] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const courseSectionLabelMap = useMemo(() => buildCourseSectionLabelMap(sections), [sections]);
	const labelsByID = useMemo(() => buildLabelsById(labels), [labels]);

	const { setConflicts, setConflictsLoading, markConflictDataFresh } = useConflictActions();
	const canEditSectionStructure = !studentOnlyEditing;
	const canDeleteSection = !studentOnlyEditing;
	const canLockSections = !studentOnlyEditing;
	const canEditPeriods = !studentOnlyEditing;
	const canEditStudents = true;
	const canEditLabels = !studentOnlyEditing;
	const canMoveOrCopyStudents = !studentOnlyEditing;
	const sectionIsLockedForStudentEditing = (section_id) => isSectionLocked(section_id) && !studentOnlyEditing;

	const restoreSavedEditState = () => {
		const saved_edit_state = savedEditStateRef.current;
		if(!saved_edit_state) return;

		setEditingTracker(cloneObj(saved_edit_state.editingTracker));
		setSearchResults(cloneObj(saved_edit_state.searchResults));
		setSectionsSelectedStudents(cloneObj(saved_edit_state.sectionsSelectedStudents));
		setSectionsSelectedLockedStudents(cloneObj(saved_edit_state.sectionsSelectedLockedStudents));
		setSectionsData(cloneObj(saved_edit_state.sectionsData));
		setChangesMade(false);
	};

	/////////////////////
	///// FUNCTIONS /////
	/////////////////////
	const handleFocus = (event) => event.target.select();
	const sanitizeSectionNumberInput = (value) => `${value ?? ''}`.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
	const isPlaceholderInclusionSectionForSnapshot = (section_info, sections_snapshot = sectionsData) => scheduleInfo.separate_ICR_codes === '0' && section_info && section_info.is_inclusion === '1' && sections_snapshot.main && sections_snapshot.main.course_id === section_info.course_id;
	const isPlaceholderInclusionSection = (section_info) => isPlaceholderInclusionSectionForSnapshot(section_info, sectionsData);
	const sectionRequestIsInclusion = (section_info) => `${section_info?.is_inclusion || '0'}` === '1';
	const getRequestMatchOptionsForSection = (section_info, includeDeleted = false) => ({
		includeDeleted,
		isInclusion: scheduleInfo.separate_ICR_codes === '0' ? sectionRequestIsInclusion(section_info) : null,
	});
	const sectionMatchesRequestContext = (section_info, course_id, is_inclusion) => (
		`${section_info?.course_id}` === `${course_id}` &&
		(scheduleInfo.separate_ICR_codes !== '0' || (`${section_info?.is_inclusion || '0'}` === '1') === is_inclusion)
	);
	const getRequestInclusionValueForSection = (section_info) => (
		scheduleInfo.separate_ICR_codes === '0' && sectionRequestIsInclusion(section_info)
	);
	const getEffectiveSectionSequenceValue = (section_info) => {
		if(!section_info) return null;

		const explicit_sequence = parseInt(section_info.section_sequence, 10);
		if(Number.isInteger(explicit_sequence) && explicit_sequence > 0) return explicit_sequence;

		const normalized_section_number = sanitizeSectionNumberInput(section_info.section_number);
		if(normalized_section_number && /^[0-9]+$/.test(normalized_section_number)) return parseInt(normalized_section_number, 10);

		return null;
	};
	const getSectionNumberSuffix = (index) => {
		let suffix = '';
		let current_index = index;
		while(current_index >= 0)
		{
			suffix = String.fromCharCode(65 + (current_index % 26)) + suffix;
			current_index = Math.floor(current_index / 26) - 1;
		}
		return suffix;
	};
	const isSameCourseSubsectionForSnapshot = (section_id, course_id, sections_snapshot = sectionsData) => {
		if(section_id === 'main' || !course_id || !sections_snapshot.main) return false;
		if(!Array.isArray(sections_snapshot.main.subsections) || !sections_snapshot.main.subsections.includes(section_id)) return false;
		return `${sections_snapshot.main.course_id || ''}` === `${course_id}`;
	};
	const getSameCourseSubsectionIdentifiers = (section_id, course_id, sections_snapshot = sectionsData) => {
		if(!isSameCourseSubsectionForSnapshot(section_id, course_id, sections_snapshot)) return null;

		const main_section_info = sections_snapshot.main;
		const main_section_number = sanitizeSectionNumberInput(main_section_info.section_number);
		const main_section_sequence = getEffectiveSectionSequenceValue(main_section_info);
		if(!main_section_number || main_section_sequence === null) return null;

		const used_numbers = new Set();
		const registerSameCourseSubsectionNumber = (section_info, other_section_id = null) => {
			if(!section_info || !section_info.course_id) return;
			if(other_section_id === section_id) return;
			if(`${section_info.course_id}` !== `${course_id}`) return;
			if(!sanitizeSectionNumberInput(section_info.section_number)?.startsWith(main_section_number)) return;

			const section_number = sanitizeSectionNumberInput(section_info.section_number);
			const suffix = section_number.slice(main_section_number.length);
			if(/^[A-Z]+$/.test(suffix)) used_numbers.add(section_number);
		};

		sections.forEach(section_info => registerSameCourseSubsectionNumber(section_info, section_info.section_id));
		Object.keys(sections_snapshot).forEach(local_section_id => registerSameCourseSubsectionNumber(sections_snapshot[local_section_id], local_section_id));

		let suffix_index = 0;
		let section_number = `${main_section_number}${getSectionNumberSuffix(suffix_index)}`;
		while(used_numbers.has(section_number) || section_number.length > 4)
		{
			suffix_index++;
			if(suffix_index > 701) return {section_number, section_sequence: main_section_sequence};
			section_number = `${main_section_number}${getSectionNumberSuffix(suffix_index)}`;
		}

		return {section_number, section_sequence: main_section_sequence};
	};
	const getConflictingSectionIdsForNumber = (section_id, course_id, section_number, sections_snapshot = sectionsData) => {
		const normalized_section_number = sanitizeSectionNumberInput(section_number);
		if(!course_id || !normalized_section_number) return [];

		const current_section_info = sections_snapshot[section_id];
		const current_saved_section_id = current_section_info && current_section_info.section_id ? `${current_section_info.section_id}` : null;
		const current_span_id = current_section_info && current_section_info.span_id ? `${current_section_info.span_id}` : null;
		const conflicting_section_ids = new Set();
		const registerConflict = (section_info, other_section_id) => {
			if(!section_info || !section_info.course_id || !other_section_id) return;
			if(`${other_section_id}` === `${section_id}`) return;
			if(current_saved_section_id && `${other_section_id}` === current_saved_section_id) return;
			if(`${section_info.course_id}` !== `${course_id}`) return;
			if(current_span_id && section_info.span_id && `${section_info.span_id}` === current_span_id) return;
			if(isPlaceholderInclusionSectionForSnapshot(section_info, sections_snapshot)) return;
			if(sanitizeSectionNumberInput(section_info.section_number) !== normalized_section_number) return;

			conflicting_section_ids.add(`${other_section_id}`);
		};

		sections.forEach(section_info => registerConflict(section_info, section_info.section_id));
		Object.keys(sections_snapshot).forEach(other_section_id => registerConflict(sections_snapshot[other_section_id], other_section_id));

		return Array.from(conflicting_section_ids);
	};
	const getOriginalSavedSectionInfo = (section_id, sections_snapshot = sectionsData) => {
		const current_section_info = sections_snapshot[section_id];
		if(!current_section_info || !current_section_info.section_id) return null;

		return sections.find(section_info => `${section_info.section_id}` === `${current_section_info.section_id}`) || null;
	};
	const getResyncBaseSectionSequence = (section_id, course_id, sections_snapshot = sectionsData) => {
		if(!course_id) return null;

		const current_section_info = sections_snapshot[section_id];
		const original_section_info = getOriginalSavedSectionInfo(section_id, sections_snapshot);
		const original_course_id = original_section_info ? `${original_section_info.course_id || ''}` : null;

		if(original_section_info && original_course_id === `${course_id}`)
		{
			const original_section_sequence = getEffectiveSectionSequenceValue(original_section_info);
			if(original_section_sequence !== null) return original_section_sequence;
		}

		return getEffectiveSectionSequenceValue(current_section_info);
	};
	const getSuggestedSectionIdentifiers = (section_id, course_id, sections_snapshot = sectionsData, {forceNext = false} = {}) => {
		if(!course_id) return {section_number: null, section_sequence: null};

		const same_course_subsection_identifiers = getSameCourseSubsectionIdentifiers(section_id, course_id, sections_snapshot);
		if(same_course_subsection_identifiers) return same_course_subsection_identifiers;

		const current_section_info = sections_snapshot[section_id];
		const current_course_id = current_section_info ? `${current_section_info.course_id || ''}` : null;
		const current_section_number = current_section_info ? sanitizeSectionNumberInput(current_section_info.section_number) : null;
		const current_section_sequence = current_section_info ? getEffectiveSectionSequenceValue(current_section_info) : null;

		if(!forceNext && current_course_id === `${course_id}`)
		{
			if(current_section_number) return {section_number: current_section_number, section_sequence: current_section_sequence};
			if(current_section_sequence !== null) return {section_number: `${current_section_sequence}`, section_sequence: current_section_sequence};
		}

		let max_section_sequence = 0;
		const registerSectionSequence = (section_info, local_section_id = null) => {
			if(!section_info || !section_info.course_id) return;
			if(`${section_info.course_id}` !== `${course_id}`) return;
			if(local_section_id === section_id && forceNext) return;
			if(isPlaceholderInclusionSectionForSnapshot(section_info, sections_snapshot)) return;

			const effective_section_sequence = getEffectiveSectionSequenceValue(section_info);
			if(effective_section_sequence !== null && effective_section_sequence > max_section_sequence) max_section_sequence = effective_section_sequence;
		};

		sections.forEach(section_info => registerSectionSequence(section_info));
		Object.keys(sections_snapshot).forEach(local_section_id => registerSectionSequence(sections_snapshot[local_section_id], local_section_id));

		const next_section_sequence = max_section_sequence + 1;
		return {section_number: `${next_section_sequence}`, section_sequence: next_section_sequence};
	};
	const getResyncedSectionIdentifiers = (section_id, course_id, sections_snapshot = sectionsData) => {
		if(!course_id) return {section_number: null, section_sequence: null};

		const same_course_subsection_identifiers = getSameCourseSubsectionIdentifiers(section_id, course_id, sections_snapshot);
		if(same_course_subsection_identifiers) return same_course_subsection_identifiers;

		const resync_base_section_sequence = getResyncBaseSectionSequence(section_id, course_id, sections_snapshot);
		if(resync_base_section_sequence !== null)
		{
			const automatic_section_number = `${resync_base_section_sequence}`;
			const conflicting_section_ids = getConflictingSectionIdsForNumber(section_id, course_id, automatic_section_number, sections_snapshot);
			if(conflicting_section_ids.length === 0) return {section_number: automatic_section_number, section_sequence: resync_base_section_sequence};
		}

		return getSuggestedSectionIdentifiers(section_id, course_id, sections_snapshot, {forceNext: true});
	};
	const shouldShowResyncSectionNumber = (section_id) => {
		const section_info = sectionsData[section_id];
		if(!section_info || !section_info.course_id || isPlaceholderInclusionSection(section_info)) return false;
		if(isSameCourseSubsectionForSnapshot(section_id, section_info.course_id, sectionsData)) return false;

		const current_section_number = sanitizeSectionNumberInput(section_info.section_number);
		const resync_base_section_sequence = getResyncBaseSectionSequence(section_id, section_info.course_id, sectionsData);
		if(resync_base_section_sequence === null) return false;

		const automatic_section_number = `${resync_base_section_sequence}`;

		return current_section_number !== automatic_section_number;
	};
	const applySuggestedSectionIdentifiers = (section_id, course_id, sections_snapshot = sectionsData, options = {}) => {
		const section_info = sections_snapshot[section_id];
		if(!section_info) return;

		if(!course_id)
		{
			section_info.section_number = null;
			section_info.section_sequence = null;
			return;
		}

		if(isPlaceholderInclusionSectionForSnapshot(section_info, sections_snapshot) && !isSameCourseSubsectionForSnapshot(section_id, course_id, sections_snapshot)) return;

		const suggested_identifiers = getSuggestedSectionIdentifiers(section_id, course_id, sections_snapshot, options);
		section_info.section_number = suggested_identifiers.section_number;
		section_info.section_sequence = suggested_identifiers.section_sequence;
	};
	const resyncSectionNumber = (section_id) => {
		const section_info = sectionsData[section_id];
		if(!section_info || !section_info.course_id || isPlaceholderInclusionSection(section_info)) return;
		if(isSameCourseSubsectionForSnapshot(section_id, section_info.course_id, sectionsData)) return;

		const current_section_number = sanitizeSectionNumberInput(section_info.section_number);
		const current_section_sequence = getEffectiveSectionSequenceValue(section_info);
		const resynced_identifiers = getResyncedSectionIdentifiers(section_id, section_info.course_id, sectionsData);
		const next_section_number = sanitizeSectionNumberInput(resynced_identifiers.section_number);

		if(current_section_number === next_section_number && current_section_sequence === resynced_identifiers.section_sequence)
		{
			if(editingTracker[section_id]?.section_number) setSectionNumberDrafts(current_drafts => ({...current_drafts, [section_id]: next_section_number || ''}));
			return;
		}

		section_info.section_number = resynced_identifiers.section_number;
		section_info.section_sequence = resynced_identifiers.section_sequence;
		if(section_id === 'main') syncPlaceholderInclusionSubsectionsWithMain(sectionsData);

		setSectionsData({...sectionsData});
		setSectionNumberDrafts(current_drafts => ({...current_drafts, [section_id]: next_section_number || ''}));
		setChangesMade(true);
	};
	const syncPlaceholderInclusionSubsectionsWithMain = (sections_snapshot = sectionsData) => {
		if(scheduleInfo.separate_ICR_codes !== '0' || !sections_snapshot.main) return;

		const main_section_info = sections_snapshot.main;
		(main_section_info.subsections || []).forEach(subsection_id => {
			const subsection_info = sections_snapshot[subsection_id];
			if(!subsection_info || subsection_info.is_inclusion !== '1') return;

			subsection_info.course_id = main_section_info.course_id;
			const same_course_subsection_identifiers = getSameCourseSubsectionIdentifiers(subsection_id, main_section_info.course_id, sections_snapshot);
			if(same_course_subsection_identifiers)
			{
				subsection_info.section_number = same_course_subsection_identifiers.section_number;
				subsection_info.section_sequence = same_course_subsection_identifiers.section_sequence;
			}
			else
			{
				subsection_info.section_number = main_section_info.section_number;
				subsection_info.section_sequence = main_section_info.section_sequence;
			}
		});
	};
	const startSectionNumberEditing = (section_id) => {
		const section_info = sectionsData[section_id];
		if(!section_info || !section_info.course_id || isPlaceholderInclusionSection(section_info)) return;
		if(isSameCourseSubsectionForSnapshot(section_id, section_info.course_id, sectionsData)) return;

		if(!sanitizeSectionNumberInput(section_info.section_number))
		{
			applySuggestedSectionIdentifiers(section_id, section_info.course_id);
			setSectionsData({...sectionsData});
		}

		setSectionNumberDrafts(current_drafts => ({
			...current_drafts,
			[section_id]: sanitizeSectionNumberInput(section_info.section_number) || '',
		}));

		if(!editingTracker[section_id]?.section_number) toggleEditing(section_id, 'section_number');
	};
	const closeSectionNumberEditing = (section_id) => {
		setSectionNumberDrafts(current_drafts => {
			const next_drafts = {...current_drafts};
			delete next_drafts[section_id];
			return next_drafts;
		});

		if(editingTracker[section_id]?.section_number)
		{
			editingTracker[section_id].section_number = false;
			setEditingTracker({...editingTracker});
		}
	};
	const cancelSectionNumberEditing = (section_id) => {
		closeSectionNumberEditing(section_id);
	};
	const acceptSectionNumberEditing = (section_id) => {
		const section_info = sectionsData[section_id];
		if(!section_info)
		{
			closeSectionNumberEditing(section_id);
			return;
		}

		if(section_info.course_id && !isPlaceholderInclusionSection(section_info) && !isSameCourseSubsectionForSnapshot(section_id, section_info.course_id, sectionsData))
		{
			const draft_value = sanitizeSectionNumberInput(sectionNumberDrafts[section_id]);
			const original_value = sanitizeSectionNumberInput(section_info.section_number);

			if(draft_value)
			{
				section_info.section_number = draft_value;
			}
			else
			{
				applySuggestedSectionIdentifiers(section_id, section_info.course_id);
			}

			if(sanitizeSectionNumberInput(section_info.section_number) !== original_value)
			{
				if(section_id === 'main') syncSameCourseSubsectionIdentifiers(sectionsData);
				setSectionsData({...sectionsData});
				setChangesMade(true);
			}
		}

		closeSectionNumberEditing(section_id);
	};
	const handleSectionNumberKeyDown = (event, section_id) => {
		if(event.key === 'Enter')
		{
			event.preventDefault();
			acceptSectionNumberEditing(section_id);
		}
		else if(event.key === 'Escape')
		{
			event.preventDefault();
			cancelSectionNumberEditing(section_id);
		}
	};
	const updateSectionNumberDraft = (section_id, value) => {
		const next_value = sanitizeSectionNumberInput(value);
		setSectionNumberDrafts(current_drafts => ({...current_drafts, [section_id]: next_value}));
	};
	const getDefaultQuarterDaysForCourse = (quarters_to_add) => {
		const default_days = currentDay ? [`${currentDay}`] : ((numDaysInCycle && numDaysInCycle.length > 0) ? numDaysInCycle : Object.keys(scheduleStructureData.schedule_structure));
		let default_quarter_days = [];

		default_days.forEach(day => {
			quarters_to_add.forEach(quarter => {
				default_quarter_days.push({day: `${day}`, quarter: `${quarter}`});
			});
		});

		return default_quarter_days;
	};
	const isSectionLocked = (section_id) => sectionsData[section_id]?.locked === '1';
	const toggleSingleSectionLock = (section_id) => {
		const section_info = sectionsData[section_id];
		if(!section_info) return;

		section_info.locked = section_info.locked === '1' ? '0' : '1';
		if(section_info.locked === '1' && editingTracker[section_id]) hideAllSectionEditing(section_id);

		setSectionsData({...sectionsData});
		setChangesMade(true);
	};
	const getFallbackCoursePeriodId = (section_id = 'main') => {
		const existing_course_periods = (sectionsData[section_id] && Array.isArray(sectionsData[section_id].course_periods)) ? sectionsData[section_id].course_periods.filter(Boolean) : [];
		if(existing_course_periods.length > 0) return existing_course_periods[0];

		return null;
	};
	const getSectionNumberValidationMessage = (section_id) => {
		const section_info = sectionsData[section_id];
		if(!section_info || !section_info.course_id || isPlaceholderInclusionSection(section_info)) return null;

		const normalized_section_number = sanitizeSectionNumberInput(section_info.section_number);
		if(!normalized_section_number) return null;

		const has_duplicate = getConflictingSectionIdsForNumber(section_id, section_info.course_id, normalized_section_number, sectionsData).length > 0;

		if(has_duplicate) return 'This section ID is already being used for this course.';
		return null;
	};
	const syncSameCourseSubsectionIdentifiers = (sections_snapshot = sectionsData) => {
		if(!sections_snapshot.main || !Array.isArray(sections_snapshot.main.subsections)) return false;

		let changed = false;
		sections_snapshot.main.subsections.forEach(subsection_id => {
			const subsection_info = sections_snapshot[subsection_id];
			if(!subsection_info || !subsection_info.course_id) return;
			if(!isSameCourseSubsectionForSnapshot(subsection_id, subsection_info.course_id, sections_snapshot)) return;

			const identifiers = getSameCourseSubsectionIdentifiers(subsection_id, subsection_info.course_id, sections_snapshot);
			if(!identifiers) return;

			if(subsection_info.section_number !== identifiers.section_number || subsection_info.section_sequence !== identifiers.section_sequence)
			{
				subsection_info.section_number = identifiers.section_number;
				subsection_info.section_sequence = identifiers.section_sequence;
				changed = true;
			}
		});

		return changed;
	};
	const clearSearchResultsForInputType = (next_search_results, section_id, input_type) => {
		if(!next_search_results[section_id]) return;

		if(input_type === 'classroom')
		{
			next_search_results[section_id].classrooms = {suggested:{course_classroom_restrictions:[], teacher_classroom_restrictions:[]}, search:[]};
		}
		else if(input_type === 'students')
		{
			next_search_results[section_id].students = {suggested_filter_ids:[], suggested: [], search: []};
		}
		else if(input_type === 'course')
		{
			next_search_results[section_id].courses = {suggested: [], search: []};
		}
		else if(input_type === 'teacher')
		{
			next_search_results[section_id].teachers = {suggested: [], search: []};
		}
	};

	const toggleEditing = (section_id, input_type) => {
		editingTracker[section_id][input_type] = !editingTracker[section_id][input_type];
		setEditingTracker({...editingTracker});

		if(!editingTracker[section_id][input_type])
		{
			hideAllSectionEditing(section_id);
			return;
		}

		if(!searchResults[section_id]) return;

		if(input_type === 'course')
		{
			searchResults[section_id].courses.search = [...courses];
		}
		else if(input_type === 'teacher')
		{
			searchResults[section_id].teachers.search = [...teachers];
		}
		else if(input_type === 'classroom')
		{
			searchResults[section_id].classrooms.search = [...classrooms];
		}
		else if(input_type === 'students')
		{
			searchResults[section_id].students.search = [...students];
			searchResults[section_id].students.suggested_filter_ids = [];
		}

		setSearchResults({...searchResults});
	}

	const handleLoadAvailability = (e, data_type, data_id) => {
		const data_position = e.target.getBoundingClientRect();
		const availability_data_position = (data_position.left > 550) ? {top:data_position.top + 50, left:data_position.left-550} : {top:data_position.top, left:data_position.left + 75};
		
		setAvailabilityDataPosition(availability_data_position);
		setAvailabilityDataType(data_type);
		setAvailabilityViewID(data_id);
	}
	
	const addNewSubsection = (is_inclusion = '0') => {
		const newID = createUniqueObjectID(5, sectionsData);
		
		// Add subsection to main section
		sectionsData['main'].subsections.push(newID);
		
		// Add subsection data to sections data
		const subsection_course_id = (is_inclusion === '1' && scheduleInfo.separate_ICR_codes === '0') ? sectionsData.main.course_id : null;
		sectionsData[newID] = {new:true, section_type:sectionType, section_id:newID, section_number:null, section_sequence:null, course_id:subsection_course_id, teacher_id:null, secondary_teachers:[], classroom_id:sectionsData.main.classroom_id || null, student_list:[], locked_students:[], labels:[], is_inclusion:is_inclusion, locked:'0', has_separate_quarter_days:'0', has_separate_classroom:'0', quarter_days:cloneObj(sectionsData.main.quarter_days || []), raw_quarter_days:[]};
		setSectionsData({...sectionsData});
		
		// Add subsection to edit tracking
		editingTracker[newID] = {course: false, teacher: false, classroom:false, students: false, section_number: false, advanced_settings_open:false, label_options_open:false};
		setEditingTracker({...editingTracker});
		
		// Add empty results to subsection
		searchResults[newID] = {courses:{suggested:[], search:[]}, teachers:{suggested:[], search:[]}, classrooms: {suggested:{course_classroom_restrictions:[], teacher_classroom_restrictions:[]}, search:[]}, students:{suggested_filter_ids:[], suggested:[], search:[]}};
		setSearchResults({...searchResults});

		// Initialize subsection selected-student trackers
		setSectionsSelectedStudents((currentSelection) => ({...currentSelection, [newID]: []}));
		setSectionsSelectedLockedStudents((currentSelection) => ({...currentSelection, [newID]: []}));
	}
	
	const removeSubsection = (subsection_id, subsection_index) => {
		// Remove subsection from main section
		sectionsData['main'].subsections.splice(subsection_index, 1);
		
		// Remove subsection data from sections data
		delete sectionsData[subsection_id]
		setSectionsData({...sectionsData});
		
		// Remove subsection from edit tracking
		delete editingTracker[subsection_id]
		setEditingTracker({...editingTracker});
		
		// Remove subsection from results
		delete searchResults[subsection_id];
		setSearchResults({...searchResults});

		// Remove subsection from selected-student trackers
		setSectionsSelectedStudents((currentSelection) => {
			const newSelection = {...currentSelection};
			delete newSelection[subsection_id];
			return newSelection;
		});
		setSectionsSelectedLockedStudents((currentSelection) => {
			const newSelection = {...currentSelection};
			delete newSelection[subsection_id];
			return newSelection;
		});

		setChangesMade(true);
	}

	const splitOffSubsection = async (subsection_id, subsection_index) => {
		const main_section_info = sectionsData['main'];
		const main_section_id = main_section_info.section_id;
		const main_course_id = main_section_info.course_id;
		const main_classroom_id = main_section_info.classroom_id;
		const main_quarter_days = main_section_info.quarter_days;
		const main_span_id = main_section_info.main_span_id;
		const main_is_lab = main_section_info.is_lab;

		const subsection_info = sectionsData[subsection_id];
		const subsection_span_id = subsection_info.span_id;
		subsection_info.main_course_id = main_course_id;
		subsection_info.classroom_id = subsection_info.has_separate_classroom === '1' ? subsection_info.classroom_id : main_classroom_id;
		subsection_info.quarter_days = subsection_info.has_separate_quarter_days === '1' ? subsection_info.quarter_days : main_quarter_days;
		subsection_info.has_separate_quarter_days = '1';
		subsection_info.has_separate_classroom = '1';
		subsection_info.is_lab = main_is_lab;

		// Prompt with modal 
		const options =  {
			title: "Split off this subsection?",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Split Subsection",
					value: true,
					visible: true,
					className: 'blue-btn'
				},
			},
			content: (
				<div>
					<p>This will split off the subsection into its own standalone section (this will break the section-subsection connection). Are you sure you want to split off this subsection?</p>
				</div>
			)
		}
	
		const split_off_subsection = await swal(options);
		
		if(split_off_subsection)
		{
			/// UPDATE FRONTEND ///
			// Remove the subsection from the current edit section page
			removeSubsection(subsection_id, subsection_index);

			// Collect all affected subsections
			let affected_subsections = [subsection_id];
			if(subsection_span_id)
			{
				affected_subsections = sections.reduce((results, section) => {
					if(section.span_id === subsection_span_id) results.push(section.section_id);
					return results;
				}, []);
			} 

			// Remove subsections from main sections
			// Then remove subsections from subsection list
			// Update any spanned sections with the current subsection data
			const affected_section_ids = affected_subsections;
			const new_sections = sections.map(section => {
				const section_span_id = section.span_id;
				const sections_subsections = section.subsections;

				// If this is a main section with the same span id, add it to affected sections
				if(main_span_id && section_span_id === main_span_id) affected_section_ids.push(section.section_id);

				// remove subsection from any listed subsections
				const new_subsections = sections_subsections.filter(subsection => !affected_subsections.includes(subsection));
				section.subsections = new_subsections;

				if(affected_subsections.includes(section.section_id))
				{
					section.section_type = '1';
					section.teacher_id = subsection_info.teacher_id;
					section.course_id = subsection_info.course_id;
					section.classroom_id = subsection_info.classroom_id;
					section.quarter_days = subsection_info.quarter_days;
					section.is_lab = subsection_info.is_lab;
					section.locked = subsection_info.locked;
				}
				return section;
			});
			const new_subsections = subsections.filter(section_subsection => section_subsection.section === main_section_id && section_subsection.subsection_id !== subsection_id);

			setSections([...new_sections]);
			setSubsections(new_subsections);

			/// UPDATE BACKEND ///
			const data = {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, section_data:subsection_info};
			
			const apiName = process.env.REACT_APP_ENDPOINT_NAME;
			const url = '/admin/split-off-subsection';
			const myInit = { // OPTIONAL
				response: true,
				body: JSON.stringify(data),
			};
		
			try {
				// Split off the subsection
				await API.post(apiName, url, myInit);
			
				calculateConflicts({school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id }).then(conflicts_response => {
					const conflicts_list = conflicts_response.conflicts;
					setConflicts([...conflicts_list]);
					markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
					setConflictsLoading(false);
				});
			} catch(e)
			{
				console.log(e.response);
			}
		}
	}
	
	const hideAllSectionEditing = (section_id, options = {}) => {
		const preserve_advanced_settings_open = options.preserveAdvancedSettingsOpen && editingTracker[section_id]?.advanced_settings_open;

		// Close all inputs for this section
		for(const input_type in editingTracker[section_id])
		{
			editingTracker[section_id][input_type] = false;
		}
		if(preserve_advanced_settings_open) editingTracker[section_id].advanced_settings_open = true;
		
		// Clear all results for this section
		for(const search_type in searchResults[section_id])
		{
			if(search_type === 'classrooms')
			{
				searchResults[section_id][search_type] = {suggested:{course_classroom_restrictions:[], teacher_classroom_restrictions:[]}, search:[]};
			}
			else if(search_type === 'students')
			{
				searchResults[section_id][search_type] = {suggested_filter_ids:[], suggested: [], search: []};
			}
			else
			{
				searchResults[section_id][search_type] = {suggested: [], search: []};
			}
		}
		
		setEditingTracker({...editingTracker});
		setSearchResults({...searchResults});
	}
	
	const fuzzySearch = (e, section_id, dataType) => {
		const search_value = e.target.value;
		
		if(dataType === 'courses')
		{
			const fuse = new Fuse(courses, {
				keys: ['name', 'course_code'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const course_results = search_value.trim().length === 0 ? [...courses] : results.map(result => result.item).slice(0, 6);
			
			searchResults[section_id][dataType]['search'] = course_results;
			setSearchResults({...searchResults});
		}
		else if(dataType === 'teachers')
		{
			const fuse = new Fuse(teachers, {
				keys: ['first_name', 'name'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const teacher_results = search_value.trim().length === 0 ? [...teachers] : results.map(result => result.item).slice(0, 6);
			
			searchResults[section_id][dataType]['search'] = teacher_results;
			setSearchResults({...searchResults});
		}
		else if(dataType === 'classrooms')
		{
			const fuse = new Fuse(classrooms, {
				keys: ['classroom_name', 'classroom_type'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const classroom_results = search_value.trim().length === 0 ? [...classrooms] : results.map(result => result.item).slice(0, 6);
			
			searchResults[section_id][dataType]['search'] = classroom_results;
			setSearchResults({...searchResults});
		}
		else if(dataType === 'students')
		{
			const fuse = new Fuse(students, {
				keys: ['first_name', 'last_name'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const has_search_value = search_value.trim().length > 0;
			const student_results = has_search_value ? results.map(result => result.item).slice(0, 30) : [...students];

			// filter out students who are requesting (so they don't show up in both lists)
			const other_students_results = student_results.filter(student => searchResults[section_id][dataType]['suggested'].findIndex(suggested_student_info => suggested_student_info.student_id === student.student_id) === -1);
			searchResults[section_id][dataType]['search'] = other_students_results;

			// add searched students to suggested filter ids so it narrows down search for requesting students too
			searchResults[section_id][dataType]['suggested_filter_ids'] = [];
			if(has_search_value)
			{
				student_results.forEach(student => {
					const student_id = student.student_id;
					searchResults[section_id][dataType]['suggested_filter_ids'].push(student_id);
				});
			}

			setSearchResults({...searchResults});
		}
	}

	const updateQuarterDays = (section_id, type, value) => {
		const section_quarter_days = sectionsData[section_id].quarter_days || [];
		if(type === 'quarter')
		{
			const quarter_index = section_quarter_days.findIndex(quarter_day => quarter_day.quarter === value);

			// If no index found then we know this is selecting quarter for first time
			// If index found then we know this is unselecting
			if(quarter_index === -1)
			{
				// Get all unique days that are currently selected
				let all_current_days = section_quarter_days.reduce((result, quarter_day) => {
					if(!result.includes(quarter_day.day)) result.push(quarter_day.day);
					return result;
				}, []);

				// If no days are selected yet, select them all by default
				all_current_days = (all_current_days.length === 0) ? numDaysInCycle : all_current_days;

				// For each unique day, add this quarter-day combo
				all_current_days.forEach(day => {
					section_quarter_days.push({quarter:value, day:day});
				});
			}
			else
			{
				const new_quarter_days = section_quarter_days.filter(quarter_day => quarter_day.quarter !== value);
				sectionsData[section_id].quarter_days = new_quarter_days;
			}
		}
		else if(type === 'day')
		{
			const day_index = section_quarter_days.findIndex(quarter_day => quarter_day.day === value);

			// If no index found then we know this is selecting day for first time
			// If index found then we know this is unselecting
			if(day_index === -1)
			{
				// Get all unique quarters that are currently selected
				let all_current_quarters = section_quarter_days.reduce((result, quarter_day) => {
					if(!result.includes(quarter_day.quarter)) result.push(quarter_day.quarter);
					return result;
				}, []);

				all_current_quarters = (all_current_quarters.length === 0) ? quarters.current : all_current_quarters;

				// For each unique quarter, add this quarter-day combo
				all_current_quarters.forEach(quarter => {
					section_quarter_days.push({quarter:quarter, day:value});
				});
			}
			else
			{
				const new_quarter_days = section_quarter_days.filter(quarter_day => quarter_day.day !== value);
				sectionsData[section_id].quarter_days = new_quarter_days;
			}
		}

		setSectionsData({...sectionsData});
		setAvailabilitiesCalculated(false);
		setChangesMade(true);
	}

	/////////////////////////////
	////// LABEL FUNCTIONS //////
	/////////////////////////////
	const addTempLabel = (section_id) => {
		const new_label_id = createUniqueObjectID(5);
		
		labels.push({new: true, label_id:new_label_id, label:null});
		sectionsData[section_id]['labels'].push(new_label_id);

		closeLabelOptions(section_id);
		setLabels([...labels]);
		setSectionsData({...sectionsData});
		setChangesMade(true);
	}

	const removeLabel = async (section_id, label_id) => {
		const data_index = sectionsData[section_id]['labels'].findIndex(data => data === label_id);
		if(data_index !== -1) sectionsData[section_id]['labels'].splice(data_index, 1);
		
		const label_index = labels.findIndex(label => label.label_id === label_id);
		const label_info = labels[label_index];
		const is_new = ('new' in label_info) ? true : false;
		
		if(is_new) labels.splice(label_index, 1);

		setSectionsData({...sectionsData});
		setLabels([...labels]);
		setChangesMade(true);
	}

	const updateLabel = async (e, section_id, label_id) => {
		const label_index = labels.findIndex(label => label.label_id === label_id);
		
		const label_input = e.target.closest('.create-labels-editing-label').querySelector('.create-labels-input');
		const label = label_input.value;
		
		if(!label)
		{
			label_input.classList.add('error');
			return;
		}
		
		const data = {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'add', label_id:label_id, label:label};
		
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-label';
	    const myInit = { // OPTIONAL
	        response: true,
	        body: JSON.stringify(data),
	    };
	   
		try {
			const response = await API.post(apiName, url, myInit);
			const new_label_id = response.data.label_id;
			
			labels[label_index]['label_id'] = new_label_id;
			labels[label_index]['label'] = label;
			
			delete labels[label_index]['new'];
			
			// Update section label id
			const section_label_index = sectionsData[section_id]['labels'].findIndex(label => label === label_id);
			if(section_label_index !== -1) sectionsData[section_id]['labels'].splice(section_label_index, 1);
			sectionsData[section_id]['labels'].push(new_label_id);

			setLabels([...labels]);
			setSectionsData({...sectionsData});
		} catch(e)
		{
			console.log(e.response);
		}
	}

	const toggleLabelOptionsOpen = (section_id) => {
		editingTracker[section_id]['label_options_open'] = !editingTracker[section_id]['label_options_open'];
		setEditingTracker({...editingTracker});
	}

	const toggleAdvancedSettingsOpen = (section_id) => {
		editingTracker[section_id]['advanced_settings_open'] = !editingTracker[section_id]['advanced_settings_open'];
		setEditingTracker({...editingTracker});
	}

	const toggleSeparateQuarterDays = (section_id, has_separate_quarter_days) => {
		if(!sectionsData[section_id]) return;

		sectionsData[section_id].has_separate_quarter_days = has_separate_quarter_days ? '1' : '0';
		if(has_separate_quarter_days)
		{
			const current_quarter_days = Array.isArray(sectionsData[section_id].quarter_days) ? sectionsData[section_id].quarter_days : [];
			sectionsData[section_id].quarter_days = cloneObj(current_quarter_days.length > 0 ? current_quarter_days : (sectionsData.main.quarter_days || []));
		}
		else
		{
			sectionsData[section_id].raw_quarter_days = [];
			sectionsData[section_id].quarter_days = cloneObj(sectionsData.main.quarter_days || []);
		}

		setSectionsData({...sectionsData});
		setChangesMade(true);
	}

	const toggleSeparateClassroom = (section_id, has_separate_classroom) => {
		if(!sectionsData[section_id]) return;

		sectionsData[section_id].has_separate_classroom = has_separate_classroom ? '1' : '0';
		sectionsData[section_id].classroom_id = has_separate_classroom ? (sectionsData[section_id].classroom_id || sectionsData.main.classroom_id || null) : (sectionsData.main.classroom_id || null);
		if(!has_separate_classroom) editingTracker[section_id].classroom = false;

		setSectionsData({...sectionsData});
		setEditingTracker({...editingTracker});
		setChangesMade(true);
	}

	const closeLabelOptions = (section_id) => {
		editingTracker[section_id]['label_options_open'] = false;
		setEditingTracker({...editingTracker});
	}

	const getSelectAllDropdownKey = (section_id, scope = 'section-students') => `${scope}-${section_id}`;
	
	useOutsideClick(ref, () => {
		const section_id = ref.current.closest('.create-labels-label-options-container').getAttribute('data-sectionid');
		
		closeLabelOptions(section_id);
	});

	// Handle all students dropdown outside click
	useEffect(() => {
		function handleClickOutside(event) {
			if (openDropdownSections.length === 0) return;
			
			const allRefs = getAllRefs();
			const sectionsToClose = [];
			
			openDropdownSections.forEach(dropdown_key => {
				const element = allRefs[dropdown_key];
				
				if (element) {
					const parentContainer = element.closest('.select-all-row');
					if (parentContainer && !parentContainer.contains(event.target)) {
						sectionsToClose.push(dropdown_key);
					}
				}
			});
			
			if (sectionsToClose.length > 0) {
				setOpenDropdownSections(openDropdownSections.filter(id => !sectionsToClose.includes(id)));
			}
		}
		
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [getAllRefs, openDropdownSections]);

	useEffect(() => {
		const editable_input_types = ['course', 'teacher', 'classroom', 'students', 'section_number'];
		const has_open_editing = Object.keys(editingTracker).some(section_id => editable_input_types.some(input_type => editingTracker[section_id]?.[input_type]));

		if(!has_open_editing) return;

		function handleEditingClickOutside(event) {
			const target = event.target;
			if(!target) return;

			const clicked_inside_active_editor =
				target.closest('.search-add-search-container') ||
				target.closest('.search-add-search-results') ||
				target.closest('.search-add-did-you-mean') ||
				target.closest('.select-all-row') ||
				target.closest('.edit-section-inline-edit-row');

			if(clicked_inside_active_editor) return;

			let tracker_changed = false;
			let search_results_changed = false;
			let drafts_changed = false;
			const next_editing_tracker = cloneObj(editingTracker);
			const next_search_results = cloneObj(searchResults);
			const next_section_number_drafts = {...sectionNumberDrafts};

			Object.keys(next_editing_tracker).forEach(section_id => {
				editable_input_types.forEach(input_type => {
					if(!next_editing_tracker[section_id]?.[input_type]) return;

					next_editing_tracker[section_id][input_type] = false;
					tracker_changed = true;

					if(input_type === 'section_number')
					{
						if(section_id in next_section_number_drafts)
						{
							delete next_section_number_drafts[section_id];
							drafts_changed = true;
						}
					}
					else
					{
						clearSearchResultsForInputType(next_search_results, section_id, input_type);
						search_results_changed = true;
					}
				});
			});

			if(tracker_changed) setEditingTracker(next_editing_tracker);
			if(search_results_changed) setSearchResults(next_search_results);
			if(drafts_changed) setSectionNumberDrafts(next_section_number_drafts);
		}

		document.addEventListener('mousedown', handleEditingClickOutside);
		return () => document.removeEventListener('mousedown', handleEditingClickOutside);
	}, [editingTracker, searchResults, sectionNumberDrafts]);

	const checkSelectedLockedStudents = (section_id, selectedStudents) => {
		let lockedStudents = [];
		selectedStudents.forEach(student_id => {
			if(sectionsData[section_id] && sectionsData[section_id].student_list.includes(student_id) && sectionsData[section_id].locked_students.includes(student_id)) {
				lockedStudents.push(student_id);
			}
		});
		setSectionsSelectedLockedStudents(current_locked_students => ({...current_locked_students, [section_id]: lockedStudents}));
	};

	const setSelectedStudentsForSection = (section_id, selected_students) => {
		const unique_selected_students = Array.from(new Set(selected_students));
		setSectionsSelectedStudents(current_selected_students => ({...current_selected_students, [section_id]: unique_selected_students}));
		checkSelectedLockedStudents(section_id, unique_selected_students);
	};

	const addSelectedStudentsForSection = (section_id, student_ids) => {
		if(student_ids.length === 0) return;

		const selected_students = sectionsSelectedStudents[section_id] || [];
		setSelectedStudentsForSection(section_id, [...selected_students, ...student_ids]);
	};

	const removeSelectedStudentsForSection = (section_id, student_ids = []) => {
		const selected_students = sectionsSelectedStudents[section_id] || [];

		if(student_ids.length === 0)
		{
			setSelectedStudentsForSection(section_id, []);
			return;
		}

		const updated_selected_students = selected_students.filter(student_id => !student_ids.includes(student_id));
		setSelectedStudentsForSection(section_id, updated_selected_students);
	};

	const toggleSelectStudent = (student_id, section_id) => {
		const selected_students = [...(sectionsSelectedStudents[section_id] || [])];
		const student_selected_index = selected_students.findIndex(selected_student_id => selected_student_id === student_id);

		if(student_selected_index !== -1) {
			selected_students.splice(student_selected_index, 1);
		} else {
			selected_students.push(student_id);
		}
		setSelectedStudentsForSection(section_id, selected_students);
	}

	const toggleSelectAll = (section_id, type = 'unlocked') => {
		let student_list = [];
		switch (type) {
			case 'all':
				student_list = sectionsData[section_id].student_list;
				break;
			case 'locked':
				student_list = sectionsData[section_id].student_list.filter(student_id => sectionsData[section_id].locked_students.includes(student_id));
				break;
			case 'unlocked':
				student_list = sectionsData[section_id].student_list.filter(student_id => !sectionsData[section_id].locked_students.includes(student_id));
				break;
			case 'none':
				student_list = [];
				break;
			default:
		}

		const selected_students = sectionsSelectedStudents[section_id] || [];
		const same_students_selected = new Set(selected_students.filter(student_id => student_list.includes(student_id))).size === student_list.length && student_list.every(student_id => selected_students.includes(student_id));

		let new_selected_students = [];
		if(!same_students_selected) {
			new_selected_students = [...selected_students, ...student_list];
		}
		setSelectedStudentsForSection(section_id, new_selected_students);
	}

	const toggleSelectAllDropdown = (dropdown_key) => {
		if(openDropdownSections.includes(dropdown_key)) {
			setOpenDropdownSections(openDropdownSections.filter(id => id !== dropdown_key));
		} else {
			setOpenDropdownSections([...openDropdownSections, dropdown_key]);
		}
	}

	const getVisibleStudentsInAddDropdown = (section_id) => {
		const student_results = searchResults[section_id]?.students;
		if(!student_results) return [];

		const suggested_filter_ids = student_results.suggested_filter_ids || [];
		const visible_suggested_students = (student_results.suggested || []).filter(student => suggested_filter_ids.length === 0 || suggested_filter_ids.includes(student.student_id));
		const visible_search_students = student_results.search || [];

		return Array.from(new Set([...visible_suggested_students, ...visible_search_students].map(student => student.student_id)));
	}

	const getStudentsByDropdownSelectionType = (section_id, type = 'visible') => {
		const visible_student_ids = getVisibleStudentsInAddDropdown(section_id);

		switch(type)
		{
			case 'in_section':
				return visible_student_ids.filter(student_id => sectionsData[section_id].student_list.includes(student_id));
			case 'not_in_section':
				return visible_student_ids.filter(student_id => !sectionsData[section_id].student_list.includes(student_id));
			case 'none':
				return [];
			case 'visible':
			default:
				return visible_student_ids;
		}
	}

	const selectStudentsInAddDropdown = (section_id, type = 'visible') => {
		const student_ids = getStudentsByDropdownSelectionType(section_id, type);
		addSelectedStudentsForSection(section_id, student_ids);
	}

	const toggleVisibleStudentsInAddDropdown = (section_id) => {
		const visible_student_ids = getVisibleStudentsInAddDropdown(section_id);
		if(visible_student_ids.length === 0) return;

		const selected_students = sectionsSelectedStudents[section_id] || [];
		const all_visible_students_selected = visible_student_ids.every(student_id => selected_students.includes(student_id));

		if(all_visible_students_selected)
		{
			removeSelectedStudentsForSection(section_id, visible_student_ids);
		}
		else
		{
			addSelectedStudentsForSection(section_id, visible_student_ids);
		}
	}

	const bulkUpdateStudentsFromAddDropdown = (section_id, change_type) => {
		const selected_students = sectionsSelectedStudents[section_id] || [];
		let relevant_student_ids = [];

		if(change_type === 'add')
		{
			relevant_student_ids = selected_students.filter(student_id => !sectionsData[section_id].student_list.includes(student_id));
			if(relevant_student_ids.length === 0) return;

			relevant_student_ids.forEach(student_id => {
				sectionsData[section_id].student_list.push(student_id);
			});
		}
		else if(change_type === 'remove')
		{
			relevant_student_ids = selected_students.filter(student_id => sectionsData[section_id].student_list.includes(student_id) && !sectionsData[section_id].locked_students.includes(student_id));
			if(relevant_student_ids.length === 0) return;

			sectionsData[section_id].student_list = sectionsData[section_id].student_list.filter(student_id => !relevant_student_ids.includes(student_id));
		}

		setSectionsData({...sectionsData});
		checkSelectedLockedStudents(section_id, sectionsSelectedStudents[section_id] || []);
		setChangesMade(true);
	}

	const toggleLockStudents = (section_id, lock_type, student_id = null) => {
		if(lock_type === 'lock')
		{
			if(student_id)
			{
				const lock_index = sectionsData[section_id].locked_students.findIndex(locked_student_id => locked_student_id === student_id);
				if(lock_index === -1) sectionsData[section_id].locked_students.push(student_id);
			}
				else
				{
					(sectionsSelectedStudents[section_id] || []).forEach(student_id => {
						sectionsData[section_id].locked_students.push(student_id);
					});
				}
			
		}
		else if(lock_type === 'unlock')
		{
			if (student_id) {
				const lock_index = sectionsData[section_id].locked_students.findIndex(locked_student_id => locked_student_id === student_id);
				if(lock_index !== -1) sectionsData[section_id].locked_students.splice(lock_index, 1);
				} else {
					(sectionsSelectedStudents[section_id] || []).forEach(student_id => {
						const lock_index = sectionsData[section_id].locked_students.findIndex(locked_student_id => locked_student_id === student_id);
						if(lock_index !== -1) sectionsData[section_id].locked_students.splice(lock_index, 1);
					});
				}
			}
		
		checkSelectedLockedStudents(section_id, sectionsSelectedStudents[section_id] || []);
		setSectionsData({...sectionsData});
		setChangesMade(true);
	}

	const bulkRemoveStudents = async (section_id, students_without_requests = []) => {
		const studentsWithoutRequestsContent = students_without_requests.length > 0 ? (
			<div style={{marginTop: '15px', borderTop: '1px solid #ddd', paddingTop: '15px'}}>
				<p><strong>Do you want to create course requests for these students?</strong></p>
				<ul style={{textAlign:'left', maxHeight:'150px', overflowY:'auto', paddingLeft: '20px', marginTop: '10px'}}>
					{students_without_requests.map((studentName, index) => (
						<li key={index}>{studentName}</li>
					))}
				</ul>
			</div>
		) : null;

		const options =  {
			title: "Are you sure?",
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: null,
					visible: true,
					className: 'gray-btn'
				},
				addAndMove: {
					text: "Yes, add and move",
					value: "addAndMove",
					visible: students_without_requests.length > 0,
					className: 'blue-btn'
				},
				justMove: {
					text: "Yes, remove selected students",
					value: "justMove",
					visible: true,
					className: 'red-btn'
				}
			},
			content: (
				<div>
					<p>This will remove the selected students from this section.</p>
					{studentsWithoutRequestsContent}
				</div>
			)
		}

		const result = await swal(options);

		if(!result) return { confirmed: false };

		const selected_students_from_section = sectionsSelectedStudents[section_id].filter(student_id => sectionsData[section_id].student_list.includes(student_id));
		const selected_students_not_from_section = sectionsSelectedStudents[section_id].filter(student_id => !sectionsData[section_id].student_list.includes(student_id));

		selected_students_from_section.forEach(student_id => {
			updateData('remove', section_id, 'student_list', student_id);
		});

		setSectionsSelectedStudents({...sectionsSelectedStudents, [section_id]: selected_students_not_from_section});
		checkSelectedLockedStudents(section_id, selected_students_not_from_section);

		setChangesMade(true);

		return {
			confirmed: true,
			addRequests: result === "addAndMove"
		};
	}

	// Component for bulk sending students to other section
	// Need to separate this out so it updates on state change in the modal (needed for search)
	const OtherSectionList = ({ oldSectionID, action = 'move', verifyConflictsIndex = null, verifyConflictsError = [], loading = false }) => {
		const { searchValue, searchResults, updateSearchValue, updateSearchResults } = useOtherSectionsSearch();

		const otherSectionsSearch = (e) => {
			const search_value = e.target.value;
			const normalized_search_value = search_value.trim().toLowerCase();
			updateSearchValue(search_value);
			
			let section_results = [];

			if(normalized_search_value.length === 0)
			{
				section_results = sections;
			}
			else
			{
				// Get courses with search term
				const fuse_courses = new Fuse(courses, {
					keys: ['name', 'course_code'],
					threshold: .1
				});
				
				const course_search_results = fuse_courses.search(search_value);
				const course_results = course_search_results.map(result => result.item).slice(0, 6);

				// Get teachers with search term
				const fuse_teachers = new Fuse(teachers, {
					keys: ['name', 'first_name'],
					threshold: .1
				});
				
				const teacher_search_results = fuse_teachers.search(search_value);
				const teacher_results = teacher_search_results.map(result => result.item).slice(0, 6);
				
				// Get any sections that have either a course or teacher with search term
				section_results = sections.reduce((results, section) => {
					const course_id = section.course_id;
					const teacher_id = section.teacher_id;
					const section_number = `${section.section_number || ''}`.toLowerCase();
					if(
						course_results.findIndex(course => course.course_id === course_id) !== -1 ||
						teacher_results.findIndex(teacher => teacher.teacher_id === teacher_id) !== -1 ||
						section_number.includes(normalized_search_value)
					) results.push(section);
					return results;
				}, []);
			}

			updateSearchResults(section_results);
		}

		const selected_students_in_section = (sectionsSelectedStudents[oldSectionID] || []).filter(student_id => sectionsData[oldSectionID].student_list.includes(student_id));

		let logged_spanned_ids = [];
		const sections_with_course_codes = sections.reduce((results, section) => {
			const span_id = section.span_id;

			if(span_id && logged_spanned_ids.includes(span_id)) return results;
			if(span_id) logged_spanned_ids.push(span_id);

			const course_id = section.course_id;
			const course_index = courses.findIndex(course => course.course_id === course_id);

			if(course_index !== -1)
			{
				const course_info = courses[course_index];
				const course_code = course_info.course_code;
				
				section.course_code = course_code;

				results.push(section);
			}

			return results;
		}, []);

		const sorted_sections_by_course_code = sortArrayOfObjects(sections_with_course_codes, 'course_code', 'text', 'desc');

		const oldSectionDepartments = sectionsData[oldSectionID].departments || [];

		// Sort sections by department match priority
		const sorted_sections_by_same_departments = sorted_sections_by_course_code.sort((sectionA, sectionB) => {
				const departmentsA = sectionA.departments || [];
				const departmentsB = sectionB.departments || [];
				const hasMatchingDepartmentsA = departmentsA.some(dept => oldSectionDepartments.includes(dept));
				const hasMatchingDepartmentsB = departmentsB.some(dept => oldSectionDepartments.includes(dept));
				
				if (hasMatchingDepartmentsA && !hasMatchingDepartmentsB) return -1;
				if (!hasMatchingDepartmentsA && hasMatchingDepartmentsB) return 1;
				
				return 0;
		});

		const old_section_id = sectionsData[oldSectionID].section_id;
		const old_span_id = sectionsData[oldSectionID].span_id;

		const title = (action === 'move') ? 'Move Students' : 'Copy Students';
		const subtitle = (action === 'move') ? 'Which section would you like to send these students to?' : 'Which section would you like to copy these students to?';
		const visible_sections = sorted_sections_by_same_departments.filter(section => searchResults.findIndex(other_section => other_section.section_id === section.section_id) !== -1);

		return (
			<div className='text-align-left edit-section-other-sections-modal'>
				<button className='edit-section-other-sections-close-btn' type='button' aria-label='Close' onClick={() => swal.close()}>
					<FontAwesomeIcon icon={faTimes}/>
				</button>
				<h1 className='edit-section-other-sections-title'>{title}</h1>
				<p className='edit-section-other-sections-subtitle'>{subtitle}</p>
				<div className='search-add-adding-input-container' style={{marginBottom:'10px'}}>
					<input className='search-add-adding-input' value={searchValue} onChange={(e) => otherSectionsSearch(e)} placeholder='Search by course, teacher, or section number'/>
					<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
				</div>
				<div className='edit-section-other-sections-results'>
				{visible_sections.length === 0 && (
					<div className='edit-data-no-data-container'>No sections match that search.</div>
				)}
				{visible_sections.map((section, i) => {
					const section_id = section.section_id;
					let course_periods = Array.isArray(section.course_periods) ? section.course_periods : [];
					const teacher_id = section.teacher_id;
					const teacher_info = teachers.find(teacher => teacher.teacher_id === teacher_id);
					const span_id = section.span_id;
					const num_students = Array.isArray(section.student_list) ? section.student_list.length : 0;
					const course_id = section.course_id;
					const is_locked = Boolean(parseInt(section.locked));
					const section_number = section.section_number || '--';
					const course_index = courses.findIndex(course => course.course_id === course_id);
					const course_info = (course_index !== -1) ? courses[course_index] : null;
					const course_code = (course_info) ? course_info.course_code : 'No Course Code';
					const course_name = (course_info) ? course_info.name : 'Unknown Course';
					const teacher_display = teacher_id && teacher_info && (teacher_info.name && teacher_info.first_name) ? `${teacher_info.name}, ${teacher_info.first_name}` : '--';
					const is_subsection_index = subsections.findIndex(subsection => subsection.subsection_id === section_id);
					const is_subsection = is_subsection_index !== -1;
					const is_inclusion = section.is_inclusion === '1';
					const quarter_days = Array.isArray(section.quarter_days) ? section.quarter_days : [];
					const selected_quarters = new Set(quarter_days.map(quarter_day => `${quarter_day.quarter}`));
					const display_quarters = [...selected_quarters].some(quarter => ['5', '6'].includes(quarter)) ? ['1', '2', '3', '4', '5', '6'] : ['1', '2', '3', '4'];
					const is_current_section = section_id === old_section_id || (span_id && span_id === old_span_id);

					// blue-text - subsection
					// orange-text - inclusion

					if(span_id)
					{
						for(const section of sections)
						{
							const section_span_id = section.span_id;
							if(section_span_id !== span_id) continue;
							
							const section_course_periods = Array.isArray(section.course_periods) ? section.course_periods : [];
							if(section_course_periods.length > 0) course_periods = [...new Set([...course_periods, ...section_course_periods])];
						}
					}

						let course_period_names = [];
						course_periods.forEach(course_period_id => {
							const period_name = getPeriodNameFromCoursePeriodId(scheduleStructureData, course_period_id, {
								schedule_type: scheduleInfo.schedule_type,
								current_day: '1',
							});
							if(!period_name) return;

							course_period_names.push({
								[course_period_id]: period_name,
							});
						});

					// sort by period name
					course_period_names.sort((a, b) => {
						const periodNameA = Object.values(a)[0];
						const periodNameB = Object.values(b)[0];
						return periodNameA.localeCompare(periodNameB);
					});

							return (
								<React.Fragment key={i}>
								<div key={i} className={`edit-section-other-section-card ${is_current_section ? 'edit-section-other-section-card-current' : ''} ${is_locked ? 'edit-section-other-section-card-locked' : ''}`}>
									<div className='edit-section-other-section-card-main'>
										<div className='edit-section-other-section-card-header'>
											<div className='edit-section-other-section-course'>
												<div className={`edit-section-other-section-course-name ${(is_subsection) ? 'blue-text' : (is_inclusion) ? 'orange-text' : ''}`}>
													{course_code} - {course_name}
												</div>
											</div>
											<div className='edit-section-other-section-statuses'>
												{is_subsection && <span className='edit-section-other-section-tag edit-section-other-section-tag-subsection'>Subsection</span>}
												{is_inclusion && <span className='edit-section-other-section-tag edit-section-other-section-tag-inclusion'>Inclusion</span>}
												{is_locked && <span className='edit-section-other-section-tag edit-section-other-section-tag-locked'><FontAwesomeIcon icon={faLock}/> Locked</span>}
											</div>
										</div>
										<div className='edit-section-other-section-details'>
											<div className='edit-section-other-section-detail-block'>
												<div className='edit-section-other-section-detail-label'>Section</div>
												<div className='edit-section-other-section-detail-value'>{section_number}</div>
											</div>
											<div className='edit-section-other-section-detail-block'>
												<div className='edit-section-other-section-detail-label'>Teacher</div>
												<div className='edit-section-other-section-detail-value'>{teacher_display}</div>
											</div>
											<div className='edit-section-other-section-detail-block'>
												<div className='edit-section-other-section-detail-label'>Roster</div>
												<div className='edit-section-other-section-detail-value edit-section-other-section-roster-count'><FontAwesomeIcon icon={faUserGraduate}/> {num_students}</div>
											</div>
											<div className='edit-section-other-section-detail-block'>
												<div className='edit-section-other-section-detail-label'>Quarters</div>
												<div className={`mb-section-quarter-day-container mb-section-quarter-day-container-readonly ${display_quarters.length > 4 ? 'mb-section-quarter-day-container-cycle' : ''}`}>
													{display_quarters.map(quarter => (
														<div className={`mb-section-quarter-day ${selected_quarters.has(quarter) ? 'mb-section-quarter-day-selected' : ''}`} key={`${section_id}-quarter-${quarter}`}>
															{display_quarters.length > 4 ? `C${quarter}` : `Q${quarter}`}
														</div>
													))}
												</div>
											</div>
											<div className='edit-section-other-section-detail-block'>
												<div className='edit-section-other-section-detail-label'>Periods</div>
												<div className='edit-section-other-section-periods'>
													{course_periods.length === 0 ?
														(
															<span className='dark-gray-text'>--</span>
														):
														(
															<>
															{course_period_names.map(course_period => {
																const period_id = Object.keys(course_period)[0];
																const period_name = Object.values(course_period)[0];
																return (
																	<div key={`${i}-${period_id}`} className='badge badge-secondary'>{period_name}</div>
																)
															})}
															</>
														)
													}
												</div>
											</div>
										</div>
									</div>
									<div className='edit-section-other-section-card-action'>
										{is_current_section ?
											(
												<div className='edit-section-other-section-tag'>Current Section</div>
											): is_locked ? (
												<FontAwesomeIcon className='bright-yellow-text' icon={faLock} />
											): (
												(loading && verifyConflictsIndex  === section_id) ? <Loader size={'32px'} /> : (<div className='btn-small btn-round blue-btn align-right' onClick={() => bulkSendStudentsToNewSection(selected_students_in_section, oldSectionID, section_id, action, section_id)}>Select</div>)
											)
										}
									</div>
						</div>
						{verifyConflictsError && verifyConflictsIndex === section_id && (
							<div className="alert-box">
								<div className="alert-header">
									<span className="alert-icon" role="img" aria-label="warning">⚠️</span>
									<span className="alert-title">Conflict(s) Detected</span>
								</div>
								<p className="alert-message">
									<ul>
										{verifyConflictsError.map((error, errorIndex) => (
											<li key={errorIndex}>{error}</li>
										))}
									</ul>
								</p>
								<div className="alert-actions">
									<button 
										className="btn-primary"
										onClick={() => bulkSendStudentsToNewSection(selected_students_in_section, oldSectionID, section_id, action, section_id, true)}
									>Yes, {action} students</button>
									<button className="btn-cancel" onClick={() => showOtherSections(oldSectionID, action, null, null, false)}>Cancel</button>
								</div>
							</div>
						)}
						</React.Fragment>
					)
				})}
				</div>
			</div>
		)
	}

	const showOtherSections = async (old_section_id, action = 'move', verifyConflictsIndex, verifyConflictsError, loading = false) => {
		if (!verifyConflictsIndex && !verifyConflictsError && !loading) {
			resetPersistentSearchState();
		}
		
		const options =  {
			button: false,
			className: 'swal-extra-wide',
			content: (
				<OtherSectionsSearchProvider sections={sections}>
					<OtherSectionList 
						oldSectionID={old_section_id} 
						action={action}
						verifyConflictsIndex={verifyConflictsIndex}
			          verifyConflictsError={verifyConflictsError}
						loading={loading}
					/>
				</OtherSectionsSearchProvider>
			)
		}

		await swal(options);
	}

	const bulkSendStudentsToNewSection = async (student_list, oldSectionID, new_section_id, action = 'move', index, skipVerifyConflicts = false) => {
		setConflictsLoading(true);
		setMatchPercentLoading(true);
		
		if (!skipVerifyConflicts) {
			showOtherSections(oldSectionID, action, index, null, true);
			const conflictsResult = await calculateConflicts({
				school_id: schoolInfo.school_id, 
				schedule_version_id: schoolInfo.current_schedule_version_id, 
				student_list: student_list, 
				affected_sections: [new_section_id],
				conflicts_to_check: [
					CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED,
					CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS,
					CONFLICT_TYPES.STUDENT_STUDENT_RESTRICTION,
					CONFLICT_TYPES.TEACHER_STUDENT_RESTRICTION,
					CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY,
					CONFLICT_TYPES.INCLUSION_TOO_MANY_STUDENTS,
					CONFLICT_TYPES.INCLUSION_OVER_HALF
				]
			});

			if (conflictsResult.conflicts && conflictsResult.conflicts.length > 0) {
				const errors = conflictsResult.conflicts.map(conflict => {
					return CONFLICT_TYPES_TEXT[conflict.type];
				});
				showOtherSections(oldSectionID, action, index, errors, false);
				return;
			}
		}

		const old_section_id = sectionsData[oldSectionID].section_id;
		let move_confirmed;

		// Move students from one section to another in FRONT END state
		const new_section_info = sections.find(section => section.section_id === new_section_id);
		const new_section_course_id = new_section_info.course_id;
		const new_section_span_id = new_section_info.span_id;
		const new_section_is_inclusion = sectionRequestIsInclusion(new_section_info);
		const new_request_is_inclusion = getRequestInclusionValueForSection(new_section_info);

		// Filter students who don't have a request for the new section course
		const students_without_requests = student_list.reduce((result, student_id) => {
			const student_info = students.find(student => student.student_id === student_id);
			if (!student_info) return result; // Skip if student not found
			
			const student_requests = student_info.student_requests || [];
			
			// Check if student has any request group with this course
			const has_request = student_requests.some(request_group => {
				const course_requests = request_group.course_requests || [];
				return course_requests.some(course_request => 
					course_request.course_id === new_section_course_id &&
					(scheduleInfo.separate_ICR_codes !== '0' || (`${course_request.is_inclusion || '0'}` === '1') === new_request_is_inclusion)
				);
			});
			
			// Add student name to array only if they do NOT have request
			if (!has_request) {
				result.push(`${student_info.last_name}, ${student_info.first_name}`);
			}
			
			return result;
		}, []);

		let addStudentsRequests = false;

		if (action === 'move') {
			// Remove these students from edit section screen
			const removeResult = await bulkRemoveStudents(oldSectionID, students_without_requests);
			
			if (!removeResult.confirmed) {
				setConflictsLoading(false);
				setMatchPercentLoading(false);
				return;
			}
			
			move_confirmed = removeResult.confirmed;
			addStudentsRequests = removeResult.addRequests;
		}

		// Close modal
		swal.close();

		// If move canceled, do nothing
		if(!move_confirmed && action === 'move') return;

		
		// Remove students from old section
		// Remove student from any sections of the same course as the new section (takes care of spans)
		// Add students to new section(s)
		const new_sections = sections.map(section => {
			const section_id = section.section_id;
			const section_course_id = section.course_id;
			const section_student_list = section.student_list;
			const section_span_id =section.span_id;
			const section_matches_request_context = sectionMatchesRequestContext(section, new_section_course_id, new_section_is_inclusion);
		
			if((section_id === new_section_id) || (section_span_id && new_section_span_id === section_span_id))
			{
				const new_student_list = [...new Set([...student_list ,...section_student_list])];
				section.student_list = new_student_list;
			}
			else if(action === 'move' && ((section_id === old_section_id) || (section_course_id === new_section_course_id && section_matches_request_context)))
			{
				const student_set = new Set(student_list);
				const new_student_list = section_student_list.filter(student_id => !student_set.has(student_id));
				section.student_list = new_student_list;
			}
			
			return section;
		});

		setSections([...new_sections]);

		// Add requests for the new section course for all students
		student_list.forEach(student_id => {
			const student_index = students.findIndex(student => student.student_id === student_id);
			const student_info = students[student_index];
			const student_requests = student_info.student_requests;

			const existing_request = findCourseRequestInStudentRequests(student_requests, new_section_course_id, {
				isInclusion: scheduleInfo.separate_ICR_codes === '0' ? new_request_is_inclusion : null,
			});

			if(!existing_request) 
			{
				students[student_index].student_requests.push({
					group_id: `NEW-${createUniqueObjectID(7)}`,
					priority:'1',
					course_requests: [{
						course_id:new_section_course_id,
						order:'1',
						priority:'1',
						matched:'1',
						data_origin:'2',
						is_inclusion:new_request_is_inclusion ? '1' : '0',
						is_deleted:'0'
					}]
				});
			}
			else
			{
				existing_request.request_info.is_deleted ='0';
			}
		});

		setStudents([...students]);

		///////////////////////////
		///// SEND TO BACKEND /////
		///////////////////////////
		// Move students from one section to another in the BACK END
		const data = {
			school_id: schoolInfo.school_id, 
			schedule_version_id: schoolInfo.current_schedule_version_id, 
			student_list: student_list, 
			old_section_id: old_section_id,
			new_section_id: new_section_id,
			action: action, // 'move' or 'copy'
			addStudentsRequests: addStudentsRequests
		};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME_V2;
		const url = '/student/section/move';
		const myInit = { // OPTIONAL
			response: true,
			body: data,
		};
		
		try {
			// MOVE STUDENTS TO NEW SECTION
			await API.put(apiName, url, myInit);

			calculateConflicts({
				school_id: schoolInfo.school_id,
				schedule_version_id: schoolInfo.current_schedule_version_id,
				match_percent: true,
			}).then(conflicts_response => {
				const conflicts_list = conflicts_response.conflicts;
				setConflicts([...conflicts_list]);
				setMatchPercent(conflicts_response.match_percent);
				markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
				setConflictsLoading(false);
				setMatchPercentLoading(false);
			});
		} catch(error)
		{
			console.log(error.response);
		}
	}
	
	const handleExitWithoutSaving = async () => {
		let exit_without_saving = true;
		
		if(changesMade)
		{
			const options =  {
				title: "Exit without saving?",
				dangerMode: true,
				buttons: {
					cancel: {
						text: "Cancel",
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: "Yes",
						value: true,
						visible: true,
						className: 'blue-btn'
					},
				},
				content: (
					<div>
						<p>You have changes that have not been saved. Do you really want to exit without saving?</p>
					</div>
				)
			}
		
			exit_without_saving = await swal(options);
		}
		
		if(exit_without_saving) toggleEditSection();
	}

	const handleDeleteSection = async () => {
		const options =  {
			title: "Are you sure?",
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div>
					<p>Do you really want to delete this section? Deleting it will remove:</p>
					<ul style={{textAlign:'left'}}>
						<li>this section from your schedule</li>
						<li>all students from this section</li>
						<li>all spanning sections connected with this section</li>	
					</ul>
				</div>
			)
		}

		const delete_section_bool = await swal(options);

		if(delete_section_bool)
		{
			setIsSaving(true);
			setConflictsLoading(true);
			setMatchPercentLoading(true);

			/////////////////////////
			//// UPDATE FRONTEND ////
			/////////////////////////
			const section_info = sections.find(section => section.section_id === sectionID);
			const span_id = (section_info) ? section_info.span_id : null;
			const affected_sections = sections.reduce((results, section, section_index) => {
				const section_id = section.section_id;
				const section_span_id = section.span_id;
				const section_subsections = section.subsections;
				if(section_id === sectionID || (span_id && span_id === section_span_id))
				{
					if(!results.section_indexes.includes(section_index)) results.section_indexes.push(section_index);
					if(!results.section_ids.includes(section_id)) results.section_ids.push(section_id);
					
					for(const section_subsection_id of section_subsections)
					{
						const subsection_section_index = sections.findIndex(section => section.section_id === section_subsection_id);
						const subsection_subsection_index = subsections.findIndex(subsection => subsection.subsection_id === section_subsection_id);

						// Add subsection section id to deleted list
						if(subsection_section_index !== -1 && !results.section_ids.includes(section_subsection_id)) results.section_ids.push(section_subsection_id);

						// Add subsection section index to indexes to delete from sections
						if(subsection_section_index !== -1 && !results.section_indexes.includes(subsection_section_index)) results.section_indexes.push(subsection_section_index);

						// Add subsection subsection index to indexes to delete from subsections
						if(subsection_subsection_index !== -1 && !results.subsection_indexes.includes(subsection_subsection_index)) results.subsection_indexes.push(subsection_subsection_index);
					}
				}
				return results;
			}, {section_ids:[], section_indexes:[], subsection_indexes:[]});

			for(const section_index of affected_sections.section_indexes)
			{
				if(section_index !== -1) sections.splice(section_index, 1);
			}

			for(const subsection_index of affected_sections.subsection_indexes)
			{
				if(subsection_index !== -1) subsections.splice(subsection_index, 1);
			}
			
			for(const affected_section_id of affected_sections.section_ids)
			{
				const filtered_results_index = filteredResults.findIndex(section => section.section_id === affected_section_id);
				if(filtered_results_index !== -1) filteredResults.splice(filtered_results_index, 1);
			}

			setSections([...sections]);
			setFilteredResults([...filteredResults]);

			////////////////////////
			//// UPDATE BACKEND ////
			////////////////////////
			const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, section_id:sectionID};

			deleteSection(data).then(response_data => {
				const deleted_sections = response_data.deleted_sections;
				
				// Delete all deleted sections
				deleted_sections.forEach(section_id => {
					const section_index = sections.findIndex(section => section.section_id === section_id);
					const filtered_results_index = filteredResults.findIndex(section => section.section_id === section_id);
		
					if(section_index !== -1) sections.splice(section_index, 1);
					if(filtered_results_index !== -1) filteredResults.splice(filtered_results_index, 1);
				});

				setSections([...sections]);
				setFilteredResults([...filteredResults]);
				setIsSaving(false);

				calculateConflicts({
					school_id: schoolInfo.school_id,
					schedule_version_id: schoolInfo.current_schedule_version_id,
					match_percent: true,
				}).then(conflicts_response => {
					const conflicts_list = conflicts_response.conflicts;
					setConflicts([...conflicts_list]);
					setMatchPercent(conflicts_response.match_percent);
					markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
					setConflictsLoading(false);
					setMatchPercentLoading(false);
				});

				toggleEditSection();
			});
		}
	}

	const checkTeacherAvailability = useCallback((teacher_id, potential_course_period_quarter_days, potential_excluded_sections) => 
	{
		const teacher_index = teachers.findIndex(teacher => teacher.teacher_id === teacher_id);
		if(teacher_index === -1) return false;

		// Get all sections where the teacher is primary OR secondary teacher
		const teacher_sections = sections.filter(section =>
			(section.teacher_id === teacher_id || (Array.isArray(section.secondary_teachers) && section.secondary_teachers.includes(teacher_id))) &&
			section.course_periods.length > 0 &&
			!potential_excluded_sections.includes(section.section_id)
		);

		for(const section of teacher_sections)
		{
			const section_course_periods = section.course_periods;
			const section_quarter_days = section.quarter_days;

			for(const potential_cpqd of potential_course_period_quarter_days)
			{
				const potential_course_period_id = potential_cpqd.course_period_id;

				// Skip if this course period doesn't conflict
				if(!section_course_periods.includes(potential_course_period_id)) continue;

				// Course period conflicts — check if any quarter-day also conflicts
				const potential_qds = potential_cpqd.quarter_days;
				for(const qd of section_quarter_days)
				{
					const conflicts = potential_qds.findIndex(p => p.day === qd.day && p.quarter === qd.quarter);
					if(conflicts !== -1) return false;
				}
			}
		}

		return true;
	},[teachers, sections])

	const checkStudentAvailability = useCallback((student_id, potential_course_period_quarter_days, potential_excluded_sections) =>
	{
		let student_available = true; 

		const student_index = students.findIndex(student => student.student_id === student_id);
		if(student_index === -1) student_available = false;

		// Get all of students sections during this potential
		const student_sections = sections.filter(section => section.student_list.includes(student_id) && section.course_periods.length > 0 && !potential_excluded_sections.includes(section.section_id));

		student_sections.forEach(section => {
			const section_course_periods = section.course_periods;
			const section_quarter_days = section.quarter_days;

			// Iterate through all this section's course period quarter days to see if student is available for all of them
			potential_course_period_quarter_days.forEach(potential_course_period_quarter_day => {
				const potential_course_period_id = potential_course_period_quarter_day.course_period_id;

				// Skip if this course period doesn't conflict
				if(!section_course_periods.includes(potential_course_period_id)) return;

				// If we got this far it means the course period conflicts
				// Check to see if quarter day also conflicts
				const potential_quarter_days = potential_course_period_quarter_day.quarter_days;
				section_quarter_days.forEach(quarter_day => {
					const section_day = quarter_day.day;
					const section_quarter = quarter_day.quarter;
	
					const section_quarter_day_index = potential_quarter_days.findIndex(quarter_day => quarter_day.day === section_day && quarter_day.quarter === section_quarter);
	
					if(section_quarter_day_index !== -1) student_available = false;
				});
			});
		});

		return student_available;
	},[students, scheduleStructureData, sections, scheduleType])

	const checkClassroomAvailability = useCallback((classroom_id, potential_course_period_quarter_days, potential_excluded_sections) => 
	{
		let classroom_available = true; 

		const classroom_index = classrooms.findIndex(classroom => classroom.classroom_id === classroom_id);
		if(classroom_index === -1) classroom_available = false;

		const classroom_info = (classroom_index !== -1) ? classrooms[classroom_index] : null;
		const is_multi_class = (classroom_info) ? classroom_info.multi_class : false;

		// If this is a classroom that allows multiple sections then it will automatically be available
		if(is_multi_class === '1') return classroom_available;
			
		// Get all of classrooms sections during this potential
		const classroom_sections = sections.filter(section => section.classroom_id === classroom_id && section.course_periods.length > 0 && !potential_excluded_sections.includes(section.section_id));

		classroom_sections.forEach(section => {
			const section_course_periods = section.course_periods;
			const section_quarter_days = section.quarter_days;

			// Iterate through all this section's course period quarter days to see if classroom is available for all of them
			potential_course_period_quarter_days.forEach(potential_course_period_quarter_day => {
				const potential_course_period_id = potential_course_period_quarter_day.course_period_id;

				// Skip if this course period doesn't conflict
				if(!section_course_periods.includes(potential_course_period_id)) return;

				// If we got this far it means the course period conflicts
				// Check to see if quarter day also conflicts
				const potential_quarter_days = potential_course_period_quarter_day.quarter_days;
				section_quarter_days.forEach(quarter_day => {
					const section_day = quarter_day.day;
					const section_quarter = quarter_day.quarter;
	
					const section_quarter_day_index = potential_quarter_days.findIndex(quarter_day => quarter_day.day === section_day && quarter_day.quarter === section_quarter);
	
					if(section_quarter_day_index !== -1) classroom_available = false;
				});
			});
		});

		return classroom_available;
	},[classrooms, sections]);

	const addSecondaryTeacher = (passedData) => {
		const section_id = passedData.section_id;
		const secondary_teacher_id = passedData.secondary_teacher_id;

		if(!secondary_teacher_id || secondary_teacher_id === '')
		{
			sectionsData[section_id].secondary_teachers.push(secondary_teacher_id);
		}
		else
		{
			const secondary_teacher_index = sectionsData[section_id].secondary_teachers.findIndex(st_id => st_id === '');
			sectionsData[section_id].secondary_teachers[secondary_teacher_index] = secondary_teacher_id;
		}
		
		setSectionsData({...sectionsData});
		setAvailabilitiesCalculated(false);
		setChangesMade(true);
	}

	const changeSecondaryTeacher = (passedData) => {
		const section_id = passedData.section_id;
		const old_secondary_teacher_id = passedData.old_secondary_teacher_id;
		const new_secondary_teacher_id = passedData.new_secondary_teacher_id;
		
		// Update the state
		const secondary_teacher_index = sectionsData[section_id].secondary_teachers.findIndex(st_id => st_id === old_secondary_teacher_id);
		sectionsData[section_id].secondary_teachers[secondary_teacher_index] = new_secondary_teacher_id;
		
		setSectionsData({...sectionsData});
		setAvailabilitiesCalculated(false);
		setChangesMade(true);
	}

	const removeSecondaryTeacher = (passedData) => {
		const section_id = passedData.section_id;
		const secondary_teacher_id = passedData.secondary_teacher_id;

		// Update the state
		const secondary_teacher_index = sectionsData[section_id].secondary_teachers.findIndex(st_id => st_id === secondary_teacher_id);
		if(secondary_teacher_index !== -1) sectionsData[section_id].secondary_teachers.splice(secondary_teacher_index, 1);
		
		setSectionsData({...sectionsData});
		setAvailabilitiesCalculated(false);
		setChangesMade(true);
	}

	///////////////////////////////
	/////// Period Functions //////
	///////////////////////////////
	const togglePeriodOptions = () => {
		editingTracker['main']['period_options_open'] = !editingTracker['main']['period_options_open'];
		setEditingTracker({...editingTracker});
	}

	const removeCoursePeriod = (course_period_id) => {
		const course_period_index = sectionsData['main'].course_periods.findIndex(cpid => cpid === course_period_id);

		if(course_period_index !== -1) sectionsData['main'].course_periods.splice(course_period_index, 1);

		setSectionsData({...sectionsData});
		setAvailabilitiesCalculated(false);
		setChangesMade(true);
	}

	const addCoursePeriod = (course_period_id) => {
		const course_period_index = sectionsData['main'].course_periods.findIndex(cpid => cpid === course_period_id);

		if(course_period_index === -1) sectionsData['main'].course_periods.push(course_period_id);

		setSectionsData({...sectionsData});
		setAvailabilitiesCalculated(false);
		togglePeriodOptions();
		setChangesMade(true);
	}
	
	const updateData = (change_type, section_id, data_key, data_id) => {
		if(change_type === 'add')
		{
			const data_add_index = sectionsData[section_id][data_key].findIndex(data_val => data_val === data_id);
			if(data_add_index === -1) sectionsData[section_id][data_key].push(data_id)
		}
		else if(change_type === 'change')
		{
			if(data_key === 'section_number') data_id = sanitizeSectionNumberInput(data_id);

			// If changing course, then change quarter days and clear student list
			if(data_key === 'course_id') 
			{
				const previous_course_id = sectionsData[section_id].course_id;

				if(section_id === 'main')
				{
					// Const used to help decided quarter days
					const semester_to_quarters = {'FY': ['1', '2', '3', '4'], 'S':['1', '2'], 'S1':['1','2'], 'S2':['3','4'], 'Q':['1'], 'Q1':['1'], 'Q2':['2'], 'Q3':['3'], 'Q4':['4']};

					// Initialize quarter days
					sectionsData[section_id]['quarter_days'] = [];

					// If canceling course id, then reset course periods and quarter days
					// Else, figure out course periods and quarter days to set
					if(!data_id)
					{
						sectionsData[section_id]['course_periods'] = [];
					}
					else
					{
						const course_info = courses.find(course => course.course_id === data_id);
						const num_periods_spanned = (course_info) ? course_info.num_periods_spanned : 1;
						const course_semester = (course_info) ? course_info.semester : null;
						const quarters_to_add = (course_semester && (course_semester in semester_to_quarters)) ? semester_to_quarters[course_semester] : [];
						const anchor_course_period_id = getFallbackCoursePeriodId(section_id);
						const fallback_quarter_days = getDefaultQuarterDaysForCourse(quarters_to_add);

						if(!anchor_course_period_id)
						{
							sectionsData[section_id]['course_periods'] = [];
							sectionsData[section_id]['quarter_days'] = fallback_quarter_days;
						}
						else
						{
							// If current day is passed, use current day
							// If current day isn't passed, the find the first day that includes the coursePeriodID that is passed
							let day_with_course_period_id = currentDay;
							if(!day_with_course_period_id && scheduleStructureData.course_periods[anchor_course_period_id])
							{
								const course_period_days = scheduleStructureData.course_periods[anchor_course_period_id].days;
								for(const day in course_period_days)
								{
									const cp_schedule_period = course_period_days[day];
									if(!day_with_course_period_id && cp_schedule_period)
									{
										day_with_course_period_id = day;
										break;
									}
								}
							}

							if(!day_with_course_period_id)
							{
								day_with_course_period_id = Object.keys(scheduleStructureData.schedule_structure)[0] || null;
							}

							const day_schedule_rows = day_with_course_period_id ? (scheduleStructureData.schedule_structure[day_with_course_period_id] || []) : [];
							const anchor_schedule_row = day_schedule_rows.find(day_ss => day_ss.course_period_id === anchor_course_period_id) || null;

							if(!anchor_schedule_row)
							{
								sectionsData[section_id]['course_periods'] = [];
								sectionsData[section_id]['quarter_days'] = fallback_quarter_days;
							}
							else
							{
								const new_schedule_period_order = parseInt(anchor_schedule_row.period_order);
								
								let new_course_periods = [];
								let new_quarter_days = [];
								let current_period_order = new_schedule_period_order;
								let logged_period_orders = [new_schedule_period_order];
								
								new_course_periods.push(anchor_course_period_id);

								// Get schedule period with the max period order
								// Used later to see if we've bumped into the last period of the day
								const max_period_order = day_schedule_rows.reduce((results, day_ss_row) => {
									const period_order = parseInt(day_ss_row.period_order);
									if(period_order > results) results = period_order;
									return results;
								}, -1);

								for(let i = 1; i < num_periods_spanned; i++)
								{
									let index = 0;
									let section_new_course_period_id = null;

									while(!section_new_course_period_id && index < 5)
									{
										// If we've used every period once, then clear logged period orders
										if(logged_period_orders.length === day_schedule_rows.length) 
										{
											logged_period_orders = [];
											current_period_order = new_schedule_period_order;
										}
					
										// If we haven't reached max period order, then keep incrementing up
										// If we have hit max period order, increment down
										current_period_order = (!logged_period_orders.includes(max_period_order)) ? ++current_period_order: --current_period_order;
					
										// If period order hasn't been used yet, then find course period during this schedule period
										if(!logged_period_orders.includes(current_period_order)) 
										{
											// First find schedule period with this period order
											let temp_day_ss_row = null;
											for(const day_ss_row of day_schedule_rows)
											{
												const period_order = parseInt(day_ss_row.period_order);
												if(period_order === current_period_order)
												{
													temp_day_ss_row = day_ss_row;
													break;
												}
											}
					
											// Then find course period id in this schedule period on current day
											section_new_course_period_id = (temp_day_ss_row) ? temp_day_ss_row.course_period_id : null;
					
											// Log the use of this period order
											logged_period_orders.push(current_period_order);
											if(section_new_course_period_id) new_course_periods.push(section_new_course_period_id);
										}
										index++;
									}
								}

								// Find days for this course period id
								const days_to_add = new_course_periods.reduce((results, new_course_period_id) => {
									const days_for_cp = scheduleStructureData.course_periods[new_course_period_id].days;

									for(const day in days_for_cp)
									{
										const schedule_period_on_day = days_for_cp[day];
										if(schedule_period_on_day && !results.includes(day)) results.push(day);
									}

									return results;
								},[]);

								// Get new quarter days
								for(const day of days_to_add)
								{
									for(const quarter of quarters_to_add)
									{
										new_quarter_days.push({day:day, quarter:quarter});
									}
								}

								sectionsData[section_id]['course_periods'] = new_course_periods;
								sectionsData[section_id]['quarter_days'] = new_quarter_days;
							}
						}
					}
				}

				sectionsData[section_id]['student_list'] = [];
				sectionsData[section_id]['course_id'] = data_id;

				if(!data_id)
				{
					sectionsData[section_id]['section_number'] = null;
					sectionsData[section_id]['section_sequence'] = null;
				}
				else if(`${previous_course_id || ''}` !== `${data_id}`)
				{
					applySuggestedSectionIdentifiers(section_id, data_id, sectionsData, {forceNext: true});
				}
				else if(!sanitizeSectionNumberInput(sectionsData[section_id]['section_number']))
				{
					applySuggestedSectionIdentifiers(section_id, data_id);
				}

				if(section_id === 'main') syncPlaceholderInclusionSubsectionsWithMain(sectionsData);
				syncSameCourseSubsectionIdentifiers(sectionsData);
				setAvailabilitiesCalculated(false);
			}
			else
			{
				sectionsData[section_id][data_key] = data_id;
				if(data_key === 'section_number' && section_id === 'main') syncSameCourseSubsectionIdentifiers(sectionsData);
				if(data_key === 'classroom_id' && section_id === 'main' && Array.isArray(sectionsData.main.subsections))
				{
					sectionsData.main.subsections.forEach(subsection_id => {
						if(sectionsData[subsection_id] && sectionsData[subsection_id].has_separate_classroom !== '1') sectionsData[subsection_id].classroom_id = data_id || null;
					});
				}
				if(data_key === 'classroom_id') setAvailabilitiesCalculated(false);
			}
		}
		else if(change_type === 'remove')
		{
			const data_index = sectionsData[section_id][data_key].findIndex(data => data === data_id);
			if(data_index !== -1) sectionsData[section_id][data_key].splice(data_index, 1);
		}
		
		setSectionsData({...sectionsData});
		if(!['student_list', 'section_number', 'labels'].includes(data_key)) hideAllSectionEditing(section_id, {preserveAdvancedSettingsOpen: data_key === 'classroom_id' && section_id !== 'main'});
		setChangesMade(true);
	}

	const updateSectionJSON = (new_students, returned_sections, deleted_sections, student_id = null, student_navigation_ids = null) => {
		let new_subsections = cloneObj(subsections);

		// Delete all deleted sections, and remove student matches
		deleted_sections.forEach(section_id => {
			const section_index = sections.findIndex(section => `${section.section_id}` === `${section_id}`);
			const filtered_results_index = filteredResults.findIndex(section => `${section.section_id}` === `${section_id}`);
			const section_info = sections[section_index];
			const section_course_id = section_info.course_id;
			const section_is_inclusion = sectionRequestIsInclusion(section_info);
			const request_is_inclusion = getRequestInclusionValueForSection(section_info);
			const deleted_students = section_info.student_list;
			
			// delete sections from students and reset student matches
			deleted_students.forEach(student_id => {
				const student_index = new_students.findIndex(student => student.student_id === student_id);
				const student_info = new_students[student_index];
				const student_requests = student_info.student_requests;
				const student_sections = student_info.student_sections;
				const student_locked_sections = student_info.student_locked_sections;

				// remove student section
				const student_section_index = student_sections.findIndex(section => `${section}` === `${section_id}`);
				if(student_section_index !== -1) student_sections.splice(student_section_index, 1);

				// remove student locked_section
				const student_locked_section_index = student_locked_sections.findIndex(section => `${section}` === `${section_id}`);
				if(student_locked_section_index !== -1) student_locked_sections.splice(student_locked_section_index, 1);
				
				// mark as unmatched
				const request_match = findCourseRequestInStudentRequests(student_requests, section_course_id, {
					isInclusion: scheduleInfo.separate_ICR_codes === '0' ? request_is_inclusion : null,
				});
				if(request_match) request_match.request_info.matched = '0';
			});

			// Update sections
			sections.splice(section_index, 1);

			// Update subsections
			new_subsections = new_subsections.filter(section_subsection => `${section_subsection.section_id}` !== `${section_id}` && `${section_subsection.subsection_id}` !== `${section_id}`);

			// Update any filtered results on Magnetboard
			if(filtered_results_index !== -1) filteredResults.splice(filtered_results_index, 1);
			});

			let subsections_to_add = [];
			returned_sections.forEach(section_info => {
				const section_id = section_info.section_id;
				const section_type = section_info.section_type || sectionType;
				section_info.section_type = section_type;
				const course_id = section_info.course_id;
				const teacher_id = section_info.teacher_id;
			const student_list = section_info.student_list;
			const span_id = section_info.span_id;
			const is_subsection = ('is_subsection' in section_info) ? section_info.is_subsection : '0';

			if(is_subsection === '1') 
			{
				const main_section = returned_sections.find(temp_section => Array.isArray(temp_section.subsections) && temp_section.subsections.some(subsection_id => `${subsection_id}` === `${section_id}`));
				const main_section_id = (main_section) ? main_section.section_id : null;
				if(main_section_id) subsections_to_add.push({subsection_id:section_id, course_id:course_id, teacher_id:teacher_id, section_id:main_section_id});
			}

			const course_index = courses.findIndex(course => `${course.course_id}` === `${course_id}`);
			const course_info = (course_index !== -1) ? courses[course_index] : null;
			const teacher_index = teachers.findIndex(teacher => `${teacher.teacher_id}` === `${teacher_id}`);
			const teacher_info = (teacher_index !== -1) ? teachers[teacher_index] : null;

			// Add departments to section info
			section_info['departments'] = course_info ? course_info.departments : [];

			// See if previous section id existed
			// If yes, we will replace old data
			// If no, then push new data
			const previous_section_index = sections.findIndex(section => `${section.section_id}` === `${section_id}`);
			const previous_filtered_results_index = filteredResults.findIndex(section => `${section.section_id}` === `${section_id}`);

			/////////////////////////////////////////
			///// UPDATE student request matches /////
			/////////////////////////////////////////
			if(section_type === '1')
			{
				// If previous section id existed then pull old student list to make sure we are updating their matches/schedules
				let previous_student_list = [];
				let previous_course_id = null;
				let previous_section_is_inclusion = sectionRequestIsInclusion(section_info);
				
				if(previous_section_index !== -1) 
				{
					const previous_section_info = cloneObj(sections[previous_section_index]);
					previous_student_list = previous_section_info.student_list;
					previous_course_id = previous_section_info.course_id;
					previous_section_is_inclusion = sectionRequestIsInclusion(previous_section_info);
				}
				const section_is_inclusion = sectionRequestIsInclusion(section_info);
				const request_is_inclusion = getRequestInclusionValueForSection(section_info);
				
				const deleted_students = (course_id !== previous_course_id) ? previous_student_list : findDeletedValues(previous_student_list, student_list);
				const added_students = (course_id !== previous_course_id) ? student_list : findAddedValues(previous_student_list, student_list);

				// delete student matches
				deleted_students.forEach(student_id => {
					const student_index = new_students.findIndex(student => `${student.student_id}` === `${student_id}`);
					const student_info = new_students[student_index];
					const student_requests = student_info.student_requests;
					const student_sections = student_info.student_sections;
					const student_locked_sections = student_info.student_locked_sections;

					// remove student section
					const student_section_index = student_sections.findIndex(section => `${section}` === `${section_id}`);
					if(student_section_index !== -1) student_sections.splice(student_section_index, 1);

					// remove student locked_section
					const student_locked_section_index = student_locked_sections.findIndex(section => `${section}` === `${section_id}`);
					if(student_locked_section_index !== -1) student_locked_sections.splice(student_locked_section_index, 1);

					// remove match for student
					const request_match = findCourseRequestInStudentRequests(student_requests, previous_course_id, {
						isInclusion: scheduleInfo.separate_ICR_codes === '0' ? previous_section_is_inclusion : null,
					});
					if(request_match) request_match.request_info.matched = '0';
				});

				// remove added students from any previous sections of this course
				const non_span_sections_with_course = sections.filter(section => `${section.course_id}` === `${course_id}` && (scheduleInfo.separate_ICR_codes !== '0' || sectionRequestIsInclusion(section) === section_is_inclusion) && `${section.section_id}` !== `${section_id}` && `${section.span_id}` !== `${span_id}`);
				non_span_sections_with_course.forEach(swc_section_info => {
					const swc_section_id = swc_section_info.section_id;
					const swc_section_index = sections.findIndex(section => `${section.section_id}` === `${swc_section_id}`);
					
					const swc_student_list = swc_section_info.student_list;
					const swc_student_list_without_added_students = swc_student_list.reduce((results, student_id) => {
						const in_added_student_list = added_students.findIndex(added_student_id => `${added_student_id}` === `${student_id}`) !== -1;
						
						const student_index = students.findIndex(student => `${student.student_id}` === `${student_id}`);
						const student_info = (student_index !== -1) ? students[student_index] : null;
						const student_locked_sections = (student_info) ? student_info.student_locked_sections : [];

						const student_has_section_locked = student_locked_sections.findIndex(locked_section_id => `${locked_section_id}` === `${swc_section_id}`) !== -1;

						if(in_added_student_list && !student_has_section_locked) return results;

						results.push(student_id);
						return results;
					}, []);

					sections[swc_section_index]['student_list'] = swc_student_list_without_added_students;
				});
				
				// add student match
				added_students.forEach(student_id => {
					const student_index = new_students.findIndex(student => `${student.student_id}` === `${student_id}`);
					const student_info = new_students[student_index];
					const student_requests = student_info.student_requests;
					const student_sections = student_info.student_sections;

					// add section to student sections
					const student_section_index = student_sections.findIndex(section => `${section}` === `${section_id}`);
					if(student_section_index === -1) student_sections.push(section_id);

					// add request match
					const request_match = findCourseRequestInStudentRequests(student_requests, course_id, {
						isInclusion: scheduleInfo.separate_ICR_codes === '0' ? request_is_inclusion : null,
					});
					
					if(request_match)
					{
						request_match.request_info.matched = '1';
						request_match.request_info.is_deleted = '0';
					}
					else
					{
						student_requests.push({
							group_id: `NEW-${createUniqueObjectID(7)}`,
							priority:'1',
							course_requests: [{
								course_id:course_id,
								order:'1',
								priority:'1',
								matched:'1',
								data_origin:'2',
								is_inclusion:request_is_inclusion ? '1' : '0',
								is_deleted:'0'
							}]
						});
					}
				});

				// Update teacher can teach
				if(teacher_id)
				{
					const teachers_can_teach = course_info.teachers_can_teach;
					const teacher_can_teach = teacher_info.teacher_can_teach;
					
					const teachers_can_teach_index = teachers_can_teach.findIndex(tct_teacher => `${tct_teacher.teacher_id}` === `${teacher_id}`);
					const teacher_can_teach_index = teacher_can_teach.findIndex(tct_course => `${tct_course.course_id}` === `${course_id}`);
					
					if(teachers_can_teach_index === -1) teachers_can_teach.push({teacher_id:teacher_id, type:'primary'});
					if(teacher_can_teach_index === -1) teacher_can_teach.push({course_id:course_id, type:'primary'});
				}
			}

			// Finally, UPDATE sections JSON... either replace old section data or push new data
			if(previous_section_index !== -1) 
			{
				sections[previous_section_index] = section_info;
				
				if(previous_filtered_results_index !== -1) filteredResults[previous_filtered_results_index] = section_info;
			}
			else
			{
				if('new' in section_info) delete section_info.new;
				sections.push(section_info);
				filteredResults.push(section_info);
			}
		});

		
		for(const subsection_info of subsections_to_add)
		{
			const existing_subsection_index = new_subsections.findIndex(existing_subsection => `${existing_subsection.subsection_id}` === `${subsection_info.subsection_id}`);
			if(existing_subsection_index === -1)
			{
				new_subsections.push(subsection_info);
			}
			else
			{
				new_subsections[existing_subsection_index] = subsection_info;
			}
		}
		

		setSections([...sections]);
		setSubsections(new_subsections);
		setFilteredResults([...filteredResults]);
		setCourses([...courses]);
		setStudents([...new_students]);
		setTeachers([...teachers]);
		
		setIsSaving(false);

		if(student_id)
		{
			toggleEditStudentSchedule(student_id, student_navigation_ids);
		}
		else
		{
			toggleEditSection();
		}
	}
	
	const saveSection = async (student_id = null, student_navigation_ids = null) => {
		if(studentOnlyEditing && !sectionID)
		{
			swal({
				title: "Unable to Save",
				text: "Counselors can edit students on existing sections only.",
				icon: "warning"
			});
			return;
		}

		/////////////////////////////////////////////////
		//// Check if all necessary data is inputted ////
		/////////////////////////////////////////////////
		let all_data_inputted = true;

		Object.keys(sectionsData).forEach(section_key => {
			const section_data = sectionsData[section_key];
			
			if(section_key === 'main')
			{
				const course_id = section_data.course_id;
				if(sectionType === '1' && !course_id) all_data_inputted = false;
			}
			else
			{
				const course_id = section_data.course_id;
				if(!course_id) all_data_inputted = false;
			}
		});
		
		if(!all_data_inputted)
		{
			const options =  {
				title: "Missing Data",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>You are missing data! Make sure the section has:</p>
						<ul style={{textAlign:'left', paddingLeft:'100px'}}>
							{sectionType === '1' &&
								<>
								<li>a course</li>
								<li>a teacher OR classroom assigned</li>
								</>
							}
							<li>quarters/days set</li>	
						</ul>
						{sectionType === '1' &&
							<p>You also cannot have subsections without courses!</p>
						}
					</div>
				)
			}
		
			swal(options);
			return;
		}

		syncSameCourseSubsectionIdentifiers(sectionsData);

		const section_number_errors = Object.keys(sectionsData).reduce((results, section_key) => {
			const validation_message = getSectionNumberValidationMessage(section_key);
			if(validation_message) results.push(validation_message);
			return results;
		}, []);

		if(section_number_errors.length > 0)
		{
			swal({
				title: "Invalid Section IDs",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>{section_number_errors[0]}</p>
						<p>Section IDs can be 1-4 letters or numbers and must be unique within a course.</p>
					</div>
				)
			});
			return;
		}
		
		setIsSaving(true);

		if(sectionID && !changesMade)
		{
			setIsSaving(false);

			if(student_id)
			{
				toggleEditStudentSchedule(student_id, student_navigation_ids);
				return;
			}
			else
			{
				toggleEditSection();
				return;
			}
		}

		setConflictsLoading(true);
		setMatchPercentLoading(true);

		////////////////////////
		//// Update Section ////
		////////////////////////
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, section_data:sectionsData, section_type:sectionType};
		
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = (sectionType === '1') ? '/update-section' : '/update-section-placeholder';
		const myInit = { // OPTIONAL
				response: true,
				body: JSON.stringify(data),
		};
	
		try {
			const result = await API.post(apiName, url, myInit);
			const response_data = result.data;
			if(response_data && (response_data.success === false || response_data.msg || response_data.message || response_data.status === 'error'))
			{
				setIsSaving(false);
				setConflictsLoading(false);
				setMatchPercentLoading(false);
				restoreSavedEditState();

				swal({
					title: "Unable to Save",
					icon: "warning",
					buttons: {
						cancel: {
							text: "Okay",
							value: false,
							visible: true,
							className: 'blue-btn'
						}
					},
					content: (
						<div>
							<p>{response_data.msg || response_data.message || 'There was a problem saving this section.'}</p>
						</div>
					)
				});
				return;
			}
			const returned_sections = response_data.returned_sections;
			const deleted_sections = response_data.deleted_sections;
			const returned_student_locked_sections = response_data.student_locked_sections;

			const new_students = cloneObj(students);

			returned_student_locked_sections.forEach(student_section_info => {
				const section_id = student_section_info.section_id;
				const student_id = student_section_info.student_id;
				const is_locked = student_section_info.is_locked;

				const student_index = new_students.findIndex(student => student.student_id === student_id);
				const student_info = (student_index !== -1) ? new_students[student_index]: null;
				const student_locked_sections = (student_info) ? student_info.student_locked_sections : [];

				const section_index = student_locked_sections.findIndex(section => section === section_id);

				if(is_locked === '0' && section_index !== -1)
				{
					new_students[student_index]['student_locked_sections'].splice(section_index, 1);
				}
				else if(is_locked === '1' && section_index === -1)
				{
					new_students[student_index]['student_locked_sections'].push(section_id);
				}
			});
				///////////////////////////
				// Update section info
				///////////////////////////
				updateSectionJSON(new_students, returned_sections, deleted_sections, student_id, student_navigation_ids);
				const refresh_targets = response_data?.refresh_targets || null;
				if(refresh_targets)
				{
					const hasTargetIDs = (target_group = {}) => (
						Array.isArray(target_group.ids) && target_group.ids.length > 0
					) || (
						Array.isArray(target_group.deleted_ids) && target_group.deleted_ids.length > 0
					) || (
						Array.isArray(target_group.course_ids) && target_group.course_ids.length > 0
					);
					const refresh_slices = ['sections', 'subsections'];
					if(hasTargetIDs(refresh_targets.students)) refresh_slices.push('students');
					if(hasTargetIDs(refresh_targets.courses)) refresh_slices.push('courses');
					if(hasTargetIDs(refresh_targets.teachers)) refresh_slices.push('teachers');
					if(hasTargetIDs(refresh_targets.classrooms)) refresh_slices.push('classrooms');

					notifyHSMSDataMutation({
						school_id: schoolInfo.school_id,
						schedule_version_id: schoolInfo.current_schedule_version_id,
						slices: refresh_slices,
						targets: refresh_targets,
						reason: 'save_section',
					});
				}
				//if(sectionType === '1') createInclusionLabels({school_id: schoolInfo.school_id, update_type:'create'});

			///////////////////////////
			// Update conflicts
			///////////////////////////
			calculateConflicts({
				school_id: schoolInfo.school_id,
				schedule_version_id: schoolInfo.current_schedule_version_id,
				match_percent: true,
			}).then(conflicts_response => {
				const conflicts_list = conflicts_response.conflicts;
				setConflicts([...conflicts_list]);
				setMatchPercent(conflicts_response.match_percent);
				markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
				setConflictsLoading(false);
				setMatchPercentLoading(false);
			});
		} catch(error) {
			setIsSaving(false);
			setConflictsLoading(false);
			setMatchPercentLoading(false);
			restoreSavedEditState();

			swal({
				title: "Unable to Save",
				icon: "warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>{error?.response?.data?.msg || error?.response?.data?.message || 'There was a problem saving this section.'}</p>
					</div>
				)
			});
			console.log(error.response);
		}
	}

	const toggleLockSection = () => {
		const all_sections_locked = Object.keys(sectionsData).every(section_id => sectionsData[section_id].locked === '1');
		const new_locked_value = all_sections_locked ? '0' : '1';
		Object.keys(sectionsData).forEach(section_id => {
			sectionsData[section_id].locked = new_locked_value;
			if(new_locked_value === '1' && editingTracker[section_id]) hideAllSectionEditing(section_id);
		});
		setSectionsData({...sectionsData});
		setChangesMade(true);
	}
	
	/////////////////////////////
	///// FOCUS ON INPUT ////////
	/////////////////////////////
	useEffect(() => {
		if(isSaving) return;

		for(const section_id in editingTracker)
		{
			for(const input_type in editingTracker[section_id])
			{
				if(editingTracker[section_id][input_type])
				{	
					////////////////////////////////////////////
					///// GET SUGGESTED DATA AFTER OPENING /////
					////////////////////////////////////////////
					// Show data with restrictions or connections first
					// First get teachers who can teach this course
					let tct_courses = [];
					let tct_teachers = [];
					let course_classroom_restrictions = [];
					let teacher_classroom_restrictions = [];
					let students_requesting = [];
					
					if(input_type === 'course')
					{
						const teacher_id = sectionsData[section_id].teacher_id;

						if(teacher_id)
						{
							const teacher_info = teachers.find(teacher => teacher.teacher_id === teacher_id);
							
							const teacher_can_teach = teacher_info.teacher_can_teach;
							const primary_courses = teacher_can_teach.filter(tct_info => tct_info.type === '1');
							const secondary_courses = teacher_can_teach.filter(tct_info => tct_info.type === '2');
							
							primary_courses.forEach(tct_info => {
								const course_id = tct_info.course_id;
								const course_info = courses.find(course => course.course_id === course_id);
								
								tct_courses.push(course_info);
							});
							
							secondary_courses.forEach(tct_info => {
								const course_id = tct_info.course_id;
								const course_info = courses.find(course => course.course_id === course_id);
								
								tct_courses.push(course_info);
							});
							
							searchResults[section_id]['courses'].suggested = tct_courses;
						}
						
						searchResults[section_id]['courses'].search = courses;

						setSearchResults(s => ({...s}));
					}
					else if(input_type === 'teacher')
					{
						const course_id = sectionsData[section_id].course_id;
					
						if(course_id)
						{
							const course_info = courses.find(course => course.course_id === course_id);
							const teachers_can_teach = course_info.teachers_can_teach;
							const primary_teachers = teachers_can_teach.filter(tct_info => tct_info.type === '1');
							const secondary_teachers = teachers_can_teach.filter(tct_info => tct_info.type === '2');
							
							primary_teachers.forEach(tct_info => {
								const teacher_id = tct_info.teacher_id;
								const teacher_info = teachers.find(teacher => teacher.teacher_id === teacher_id);
								
								tct_teachers.push(teacher_info);
							});
							
							secondary_teachers.forEach(tct_info => {
								const teacher_id = tct_info.teacher_id;
								const teacher_info = teachers.find(teacher => teacher.teacher_id === teacher_id);
								
								tct_teachers.push(teacher_info);
							});
							
							searchResults[section_id]['teachers'].suggested = tct_teachers;
						}
						
						searchResults[section_id]['teachers'].search = teachers;
							
						setSearchResults(s => ({...s}));
					}
					else if(input_type === 'classroom')
					{
						const course_id = sectionsData[section_id].course_id;
						const teacher_id = sectionsData[section_id].teacher_id;
						
						// Get any course-classroom restrictions
						if(course_id)
						{
							const course_info = courses.find(course => course.course_id === course_id);
							const classroom_restrictions = course_info.classroom_restrictions;
							
							classroom_restrictions.forEach(classroom_id => {
								const classroom_info = classrooms.find(classroom => classroom.classroom_id === classroom_id);
								
								course_classroom_restrictions.push(classroom_info);
							});
							
							searchResults[section_id]['classrooms'].suggested.course_classroom_restrictions = course_classroom_restrictions;
						}

						// Get any teacher-classroom preferences
						if(teacher_id)
						{
							const teacher_info = teachers.find(teacher => teacher.teacher_id === teacher_id);
							const classroom_restrictions = teacher_info.classroom_restrictions;
							
							classroom_restrictions.forEach(classroom_id => {
								const classroom_info = classrooms.find(classroom => classroom.classroom_id === classroom_id);
								
								teacher_classroom_restrictions.push(classroom_info);
							});
							
							searchResults[section_id]['classrooms'].suggested.teacher_classroom_restrictions = teacher_classroom_restrictions;
						}

						searchResults[section_id]['classrooms'].search = classrooms;

						setSearchResults(s => ({...s}));
					}
					else if(input_type === 'students')
					{
						const section_info = sectionsData[section_id];
						const course_id = section_info.course_id;
						const section_is_inclusion = sectionRequestIsInclusion(section_info);
					
						if(course_id)
						{
							const student_requests = students.reduce((results, student) => {
								const requests = student.student_requests;
								const request_match = findCourseRequestInStudentRequests(requests, course_id, getRequestMatchOptionsForSection(section_info, false));

								if(request_match)
								{
									const priority = request_match.priority || '1';
									const matched = sectionsData[section_id].student_list.includes(student.student_id) || (sections.findIndex(section => sectionMatchesRequestContext(section, course_id, section_is_inclusion) && section.student_list.includes(student.student_id)) !== -1);

									results.push({student_id:student.student_id, priority:priority, matched:matched});
								}

								return results;
							}, []);
							const unmatched = student_requests.filter(request_info => !request_info.matched);
							const matched = student_requests.filter(request_info => request_info.matched);
							
							unmatched.forEach(request_info => {
								const student_id = request_info.student_id;
								const student_info = students.find(student => student.student_id === student_id);
								
								students_requesting.push(student_info);
							});
							
							matched.forEach(request_info => {
								const student_id = request_info.student_id;
								const student_info = students.find(student => student.student_id === student_id);
								
								students_requesting.push(student_info);
							});
							
							searchResults[section_id]['students'].suggested = students_requesting;
						}
						
						if(searchResults[section_id]['students'].suggested.length === 0) searchResults[section_id]['students'].search = students;
							
						setSearchResults(s => ({...s}));
					}
				}
			}
		}
	}, [editingTracker, classrooms, courses, sections, sectionsData, students, teachers]);
	
	
	/////////////////////////
	///// DO ON LOAD ////////
	/////////////////////////

	useEffect(() => {
		if(isSaving) return; 

		const semester_to_quarters = {'FY':['1','2','3','4'], 'S':['1','2'], 'S1':['1','2'], 'S2':['3','4'], 'Q':['1'], 'Q1':['1'], 'Q2':['2'], 'Q3':['3'], 'Q4':['4']};

		let num_days_array = [];
		let num_days_in_cycle = Object.keys(scheduleStructureData.schedule_structure).length;
		for(var day = 1; day<=num_days_in_cycle; day++)
		{
			num_days_array.push(`${day}`);
		}
		setNumDaysInCycle((numDaysInCycle) => ([...num_days_array]));

		if(sectionID)
		{
			// Set main section data and input editing tracker
			const main_section_index = sections.findIndex(section => section.section_id === sectionID);
			if(main_section_index === -1) return;
			const main_section_data = cloneObj(sections[main_section_index]);

			const locked_students = ('student_list' in main_section_data) ? main_section_data.student_list.reduce((results, student_id) => {
				const student_index = students.findIndex(student => student.student_id === student_id);
				if(student_index === -1) return results;
				const student_info = students[student_index];
				const student_locked_sections = ('student_locked_sections' in student_info) ? student_info.student_locked_sections : [];
				if(student_locked_sections.includes(sectionID)) results.push(student_id);
				return results;
			}, []) : [];
			
			// Set up main section data
			sectionsData.main = main_section_data;
			sectionsData.main.locked_students = locked_students;
			sectionsData.main.has_separate_quarter_days = sectionsData.main.has_separate_quarter_days || '1';
			sectionsData.main.has_separate_classroom = sectionsData.main.has_separate_classroom || '1';

			// Set up main section trackers and search results
			editingTracker.main = {course: false, teacher: false, classroom:false, section_number:false, label_options_open:false, period_options_open:false};
			searchResults.main = {courses: {suggested:[], search:[]}, teachers:{suggested:[], search:[]}, classrooms: {suggested:{course_classroom_restrictions:[], teacher_classroom_restrictions:[]}, search:[]}, students:{suggested_filter_ids:[], suggested:[], search:[]}};
			
			// Set subsection data and input editing tracker
			const subsections = sectionsData.main.subsections;
			
			/// Set up all Subsections ////
			subsections.forEach(subsection_id => {
				const subsection_index = sections.findIndex(section => section.section_id === subsection_id);

				if(subsection_index !== -1)
				{
					const subsection_data = cloneObj(sections[subsection_index]);

					const subsection_locked_students = subsection_data.student_list.reduce((results, student_id) => {
						const student_index = students.findIndex(student => student.student_id === student_id);
						if(student_index === -1) return results;
						const student_info = students[student_index];
						const student_locked_sections = student_info.student_locked_sections;
						if(student_locked_sections.includes(subsection_id)) results.push(student_id);
						return results;
					}, []);
				
					// Set up subsection data
					sectionsData[subsection_id] = subsection_data;
					sectionsData[subsection_id].locked_students = subsection_locked_students;
					sectionsData[subsection_id].locked = sectionsData[subsection_id].locked || '0';
					sectionsData[subsection_id].has_separate_quarter_days = sectionsData[subsection_id].has_separate_quarter_days || '0';
					sectionsData[subsection_id].has_separate_classroom = sectionsData[subsection_id].has_separate_classroom || '0';
					if(sectionsData[subsection_id].has_separate_classroom !== '1') sectionsData[subsection_id].classroom_id = sectionsData.main.classroom_id || null;

					// Set up subsection trackers and search results
					editingTracker[subsection_id] = {course: false, teacher: false, classroom:false, students: false, section_number:false, label_options_open:false, advanced_settings_open:false};
					searchResults[subsection_id] = {courses:{suggested:[], search:[]}, teachers:{suggested:[], search:[]}, classrooms: {suggested:{course_classroom_restrictions:[], teacher_classroom_restrictions:[]}, search:[]}, students:{suggested_filter_ids:[], suggested:[], search:[]}};
				}
			});

			Object.keys(sectionsData).forEach(temp_section_id => {
				const temp_section_info = sectionsData[temp_section_id];
				if(!temp_section_info || !temp_section_info.course_id || isPlaceholderInclusionSectionForSnapshot(temp_section_info, sectionsData)) return;
				if(sanitizeSectionNumberInput(temp_section_info.section_number)) return;
				applySuggestedSectionIdentifiers(temp_section_id, temp_section_info.course_id, sectionsData);
			});
			syncPlaceholderInclusionSubsectionsWithMain(sectionsData);

			// Create predictive quarter days if they don't exist already
			if(main_section_data.quarter_days.length <= 0 && main_section_data.course_id)
			{
				const course_info = courses.find(course => course.course_id === main_section_data.course_id);
				const course_semester = (course_info) ? course_info.semester : null;
				const course_default_quarters = (course_semester) ? semester_to_quarters[course_semester] : [];

				numDaysInCycle.forEach(day => {
					course_default_quarters.forEach(quarter => {
						sectionsData['main']['quarter_days'].push({quarter:quarter, day:day});
					});
				});

				setChangesMade(true);
			}
			
			// Set section data
			setSectionsData(sectionsData => ({...sectionsData}));

			const sectionsKeys = Object.keys(sectionsData).reduce((acc, sectionKey) => {
				acc[sectionKey] = [];
				return acc;
			}, {});

			savedEditStateRef.current = {
				sectionsData: cloneObj(sectionsData),
				editingTracker: cloneObj(editingTracker),
				searchResults: cloneObj(searchResults),
				sectionsSelectedStudents: cloneObj(sectionsKeys),
				sectionsSelectedLockedStudents: cloneObj(sectionsKeys),
			};
			setSectionsSelectedStudents(sectionsKeys);
			setSectionsSelectedLockedStudents(sectionsKeys);

			// Set editing trackers
			setEditingTracker(editingTracker => ({...editingTracker}));
			
			// Set search results
			setSearchResults(searchResults => ({...searchResults}));
		}
		else
		{
			const newSectionID = createUniqueObjectID(5, sectionsData);
			const teacher_id = (currentView === 'teacher') ? dataID : null;
			const course_id = (currentView === 'course') ? dataID : null;
			const classroom_id = (currentView === 'classroom') ? dataID : null;

			let default_quarter_days = [];
			numDaysInCycle.forEach(day => {
				quarters.current.forEach(quarter => {
					default_quarter_days.push({day:day, quarter:quarter});
				});
			});

			const initial_course_periods = (coursePeriodID) ? [coursePeriodID] : []; 
			
			// Set section data
			setSectionsData((sectionsData) => ({'main': {new: true, section_type:sectionType, section_id:newSectionID, section_number:null, section_sequence:null, course_id:course_id, teacher_id:teacher_id, secondary_teachers:[], classroom_id:classroom_id, course_periods:initial_course_periods, quarter_days:default_quarter_days, raw_quarter_days:default_quarter_days, student_list:[], locked_students:[], subsections:[], span_id:null, labels:[], is_lab:'0', is_inclusion:'0', locked:'0', has_separate_quarter_days:'1', has_separate_classroom:'1'}}));
			
			// Set editing trackers
			setEditingTracker((editingTracker) => ({'main': {course: false, teacher: false, classroom:false, students:false, section_number:false, period_options_open:false}}));
			
			// Set search results
			setSearchResults((searchResults) => ({'main': {courses: {suggested:[], search:[]}, teachers:{suggested:[], search:[]}, classrooms: {suggested:{course_classroom_restrictions:[], teacher_classroom_restrictions:[]}, search:[]}, students:{suggested_filter_ids:[], suggested:[], search:[]}}}));
		}

		const period_type = getPeriodTypeFromScheduleType(scheduleType, {
			alternating_uses_day_course_periods: false,
		});
		const displayed_periods = buildPeriodsToShow(scheduleStructureData, {
			period_type,
			current_day: '1',
			natural_sort_course_periods: true,
		});
		const period_options = displayed_periods.reduce((results, period_info) => {
			const period_id = period_info.period_id;
			const period_name = period_info.period_name;
			const course_period_id = getCoursePeriodIdForPeriod(scheduleStructureData, period_id, {
				period_type,
				schedule_type: scheduleType,
				current_day: '1',
			});
			if(!course_period_id || !period_name) return results;

			results.push({value: course_period_id, display: period_name});
			return results;
		}, []);
		
		setPeriodOptions((periodOptions) => (period_options));	
		setIsLoading(false);
	}, [sections, students, courses, currentView, dataID, scheduleStructureData.schedule_periods, scheduleStructureData.course_periods, scheduleStructureData.schedule_structure, scheduleType, sectionID, sectionType]);

	// Calculate Teacher, Student, and Classroom Availabilities
	useEffect(() => {
		if(!studentsLoading && !teachersLoading && !coursesLoading && !classroomsLoading && !sectionsLoading && !isLoading && !isSaving)
		{
			if(availabilitiesCalculated) return;
			if(sectionType !== '1' || !sectionsData['main'].course_periods || sectionsData['main'].course_periods.length === 0 || sectionsData['main'].quarter_days.length <= 0) return;

			const schedule_structure = scheduleStructureData.schedule_structure;
			let excluded_sections = (sectionID) ? [sectionID] : [];

			/////////////////////////////////////////////////////////////////////////////////////////////////////////
			//// COLLECT ALL SPACES IN THE SCHEDULE THAT WOULD NEED TO BE (COULD BE) OPEN TO FULFILL THIS COURSE ////
			/////////////////////////////////////////////////////////////////////////////////////////////////////////
			let potential_course_period_quarter_days = [];

			// Start by collecting the main sections course periods
			sectionsData['main'].course_periods.forEach(course_period_id => {
				potential_course_period_quarter_days.push({course_period_id:course_period_id, quarter_days: sectionsData['main'].quarter_days});
			});

			// If span, collect the spans as course periods/quarters that would need to be open for something to be available
			if(sectionsData['main'].span_id)
			{
				const main_section_info = sectionsData['main']
				const main_section_id = main_section_info.section_id;

				// Get all other spanned sections
				const spanned_sections = sections.filter(section => section.span_id === sectionsData['main'].span_id && section.section_id !== main_section_id);

				potential_course_period_quarter_days = spanned_sections.reduce((results, section) => {
					const section_id = section.section_id;
					const section_course_periods = section.course_periods;
					const section_quarter_days = section.quarter_days;
					// skip any unplaced sections
					if(section_course_periods.length === 0) return results;

					// Add all labs section IDs to excluded sections 
					if(!excluded_sections.includes(section_id)) excluded_sections.push(section_id);

					// Now add all the lab course period quarter days to list of necessary open slots
					section_course_periods.forEach(course_period_id => {
						results.push({course_period_id:course_period_id, quarter_days: section_quarter_days});
					});
					
					return results;
				}, potential_course_period_quarter_days);
			}

			// If daily schedule, then there could be overlapping periods that create conflicts for some of the periods
			if(scheduleInfo.schedule_type === 'daily')
			{
				// For each potential, add all conflicting periods to potentials that needs to be open for this to be considered available
				const new_conflicting_course_period_quarter_days = [];
				for(const potential_course_period_info of potential_course_period_quarter_days)
				{
					const potential_course_period_id = potential_course_period_info.course_period_id;
					const potential_quarter_days = potential_course_period_info.quarter_days;
					const course_period_schedule_periods = scheduleStructureData.course_periods[potential_course_period_id]['days'];

					for(const day in course_period_schedule_periods)
					{
						const schedule_period_id = course_period_schedule_periods[day];

						if(!schedule_period_id) continue;
						
						const conflicting_schedule_periods = scheduleStructureData.availability_matrix[schedule_period_id];
						for(const day in schedule_structure)
						{
							const day_schedule_periods = schedule_structure[day];

							for(const temp_schedule_period_id in day_schedule_periods)
							{
								if(conflicting_schedule_periods.includes(temp_schedule_period_id))
								{
									const temp_course_period_id = day_schedule_periods[temp_schedule_period_id];

									// Add the conflicting course period id (copying the quarter days) to the list of 
									new_conflicting_course_period_quarter_days.push({course_period_id:temp_course_period_id, quarter_days:potential_quarter_days});
								}
							}
						}
					}
				}

				// Merge the original potential course period quarter days with the new conflicting ones
				potential_course_period_quarter_days = [...potential_course_period_quarter_days, ...new_conflicting_course_period_quarter_days];
			}

			const teacher_potentials = cloneObj(potential_course_period_quarter_days);
			const student_potentials = cloneObj(potential_course_period_quarter_days);
			const classroom_potentials = cloneObj(potential_course_period_quarter_days);

			let teachers_availability = [];
			teachers.forEach(teacher_info => {
				const teacher_availability = checkTeacherAvailability(teacher_info.teacher_id, teacher_potentials, excluded_sections);
				teachers_availability.push({teacher_id:teacher_info.teacher_id, availability:teacher_availability});
			});
			setTeachersAvailability([...teachers_availability]);

			let students_availability = [];
			students.forEach(student_info => {
				const student_availability = checkStudentAvailability(student_info.student_id, student_potentials, excluded_sections);
				students_availability.push({student_id:student_info.student_id, availability:student_availability});
			});
			setStudentsAvailability([...students_availability]);

			let classrooms_availability = [];
			classrooms.forEach(classroom_info => {
				const classroom_availability = checkClassroomAvailability(classroom_info.classroom_id, classroom_potentials, excluded_sections);
				classrooms_availability.push({classroom_id:classroom_info.classroom_id, availability:classroom_availability});
			});
			setClassroomsAvailability([...classrooms_availability]);

			setAvailabilitiesCalculated(true);
		}
	}, [classroomsLoading, coursesLoading, studentsLoading, teachersLoading, sectionsLoading, currentDay, currentViewType, isLoading, numDaysInCycle, scheduleInfo.schedule_type, scheduleStructureData, sectionID, sectionType, sectionsData, sections, teachers, students, courses, classrooms, availabilitiesCalculated, checkClassroomAvailability, checkStudentAvailability, checkTeacherAvailability]);

	//// Collect all Teams/Labels ////
	const section_labels = labels.filter(label => label);
	const sorted_labels = sortArrayOfObjects(section_labels, 'label', 'text', 'asc');

	///// Collect All Main Section Data /////
	const mainSectionLoaded = ('main' in sectionsData);
	
	const main_course_info = (mainSectionLoaded && sectionsData.main.course_id) ? courses.find(course => course.course_id === sectionsData.main.course_id) : null;
	const main_teacher_info = (mainSectionLoaded && sectionsData.main.teacher_id) ? teachers.find(teacher => teacher.teacher_id === sectionsData.main.teacher_id) : null;
	const main_classroom_info = (mainSectionLoaded && sectionsData.main.classroom_id) ? classrooms.find(classroom => classroom.classroom_id === sectionsData.main.classroom_id) : null;
	
	const main_course_name = (main_course_info) ? `${main_course_info.name} (${main_course_info.course_code})` : null;
	const main_teacher_name = (main_teacher_info) ? `${main_teacher_info.name}, ${main_teacher_info.first_name}` : null;
	const main_classroom_name = (main_classroom_info) ? `${main_classroom_info.classroom_name}` : null;

	const main_quarter_days = (mainSectionLoaded && sectionsData.main.quarter_days) ? sectionsData.main.quarter_days : [];
	const new_section_placement_incomplete = mainSectionLoaded
		&& sectionsData.main.new === true
		&& (
			!Array.isArray(sectionsData.main.course_periods)
			|| sectionsData.main.course_periods.filter(Boolean).length === 0
			|| !main_quarter_days.some(quarter_day => `${quarter_day?.quarter || ''}`.trim())
		);
	const renderAvailabilityStatus = (available, data_type, data_id) => {
		const status_text = new_section_placement_incomplete ? 'Availability' : available ? 'Available' : 'Unavailable';
		const status_color = new_section_placement_incomplete ? 'blue-text' : available ? 'green-text' : 'red-text';

		return (
			<div
				className={`availability-hover small-text ${status_color}`}
				onMouseEnter={(e) => handleLoadAvailability(e, data_type, data_id)}
				onMouseLeave={(e) => handleLoadAvailability(e, data_type, null)}
			>
				{status_text}
			</div>
		);
	};

	let main_section_all_labels = (mainSectionLoaded && sectionsData.main.labels) ? sectionsData.main.labels : [];												
	main_section_all_labels = main_section_all_labels.map(label_id => {
		const label_info = labels.find(label => label.label_id === label_id);
		return label_info;
	});
	
	const main_section_teams = [];
	const sorted_main_section_teams = sortArrayOfObjects(main_section_teams, 'label', 'text', 'asc');
	
	const main_section_labels = main_section_all_labels.filter(label => label);
	const sorted_main_section_labels = sortArrayOfObjects(main_section_labels, 'label', 'text', 'asc');

	const total_student_count = Object.keys(sectionsData).reduce(function (total_students, key) {
		const section_student_count = sectionsData[key].student_list.length;
		return total_students + section_student_count;
	}, 0);

	const main_students_selected = (sectionsSelectedStudents.main && sectionsSelectedStudents.main.filter(student_id => sectionsData.main.student_list.includes(student_id)).length > 0);
	const main_section_dropdown_key = getSelectAllDropdownKey('main');
	const main_search_dropdown_key = getSelectAllDropdownKey('main', 'student-search');
	const main_dropdown_visible_students = getVisibleStudentsInAddDropdown('main');
	const main_dropdown_selected_students = (sectionsSelectedStudents.main || []).filter(student_id => main_dropdown_visible_students.includes(student_id));
	const main_dropdown_students_selected = main_dropdown_selected_students.length > 0;
	const main_dropdown_students_to_add = main_dropdown_selected_students.filter(student_id => !sectionsData.main.student_list.includes(student_id));
	const main_dropdown_students_to_remove = main_dropdown_selected_students.filter(student_id => sectionsData.main.student_list.includes(student_id));
	const main_dropdown_locked_students = main_dropdown_students_to_remove.filter(student_id => sectionsData.main.locked_students.includes(student_id));

	const main_section_actually_locked = (mainSectionLoaded && isSectionLocked('main'));
	const main_section_locked = (mainSectionLoaded && (main_section_actually_locked || studentOnlyEditing));
	const main_student_roster_locked = mainSectionLoaded && sectionIsLockedForStudentEditing('main');
	const all_sections_locked = mainSectionLoaded && Object.keys(sectionsData).length > 0 && Object.keys(sectionsData).every(section_id => isSectionLocked(section_id));
	const main_section_is_lab = (mainSectionLoaded && sectionsData.main.is_lab === '1');
	const section_type_display = main_section_is_lab ? 'Lab' : sectionType === '2' ? 'Prep' : sectionType === '3' ? 'Duty' : 'Section';
	const section_theme_bg_class = main_section_is_lab ? 'green' : sectionType === '2' ? 'pastel-light-blue' : sectionType === '3' ? 'pastel-brown' : 'dark-blue';
	const main_section_number_validation_message = getSectionNumberValidationMessage('main');
	const show_main_section_resync_link = mainSectionLoaded && !main_section_locked && shouldShowResyncSectionNumber('main');

	//console.log({periodOptions});
	//console.log(changesMade);
	//console.log({dataID, classrooms, courses, teachers, students});
	//console.log(searchResults);
	
	return (
		<div className='modal-screen'>
			<div className='modal-screen-content'>
				{!isSaving &&
					<>
					<span className="data-modal-close" onClick={handleExitWithoutSaving}>&times;</span>
					{sectionID && canDeleteSection &&
						<div className="modal-delete-btn" onClick={handleDeleteSection}><FontAwesomeIcon icon={faTrashAlt}/></div>
					}
					{canLockSections && <div className={all_sections_locked ? 'modal-locked-btn' : 'modal-unlocked-btn'} onClick={() => toggleLockSection()}>{all_sections_locked ? <FontAwesomeIcon icon={faLock}/> : <FontAwesomeIcon icon={faUnlock}/>}</div>}

					<div className="modal-save-btn" onClick={() => saveSection(null)}><FontAwesomeIcon icon={faSave}/>Save {studentOnlyEditing ? 'Students' : section_type_display}</div>
					</>
				}
				<div className='full-width'>
					<div id='edit-section-data-content'>
						<FontAwesomeIcon className='edit-data-main-head-icon dark-blue-text' icon={faEdit}/>
						<h1 className='edit-data-main-header dark-blue-text'>{studentOnlyEditing ? 'Edit Section Students' : `${sectionID ? 'Edit' : 'Add New'} ${section_type_display}`}</h1>
						{isSaving &&
							<div className='dashboard-database-message-container'>
								<img src={loading_gif} alt='loading...' style={{height:'80px'}}/>
								<div>Saving...</div>
							</div>
						}
						{isLoading &&
							<div className='dashboard-database-message-container'>
								<img src={loading_gif} alt='loading...' style={{height:'80px'}}/>
							</div>
						}
						{(!isLoading && !isSaving) &&
							<form className={`edit-data-form ${sectionType === '1' ? 'edit-section-form' : ''}`} style={{gridColumnGap:'50px'}}>
								{(availabilityViewID) &&
									<Availability availabilityDataType={availabilityDataType} availabilityViewID={availabilityViewID} availabilityDataPosition={availabilityDataPosition} scheduleType={scheduleType} currentDay={currentDay} teachers={teachers} classrooms={classrooms} students={students} courses={courses} sections={sections} scheduleStructureData={scheduleStructureData} />
								}
								<div className='edit-section-sections-container'>
									<div className='edit-section-main-section-container'>
										<div className={`data-modal-heading edit-section-heading ${section_theme_bg_class} ${main_section_actually_locked ? 'locked' : ''}`}>
											<div>{sectionType === '2' ? 'Prep' : sectionType === '3' ? 'Duty' : main_section_is_lab ? 'Lab' : 'Main Section'}</div>
											{canEditSectionStructure && <div className='edit-section-heading-btn-container'>
												<span title={main_section_locked ? 'Unlock main section' : 'Lock main section'}>
													<FontAwesomeIcon className='edit-section-heading-icon' icon={main_section_locked ? faLock : faUnlock} onClick={() => toggleSingleSectionLock('main')}/>
												</span>
											</div>}
										</div>
										<div className='edit-section-main-section-content'>
												{sectionType === '1' &&
													<>
												<div className='edit-section-label'>Course</div>
												<div className='edit-section-data-row-container'>
													{!editingTracker.main.course ?
														(
															<div className={`edit-section-data-row ${main_section_locked ? 'disabled' : 'edit-section-editable-row'}`} onClick={() => !main_section_locked && toggleEditing('main', 'course')}>
																<div>{sectionsData.main.course_id ? main_course_name : '--'}</div>
																{!main_section_locked && <div className='blue-link medium-text edit-section-hover-action'>{sectionsData.main.course_id ? 'Change' : 'Add'}...</div>}
															</div>
														) :
														(
															<>
															<div className='search-add-search-container'>
																<div className='search-add-adding-input-container'>
																	<input id='main-course-input' className='edit-section-input' placeholder='Search for a course' defaultValue={main_course_name} onChange={(e) => fuzzySearch(e, 'main', 'courses')} autoFocus={true} onFocus={handleFocus}/>
																	<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
																</div>
																<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes}  onClick={() => toggleEditing('main', 'course')}/>
															</div>
															{searchResults.main.courses.suggested.length !== 0 &&
																<>
																<div className='search-add-did-you-mean'>Courses this Teacher Teaches:</div>
																<div className='search-add-search-results'>
																	{searchResults.main.courses.suggested.map((course, i) => {
																		if(!course) return null;
																		return (
																			<div className='search-add-search-result' key={i}>
																				<div>{course.name} ({course.course_code})</div>
																				<div>
																					<div className='search-add-add-btn' onClick={() => updateData('change', 'main', 'course_id', course.course_id)}>Select</div>
																				</div>
																			</div>
																		)
																	})}
																</div>
																</>
															}
															{searchResults.main.courses.search.length !== 0 &&
																<>
																<div className='search-add-did-you-mean'>Other Courses:</div>
																<div className='search-add-search-results'>
																	{searchResults.main.courses.search.map((course, i) => {
																		return (
																			<div className='search-add-search-result' key={i}>
																				<div>{course.name} ({course.course_code})</div>
																				<div>
																					<div className='search-add-add-btn' onClick={() => updateData('change', 'main', 'course_id', course.course_id)}>Select</div>
																				</div>
																			</div>
																		)
																	})}
																</div>
																</>
															}
															</>
														)											
													}
												</div>
												<div className='edit-section-label'>Section ID</div>
												<div className='edit-section-data-row-container'>
													{!editingTracker.main.section_number ?
														<div className={`edit-section-data-row ${main_section_locked || !sectionsData.main.course_id ? 'disabled' : 'edit-section-editable-row'}`} onClick={() => !main_section_locked && sectionsData.main.course_id && startSectionNumberEditing('main')}>
															<div>{sectionsData.main.course_id ? (sectionsData.main.section_number || '--') : '--'}</div>
															{!main_section_locked && sectionsData.main.course_id && <div className='edit-section-inline-edit-hint'>Edit...</div>}
														</div>
														:
														<div className={`edit-section-data-row edit-section-inline-edit-row ${main_section_locked ? 'disabled' : ''}`}>
															<input
																className='edit-section-inline-row-input'
																value={sectionNumberDrafts.main ?? ''}
																onChange={(e) => updateSectionNumberDraft('main', e.target.value)}
																onKeyDown={(e) => handleSectionNumberKeyDown(e, 'main')}
																disabled={main_section_locked}
																maxLength={4}
																autoFocus={true}
															/>
															<div className='edit-section-inline-edit-actions'>
																<FontAwesomeIcon className='checkmark-submit' icon={faCheck} onClick={() => acceptSectionNumberEditing('main')}/>
																<FontAwesomeIcon className='x-cancel' icon={faTimes} onClick={() => cancelSectionNumberEditing('main')}/>
															</div>
														</div>
													}
													{main_section_number_validation_message &&
														<div className='small-text red-text'>{main_section_number_validation_message}</div>
													}
													{show_main_section_resync_link &&
														<div className='blue-link small-text edit-section-resync-link' onClick={() => resyncSectionNumber('main')}>Resync section ID</div>
													}
												</div>
												<div className='edit-section-label'>Teacher</div>
												<div className='edit-section-data-row-container'>
													{!editingTracker.main.teacher ?
														(
															<div className={`edit-section-data-row ${main_section_locked ? 'disabled' : 'edit-section-editable-row'}`} onClick={() => !main_section_locked && toggleEditing('main', 'teacher')}>
																<div>{sectionsData.main.teacher_id ? 
																	<span className='edit-section-current-data'>{main_teacher_name} 
																		{!main_section_locked && <span><FontAwesomeIcon icon={faTimes} className='medium-text gray-to-red-link' onClick={(e) => {e.stopPropagation(); updateData('change', 'main', 'teacher_id', null)}}/>
																		</span>}
																	</span> : '--'}</div>
																{!main_section_locked && <div className='blue-link medium-text edit-section-hover-action'>{sectionsData.main.teacher_id ? 'Change' : 'Add'}...</div>}
															</div>
														) :
														(
															<>
															<div className='search-add-search-container'>
																<div className='search-add-adding-input-container'>
																	<input id='main-teacher-input' className='edit-section-input' placeholder='Search for a teacher' defaultValue={main_teacher_name} onChange={(e) => fuzzySearch(e, 'main', 'teachers')} autoFocus={true} onFocus={handleFocus}/>
																	<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
																</div>
																<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes}  onClick={() => toggleEditing('main', 'teacher')}/>
															</div>
															{searchResults.main.teachers.suggested.length !== 0 &&
																<>
																<div className='search-add-did-you-mean'>Teachers Who Can Teach:</div>
																<div className='search-add-search-results'>
																	{searchResults.main.teachers.suggested.map((teacher, i) => {
																		if(!teacher) return null;

																		const teacher_availability_index = teachersAvailability.findIndex(teacher_availability => teacher_availability.teacher_id === teacher.teacher_id);
																		const teacher_available = (teacher_availability_index !== -1) ? teachersAvailability[teacher_availability_index].availability : false;
																		const is_current_primary_teacher = sectionsData.main.teacher_id === teacher.teacher_id;
																		const is_secondary_teacher = (sectionsData.main.secondary_teachers || []).includes(teacher.teacher_id);
																		const is_current_teacher = is_current_primary_teacher || is_secondary_teacher;

																		return (
																			<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 80px 85px'}}>
																				<div>{teacher.name}, {teacher.first_name}</div>
																				{is_current_teacher ? (
																					<div className='small-text gray-text'>Selected</div>
																				) : (
																	renderAvailabilityStatus(teacher_available, 'teacher', teacher.teacher_id)
																				)}
																				<div>
																					{is_current_primary_teacher ? (
																						<div className='search-add-add-btn red' onClick={() => updateData('change', 'main', 'teacher_id', null)}>Remove</div>
																					) : is_current_teacher ? (
																						<div className='small-text gray-text'>.</div>
																					) : (
																						<div className='search-add-add-btn' onClick={() => updateData('change', 'main', 'teacher_id', teacher.teacher_id)}>Select</div>
																					)}
																				</div>
																			</div>
																		)
																	})}
																</div>
																</>
															}
															{searchResults.main.teachers.search.length !== 0 &&
																<>
																<div className='search-add-did-you-mean'>Other Teachers:</div>
																<div className='search-add-search-results'>
																	{searchResults.main.teachers.search.map((teacher, i) => {
																		const teacher_availability_index = teachersAvailability.findIndex(teacher_availability => teacher_availability.teacher_id === teacher.teacher_id);
																		const teacher_available = (teacher_availability_index !== -1) ? teachersAvailability[teacher_availability_index].availability : false;
																		const is_current_primary_teacher = sectionsData.main.teacher_id === teacher.teacher_id;
																		const is_secondary_teacher = (sectionsData.main.secondary_teachers || []).includes(teacher.teacher_id);
																		const is_current_teacher = is_current_primary_teacher || is_secondary_teacher;
																		
																		return (
																			<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 80px 85px'}}>
																				<div>{teacher.name}, {teacher.first_name}</div>
																				{is_current_teacher ? (
																					<div className='small-text gray-text'>Selected</div>
																				) : (
																	renderAvailabilityStatus(teacher_available, 'teacher', teacher.teacher_id)
																				)}
																				<div>
																					{is_current_primary_teacher ? (
																						<div className='search-add-add-btn red' onClick={() => updateData('change', 'main', 'teacher_id', null)}>Remove</div>
																					) : is_current_teacher ? (
																						<div className='small-text gray-text'>.</div>
																					) : (
																						<div className='search-add-add-btn' onClick={() => updateData('change', 'main', 'teacher_id', teacher.teacher_id)}>Select</div>
																					)}
																				</div>
																			</div>
																		)
																	})}
																</div>
																</>
															}
															</>
														)											
													}
												</div>
												{sectionsData.main.secondary_teachers.map((secondary_teacher_id, i) => {
													return (
															<EditSectionSecondaryTeacher teachers={teachers} courses={courses} sectionID='main' courseID={sectionsData.main.course_id} secondaryTeacherID={secondary_teacher_id} primaryTeacherID={sectionsData.main.teacher_id} teachersAvailability={teachersAvailability} handleLoadAvailability={handleLoadAvailability} changeSecondaryTeacher={changeSecondaryTeacher} removeSecondaryTeacher={removeSecondaryTeacher} disabled={main_section_locked} key={i}/>
													)
												})}
												{sectionsData.main.teacher_id && !main_section_locked &&
													<div className='blue-link align-right small-text edit-section-secondary-teacher-blue-link' onClick={() => addSecondaryTeacher({section_id:'main', secondary_teacher_id:''})}>Add Another Teacher</div>
												}
												<div className='edit-section-label'>Classroom</div>
												<div className='edit-section-data-row-container'>
													{!editingTracker.main.classroom ?
														(
															<div className={`edit-section-data-row ${main_section_locked ? 'disabled' : 'edit-section-editable-row'}`} onClick={() => !main_section_locked && toggleEditing('main', 'classroom')}>
																<div>{sectionsData.main.classroom_id ? 
																	<span className={`edit-section-current-data ${main_section_locked ? 'disabled' : ''}`}>{main_classroom_name} 
																		{!main_section_locked && <span><FontAwesomeIcon icon={faTimes} className='medium-text gray-to-red-link' onClick={(e) => {e.stopPropagation(); updateData('change', 'main', 'classroom_id', null)}}/>
																		</span>}
																	</span> : '--'}
																</div>
																{!main_section_locked && <div className='blue-link medium-text edit-section-hover-action'>{sectionsData.main.classroom_id ? 'Change' : 'Add'}...</div>}
															</div>
														) :
														(
															<>
															<div className='search-add-search-container'>
																<div className='search-add-adding-input-container'>
																	<input id='main-classroom-input' className='edit-section-input' placeholder='Search for a classroom' defaultValue={main_classroom_name} onChange={(e) => fuzzySearch(e, 'main', 'classrooms')} autoFocus={true} onFocus={handleFocus}/>
																	<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
																</div>
																<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes}  onClick={() => toggleEditing('main', 'classroom')}/>
															</div>
															{searchResults.main.classrooms.suggested.course_classroom_restrictions.length !== 0 &&
																<>
																<div className='search-add-did-you-mean'>Classroom(s) for this Course:</div>
																<div className='search-add-search-results'>
																	{searchResults.main.classrooms.suggested.course_classroom_restrictions.map((classroom, i) => {
																		const classroom_availability_index = classroomsAvailability.findIndex(classroom_availability => classroom_availability.classroom_id === classroom.classroom_id);
																		const classroom_available = (classroom_availability_index !== -1) ? classroomsAvailability[classroom_availability_index].availability : false;

																		return (
																			<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 80px auto'}}>
																				<div>{classroom.classroom_name}</div>
															{renderAvailabilityStatus(classroom_available, 'classroom', classroom.classroom_id)}
																				<div>
																					<div className='search-add-add-btn' onClick={() => updateData('change', 'main', 'classroom_id', classroom.classroom_id)}>Select</div>
																				</div>
																			</div>
																		)
																	})}
																</div>
																</>
															}
															{searchResults.main.classrooms.suggested.teacher_classroom_restrictions.length !== 0 &&
																<>
																<div className='search-add-did-you-mean'>Teacher's Preferred Classroom(s):</div>
																<div className='search-add-search-results'>
																	{searchResults.main.classrooms.suggested.teacher_classroom_restrictions.map((classroom, i) => {
																		const classroom_availability_index = classroomsAvailability.findIndex(classroom_availability => classroom_availability.classroom_id === classroom.classroom_id);
																		const classroom_available = (classroom_availability_index !== -1) ? classroomsAvailability[classroom_availability_index].availability : false;
																		
																		return (
																			<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 80px auto'}}>
																				<div>{classroom.classroom_name}</div>
															{renderAvailabilityStatus(classroom_available, 'classroom', classroom.classroom_id)}
																				<div>
																					<div className='search-add-add-btn' onClick={() => updateData('change', 'main', 'classroom_id', classroom.classroom_id)}>Select</div>
																				</div>
																			</div>
																		)
																	})}
																</div>
																</>
															}
															{searchResults.main.classrooms.search.length !== 0 &&
																<>
																<div className='search-add-did-you-mean'>Search Results:</div>
																<div className='search-add-search-results'>
																	{searchResults.main.classrooms.search.map((classroom, i) => {
																		const classroom_availability_index = classroomsAvailability.findIndex(classroom_availability => classroom_availability.classroom_id === classroom.classroom_id);
																		const classroom_available = (classroom_availability_index !== -1) ? classroomsAvailability[classroom_availability_index].availability : false;
																		
																		return (
																			<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 80px auto'}}>
																				<div>{classroom.classroom_name}</div>
															{renderAvailabilityStatus(classroom_available, 'classroom', classroom.classroom_id)}
																				<div>
																					<div className='search-add-add-btn' onClick={() => updateData('change', 'main', 'classroom_id', classroom.classroom_id)}>Select</div>
																				</div>
																			</div>
																		)
																	})}
																</div>
																</>
															}
															</>
														)											
													}
												</div>
												</>
											}
											{canEditPeriods &&
												<>
												<div className='edit-section-label'>Period(s)</div>
												{sectionsData['main'].course_periods.map(course_period_id => {
													const day_ss = (currentDay) ? scheduleStructureData.schedule_structure[currentDay] : [];
													const day_ss_course_period_index = day_ss.findIndex(day_ss_row => day_ss_row.course_period_id === course_period_id);
													const day_ss_schedule_period_id = (day_ss_course_period_index !== -1) ? day_ss[day_ss_course_period_index].schedule_period_id : null;
													const period_id = (scheduleType === 'daily') ? day_ss_schedule_period_id : course_period_id;

													if(!period_id) return null;

													const period_name = (scheduleType === 'daily') ? scheduleStructureData.schedule_periods[period_id].period_name : scheduleStructureData.course_periods[period_id].period_name;

													return (
														<div className={`edit-section-data-row ${main_section_locked ? 'disabled' : ''}`} key={course_period_id} style={{marginBottom:'5px'}}>
															<div className={`${main_section_locked ? 'disabled' : ''}`}>{period_name}</div>
															{!main_section_locked && <FontAwesomeIcon className='x-cancel' icon={faTimes}  onClick={() => removeCoursePeriod(course_period_id)}/>}
														</div>
													)
												})}
												{!main_section_locked && <div className='blue-link align-right small-text' onClick={togglePeriodOptions}>Add Period</div>}
												{editingTracker.main.period_options_open &&
													<div>
														{periodOptions.map((period_option, i) => {
															const course_period_id = period_option.value;
															const period_name = period_option.display;

															return (
																<div className='search-add-search-result' key={i}>
																	<div>{period_name}</div>
																	<div className='search-add-add-btn' onClick={() => addCoursePeriod(course_period_id)}>Select</div>
																</div>
															)
														})}
													</div>
												}
												</>
											}
											<div className='edit-section-label'>Quarters</div>
											{quarters.current.map(quarter => {
												const quarter_index = main_quarter_days.findIndex(quarter_day => quarter_day.quarter === quarter);
												const quarter_selected = (quarter_index !== -1) ? true : false;
												return (
													<div className={`option-btn option-btn-no-hover small-text ${main_section_locked ? 'disabled' : ''}`} key={quarter} onClick={() => !main_section_locked && updateQuarterDays('main', 'quarter', quarter)}>
														<div>
															<FontAwesomeIcon className={`quarter_day_icon ${quarter_selected ? 'fas-checkbox-checked' : 'fas-checkbox-unchecked'} ${(quarter_selected && sectionType === '2') ? 'pastel-light-blue-text' : (quarter_selected && sectionType === '3') ? 'pastel-brown-text' : ''} ${main_section_locked ? 'disabled' : ''}`} icon={quarter_selected ? faCheckSquare : faSquare}/>
														</div>
														<div>Q{quarter}</div>
													</div>
												)
											})}
											<div className='edit-section-label'>Days</div>
											{numDaysInCycle.map(day => {
												const day_index = main_quarter_days.findIndex(quarter_day => quarter_day.day === day);
												const day_selected = (day_index !== -1) ? true : false;
												return (
													<div className={`option-btn option-btn-no-hover small-text ${main_section_locked ? 'disabled' : ''}`} key={day} onClick={() => !main_section_locked && updateQuarterDays('main', 'day', day)}>
														<div>
															<FontAwesomeIcon className={`quarter_day_icon ${day_selected ? 'fas-checkbox-checked' : 'fas-checkbox-unchecked'} ${(day_selected && sectionType === '2') ? 'pastel-light-blue-text' : (day_selected && sectionType === '3') ? 'pastel-brown-text' : ''} ${main_section_locked ? 'disabled' : ''}`} icon={day_selected ? faCheckSquare : faSquare}/>
														</div>
														<div>Day {day}</div>
													</div>
												)
											})}
											{sectionType === '1' && canEditLabels &&
												<>
												<div className='edit-section-labels-container'>
													<>
													{sorted_main_section_teams.map((label, index) => (
														<div key={label.label_id}>
															{label.label ?
																(
																	<div className='edit-section-section-label'>
																		<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																		<div>{label.label}</div>
																		{!main_section_locked && <FontAwesomeIcon className='cancel-link' icon={faTimes} onClick={() => removeLabel('main', label.label_id)}/>}
																	</div>
																) :
																(
																	<div className='create-labels-editing-label'>
																		<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																		<input className='create-labels-input' placeholder='Add label name...' onKeyPress={(e) => {if(e.key === 'Enter') updateLabel(e, 'main', label.label_id)}} autoFocus={true}/>
																		{!main_section_locked && <FontAwesomeIcon className='gray-to-red-link' icon={faTimes} onClick={() => removeLabel('main', label.label_id)}/>}
																		{!main_section_locked && <FontAwesomeIcon className='gray-to-blue-link' icon={faCheck} onClick={(e) => updateLabel(e, 'main', label.label_id)}/>}
																	</div>
																)
															}
														</div>
													))}
													{sorted_main_section_labels.map((label, index) => (
														<div key={label.label_id}>
															{label.label ?
																(
																	<div className='edit-section-section-label'>
																		<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																		<div>{label.label}</div>
																		{!main_section_locked && <FontAwesomeIcon className='cancel-link' icon={faTimes} onClick={() => removeLabel('main', label.label_id)}/>}
																	</div>
																) :
																(
																	<div className='create-labels-editing-label'>
																		<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																		<input className='create-labels-input' placeholder='Add label name...' onKeyPress={(e) => {if(e.key === 'Enter') updateLabel(e, 'main', label.label_id)}} autoFocus={true}/>
																		{!main_section_locked && <FontAwesomeIcon className='gray-to-red-link' icon={faTimes} onClick={() => removeLabel('main', label.label_id)}/>}
																		{!main_section_locked && <FontAwesomeIcon className='gray-to-blue-link' icon={faCheck} onClick={(e) => updateLabel(e, 'main', label.label_id)}/>}
																	</div>
																)
															}
														</div>
													))}
													</>
												</div>
												{!main_section_locked && <div className='add-label-to-section blue-link small-text click-restricted' onClick={() => toggleLabelOptionsOpen('main')}>Add Team/Label</div>}
												<div className='create-labels-label-options-container' data-sectionid='main'>
													{editingTracker.main.label_options_open &&
														<div className='data-options-container' ref={ref}>
															<div className='data-options-heading'>Teams/Labels</div>
															{sorted_labels.length === 0 &&
																<div className='create-labels-no-label-options'>No teams/labels added yet...</div>
															}
															{sorted_labels.map((label, index) => {
																const label_selected = sectionsData.main.labels.includes(label.label_id);
																return (
																	<div className='data-options-option create-labels-label-option' key={index} onClick={label_selected ? undefined : () => updateData('add', 'main', 'labels', label.label_id)}>
																		<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																		<div>{label.label}</div>
																		{label_selected && <div className='search-add-already-selected'>Selected</div>}
																	</div>
																);
															})}
															<div className='blue-link small-text edit-section-add-new-label-link' onClick={() => addTempLabel('main')}>Add New Team/Label</div>
														</div>
													}
												</div>
												</>
											}
										</div>
									</div>
									{sectionType === '1' &&
										<>
											{sectionsData['main'].subsections.map((subsection_id, subsection_index) => {
												const subsection_info = sectionsData[subsection_id];
												if(!subsection_info) return null;
												const subsection_editing_tracker = editingTracker[subsection_id] || {course: false, teacher: false, students: false, section_number:false, label_options_open:false, advanced_settings_open:false};
												const is_new = ('new' in subsection_info) ? true : false;
											const subsection_course_id = subsection_info.course_id;
											const subsection_teacher_id = subsection_info.teacher_id;
											const subsection_classroom_id = subsection_info.classroom_id;
											
											const subsection_course_info = (subsection_course_id) ? courses.find(course => course.course_id === subsection_course_id) : null;
											const subsection_teacher_info = (subsection_teacher_id) ? teachers.find(teacher => teacher.teacher_id === subsection_teacher_id) : null;
											const subsection_classroom_info = (subsection_classroom_id) ? classrooms.find(classroom => classroom.classroom_id === subsection_classroom_id) : null;
											
											const subsection_course_name = (subsection_course_info) ? `${subsection_course_info.name} (${subsection_course_info.course_code})` : '';
											const subsection_teacher_name = (subsection_teacher_info) ? `${subsection_teacher_info.name}, ${subsection_teacher_info.first_name}` : '';
											const subsection_classroom_name = (subsection_classroom_info) ? `${subsection_classroom_info.classroom_name}` : '';
											const subsection_secondary_teachers = subsection_info.secondary_teachers;
											const subsection_is_inclusion = (subsection_info.is_inclusion === '1');
											const subsection_section_actually_locked = isSectionLocked(subsection_id);
											const subsection_section_locked = subsection_section_actually_locked || studentOnlyEditing;
											const subsection_has_separate_quarter_days = subsection_info.has_separate_quarter_days === '1';
											const subsection_has_separate_classroom = subsection_info.has_separate_classroom === '1';
											const subsection_quarter_days = subsection_info.quarter_days || [];
											const subsection_matches_main_course = isSameCourseSubsectionForSnapshot(subsection_id, subsection_course_id, sectionsData);

											let is_placeholder_inclusion = false;
											if(scheduleInfo.separate_ICR_codes === '0' && sectionsData.main.course_id === subsection_course_id && subsection_is_inclusion) is_placeholder_inclusion = true;
											const subsection_section_number_validation_message = getSectionNumberValidationMessage(subsection_id);
											
											let subsection_all_labels = subsection_info.labels;		
											subsection_all_labels = subsection_all_labels.map(label_id => {
												const label_info = labels.find(label => label.label_id === label_id);
												return label_info;
											});
											
											const subsection_teams = [];
											const sorted_subsection_teams = sortArrayOfObjects(subsection_teams, 'label', 'text', 'asc');
											
											const subsection_labels = subsection_all_labels.filter(label => label);
											const sorted_subsection_labels = sortArrayOfObjects(subsection_labels, 'label', 'text', 'asc');
											
											return (
												<div className='edit-section-subsection-container' key={subsection_id}>
													<div className={`data-modal-heading edit-section-heading ${subsection_is_inclusion ? 'yellow' : 'blue'} ${subsection_section_actually_locked ? 'locked' : ''}`}>
														<div>{subsection_is_inclusion && 'Inclusion'} Subsection</div>
														<div className='edit-section-heading-btn-container'>
															{canEditSectionStructure && <>
															{(!is_placeholder_inclusion && !is_new && !subsection_section_locked) &&
																<span title='Split off subsection'><FontAwesomeIcon className='edit-section-heading-icon edit-section-heading-icon-split' icon={faObjectUngroup} onClick={() => splitOffSubsection(subsection_id, subsection_index)}/></span>
															}
															</>}
															{canLockSections &&
															<span title={subsection_section_locked ? 'Unlock subsection' : 'Lock subsection'}>
																<FontAwesomeIcon className='edit-section-heading-icon' icon={subsection_section_locked ? faLock : faUnlock} onClick={() => toggleSingleSectionLock(subsection_id)}/>
															</span>
															}
															{canEditSectionStructure && <>
															{!subsection_section_locked && <span title='Remove subsection'><FontAwesomeIcon className='edit-section-heading-icon' icon={faTrashAlt} onClick={() => removeSubsection(subsection_id, subsection_index)}/></span>}
															</>}
														</div>
													</div>
													<div className='edit-section-main-section-content'>
													{!is_placeholder_inclusion &&
															<>
															<div className='edit-section-label'>Course</div>
															<div className='edit-section-data-row-container'>
																{!subsection_editing_tracker.course ?
																	(
																		<div className={`edit-section-data-row ${subsection_section_locked ? 'disabled' : 'edit-section-editable-row'}`} onClick={() => !subsection_section_locked && toggleEditing(subsection_id, 'course')}>
																			<div>{sectionsData[subsection_id].course_id ? subsection_course_name : '--'}</div>
																			{!subsection_section_locked && <div className='blue-link medium-text edit-section-hover-action'>{subsection_course_id ? 'Change' : 'Add'}...</div>}
																		</div>
																	) :
																	(
																		<>
																		<div className='search-add-search-container'>
																			<div className='search-add-adding-input-container'>
																			<input id={`${subsection_id}-course-input`} className='edit-section-input' placeholder='Search for a course' defaultValue={subsection_course_name} onChange={(e) => fuzzySearch(e, subsection_id, 'courses')} autoFocus={true} onFocus={handleFocus}/>
																				<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
																			</div>
																			<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes}  onClick={() => toggleEditing(subsection_id, 'course')}/>
																		</div>
																		{searchResults[subsection_id].courses.suggested.length !== 0 &&
																			<>
																			<div className='search-add-did-you-mean'>Courses this Teacher Teaches:</div>
																			<div className='search-add-search-results'>
																				{searchResults[subsection_id].courses.suggested.map((course, i) => {
																					return (
																						<div className='search-add-search-result' key={i}>
																							<div>{course.name} ({course.course_code})</div>
																							<div>
																								<div className='search-add-add-btn' onClick={() => updateData('change', subsection_id, 'course_id', course.course_id)}>Select</div>
																							</div>
																						</div>
																					)
																				})}
																			</div>
																			</>
																		}
																		{searchResults[subsection_id].courses.search.length !== 0 &&
																			<>
																			<div className='search-add-did-you-mean'>Other Courses:</div>
																			<div className='search-add-search-results'>
																				{searchResults[subsection_id].courses.search.map((course, i) => {
																					return (
																						<div className='search-add-search-result' key={i}>
																							<div>{course.name} ({course.course_code})</div>
																							<div>
																								<div className='search-add-add-btn' onClick={() => updateData('change', subsection_id, 'course_id', course.course_id)}>Select</div>
																							</div>
																						</div>
																					)
																				})}
																			</div>
																			</>
																		}
																		</>
																	)											
																}
															</div>
														</>
													}
													{!is_placeholder_inclusion && !subsection_matches_main_course &&
														<>
														<div className='edit-section-label'>Section ID</div>
														<div className='edit-section-data-row-container'>
															{!subsection_editing_tracker.section_number ?
																<div className={`edit-section-data-row ${subsection_section_locked || !sectionsData[subsection_id].course_id ? 'disabled' : 'edit-section-editable-row'}`} onClick={() => !subsection_section_locked && sectionsData[subsection_id].course_id && startSectionNumberEditing(subsection_id)}>
																	<div>{sectionsData[subsection_id].course_id ? (sectionsData[subsection_id].section_number || '--') : '--'}</div>
																	{!subsection_section_locked && sectionsData[subsection_id].course_id && <div className='edit-section-inline-edit-hint'>Edit...</div>}
																</div>
																:
																<div className={`edit-section-data-row edit-section-inline-edit-row ${subsection_section_locked ? 'disabled' : ''}`}>
																	<input
																		className='edit-section-inline-row-input'
																		value={sectionNumberDrafts[subsection_id] ?? ''}
																		onChange={(e) => updateSectionNumberDraft(subsection_id, e.target.value)}
																		onKeyDown={(e) => handleSectionNumberKeyDown(e, subsection_id)}
																		disabled={subsection_section_locked}
																		maxLength={4}
																		autoFocus={true}
																	/>
																	<div className='edit-section-inline-edit-actions'>
																		<FontAwesomeIcon className='checkmark-submit' icon={faCheck} onClick={() => acceptSectionNumberEditing(subsection_id)}/>
																		<FontAwesomeIcon className='x-cancel' icon={faTimes} onClick={() => cancelSectionNumberEditing(subsection_id)}/>
																	</div>
																</div>
															}
															{subsection_section_number_validation_message &&
																<div className='small-text red-text'>{subsection_section_number_validation_message}</div>
															}
															{!subsection_section_locked && shouldShowResyncSectionNumber(subsection_id) &&
																<div className='blue-link small-text edit-section-resync-link' onClick={() => resyncSectionNumber(subsection_id)}>Resync section ID</div>
															}
														</div>
														</>
													}
													<div className='edit-section-label'>{is_placeholder_inclusion ? 'Inclusion' : ''} Teacher</div>
														<div className='edit-section-data-row-container'>
															{!subsection_editing_tracker.teacher ?
																(
																	<div className={`edit-section-data-row ${subsection_section_locked ? 'disabled' : 'edit-section-editable-row'}`} onClick={() => !subsection_section_locked && toggleEditing(subsection_id, 'teacher')}>
																		<div>{sectionsData[subsection_id].teacher_id ? subsection_teacher_name : '--'}</div>
																		{!subsection_section_locked && <div className='blue-link medium-text edit-section-hover-action'>{subsection_teacher_id ? 'Change' : 'Add'}...</div>}
																	</div>
																) :
																(
																	<>
																	<div className='search-add-search-container'>
																		<div className='search-add-adding-input-container'>
																			<input id={`${subsection_id}-teacher-input`} className='edit-section-input' placeholder='Search for a teacher' defaultValue={subsection_teacher_name} onChange={(e) => fuzzySearch(e, subsection_id, 'teachers')} autoFocus={true} onFocus={handleFocus}/>
																			<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
																		</div>
																		<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes}  onClick={() => toggleEditing(subsection_id, 'teacher')}/>
																	</div>
																	{searchResults[subsection_id].teachers.suggested.length !== 0 &&
																		<>
																		<div className='search-add-did-you-mean'>Teachers Who Can Teach:</div>
																		<div className='search-add-search-results'>
																			{searchResults[subsection_id].teachers.suggested.map((teacher, i) => {
																				if(!teacher) return null;

																				const teacher_availability_index = teachersAvailability.findIndex(teacher_availability => teacher_availability.teacher_id === teacher.teacher_id);
																				const teacher_available = (teacher_availability_index !== -1) ? teachersAvailability[teacher_availability_index].availability : false;
																				const is_current_primary_teacher = sectionsData[subsection_id].teacher_id === teacher.teacher_id;
																				const is_secondary_teacher = (sectionsData[subsection_id].secondary_teachers || []).includes(teacher.teacher_id);
																				const is_current_teacher = is_current_primary_teacher || is_secondary_teacher;

																				return (
																					<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 80px 85px'}}>
																						<div>{teacher.name}, {teacher.first_name}</div>
																						{is_current_teacher ? (
																							<div className='small-text gray-text'>Selected</div>
																						) : (
																			renderAvailabilityStatus(teacher_available, 'teacher', teacher.teacher_id)
																						)}
																						<div>
																							{is_current_primary_teacher ? (
																								<div className='search-add-add-btn red' onClick={() => updateData('change', subsection_id, 'teacher_id', null)}>Remove</div>
																							) : is_current_teacher ? (
																								<div className='small-text gray-text'>.</div>
																							) : (
																								<div className='search-add-add-btn' onClick={() => updateData('change', subsection_id, 'teacher_id', teacher.teacher_id)}>Select</div>
																							)}
																						</div>
																					</div>
																				)
																			})}
																		</div>
																		</>
																	}
																	{searchResults[subsection_id].teachers.search.length !== 0 &&
																		<>
																		<div className='search-add-did-you-mean'>Other Teachers:</div>
																		<div className='search-add-search-results'>
																			{searchResults[subsection_id].teachers.search.map((teacher, i) => {
																				const teacher_availability_index = teachersAvailability.findIndex(teacher_availability => teacher_availability.teacher_id === teacher.teacher_id);
																				const teacher_available = (teacher_availability_index !== -1) ? teachersAvailability[teacher_availability_index].availability : false;
																				const is_current_primary_teacher = sectionsData[subsection_id].teacher_id === teacher.teacher_id;
																				const is_secondary_teacher = (sectionsData[subsection_id].secondary_teachers || []).includes(teacher.teacher_id);
																				const is_current_teacher = is_current_primary_teacher || is_secondary_teacher;
																				
																				return (
																					<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 80px 85px'}}>
																						<div>{teacher.name}, {teacher.first_name}</div>
																						{is_current_teacher ? (
																							<div className='small-text gray-text'>Selected</div>
																						) : (
																			renderAvailabilityStatus(teacher_available, 'teacher', teacher.teacher_id)
																						)}
																						<div>
																							{is_current_primary_teacher ? (
																								<div className='search-add-add-btn red' onClick={() => updateData('change', subsection_id, 'teacher_id', null)}>Remove</div>
																							) : is_current_teacher ? (
																								<div className='small-text gray-text'>.</div>
																							) : (
																								<div className='search-add-add-btn' onClick={() => updateData('change', subsection_id, 'teacher_id', teacher.teacher_id)}>Select</div>
																							)}
																						</div>
																					</div>
																				)
																			})}
																		</div>
																		</>
																	}
																	</>
																)											
															}
														</div>
														{subsection_secondary_teachers.map((secondary_teacher_id, i) => {
															return (
																<EditSectionSecondaryTeacher teachers={teachers} courses={courses} sectionID={subsection_id} courseID={sectionsData[subsection_id].course_id} secondaryTeacherID={secondary_teacher_id} primaryTeacherID={sectionsData[subsection_id].teacher_id} teachersAvailability={teachersAvailability} handleLoadAvailability={handleLoadAvailability} changeSecondaryTeacher={changeSecondaryTeacher} removeSecondaryTeacher={removeSecondaryTeacher} disabled={subsection_section_locked} key={i}/>
															)
														})}
														{subsection_teacher_id && !subsection_section_locked &&
															<div className='blue-link align-right small-text edit-section-secondary-teacher-blue-link' onClick={() => addSecondaryTeacher({section_id:subsection_id, secondary_teacher_id:''})}>Add Another Teacher</div>
														}
														{canEditLabels && <div className='edit-section-labels-container'>
															<>
															{sorted_subsection_teams.map((label, index) => (
																<div key={label.label_id}>
																	{label.label ?
																		(
																			<div className='edit-section-section-label'>
																				<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																				<div>{label.label}</div>
																				{!subsection_section_locked && <FontAwesomeIcon className='cancel-link' icon={faTimes} onClick={() => removeLabel(subsection_id, label.label_id)}/>}
																			</div>
																		) :
																		(
																			<div className='create-labels-editing-label'>
																				<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																				<input className='create-labels-input' placeholder='Add label name...' onKeyPress={(e) => {if(e.key === 'Enter') updateLabel(e, subsection_id, label.label_id)}} autoFocus={true}/>
																				{!subsection_section_locked && <FontAwesomeIcon className='gray-to-red-link' icon={faTimes} onClick={() => removeLabel(subsection_id, label.label_id)}/>}
																				{!subsection_section_locked && <FontAwesomeIcon className='gray-to-blue-link' icon={faCheck} onClick={(e) => updateLabel(e, subsection_id, label.label_id)}/>}
																			</div>
																		)
																	}
																</div>
															))}
															{sorted_subsection_labels.map((label, index) => (
																<div key={label.label_id}>
																	{label.label ?
																		(
																			<div className='edit-section-section-label'>
																				<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																				<div>{label.label}</div>
																				{!subsection_section_locked && <FontAwesomeIcon className='cancel-link' icon={faTimes} onClick={() => removeLabel(subsection_id, label.label_id)}/>}
																			</div>
																		) :
																		(
																			<div className='create-labels-editing-label'>
																				<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																				<input className='create-labels-input' placeholder='Add label name...' onKeyPress={(e) => {if(e.key === 'Enter') updateLabel(e, label.label_id)}} autoFocus={true}/>
																				{!subsection_section_locked && <FontAwesomeIcon className='gray-to-red-link' icon={faTimes} onClick={() => removeLabel(subsection_id, label.label_id)}/>}
																				{!subsection_section_locked && <FontAwesomeIcon className='gray-to-blue-link' icon={faCheck} onClick={(e) => updateLabel(e, subsection_id, label.label_id)}/>}
																			</div>
																		)
																	}
																</div>
															))}
															</>
														</div>}
														{canEditLabels && !subsection_section_locked && <div className='edit-section-subsection-tag-container'>
															<div className='add-label-to-section blue-link small-text click-restricted' onClick={() => toggleLabelOptionsOpen(subsection_id)}>Add Team/Label</div>
														</div>}
														{canEditLabels && <div className='create-labels-label-options-container' data-sectionid={subsection_id}>
															{subsection_editing_tracker.label_options_open &&
																<div className='data-options-container' ref={ref}>
																	<div className='data-options-heading'>Teams/Labels</div>
																	{sorted_labels.length === 0 &&
																		<div className='create-labels-no-label-options'>No teams/labels added yet...</div>
																	}
																	{sorted_labels.map((label, index) => {
																		const label_selected = sectionsData[subsection_id].labels.includes(label.label_id);
																		return (
																			<div className='data-options-option create-labels-label-option' key={index} onClick={label_selected ? undefined : () => updateData('add', subsection_id, 'labels', label.label_id)}>
																				<div className={`create-labels-icon ${getLabelColorClass(label)}`}></div>
																				<div>{label.label}</div>
																				{label_selected && <div className='search-add-already-selected'>Selected</div>}
																			</div>
																		);
																	})}
																	<div className='blue-link small-text edit-section-add-new-label-link' onClick={() => addTempLabel(subsection_id)}>Add New Team/Label</div>
																</div>
															}
														</div>}
														<div className='edit-section-advanced-settings'>
															<div className='small-text edit-section-advanced-settings-toggle' onClick={() => toggleAdvancedSettingsOpen(subsection_id)}>
																<FontAwesomeIcon icon={subsection_editing_tracker.advanced_settings_open ? faChevronDown : faChevronRight}/>
																<span>Advanced Settings</span>
															</div>
															{subsection_editing_tracker.advanced_settings_open &&
																<div className='edit-section-advanced-settings-content'>
																	<div className='edit-section-advanced-settings-row'>
																		<div>Has separate quarters/days?</div>
																		<div className='edit-section-toggle-with-label'>
																			<span>No</span>
																			<Toggle toggleOn={subsection_has_separate_quarter_days} handleChange={(new_state) => toggleSeparateQuarterDays(subsection_id, new_state)} isSmall={true} disabled={subsection_section_locked}/>
																			<span>Yes</span>
																		</div>
																	</div>
																	{subsection_has_separate_quarter_days &&
																		<div className='edit-section-subsection-quarter-days'>
																			<div className='edit-section-label'>Quarters</div>
																			{quarters.current.map(quarter => {
																				const quarter_selected = subsection_quarter_days.findIndex(quarter_day => quarter_day.quarter === quarter) !== -1;
																				return (
																					<div className={`option-btn option-btn-no-hover small-text ${subsection_section_locked ? 'disabled' : ''}`} key={`${subsection_id}-quarter-${quarter}`} onClick={() => !subsection_section_locked && updateQuarterDays(subsection_id, 'quarter', quarter)}>
																						<div>
																							<FontAwesomeIcon className={`quarter_day_icon ${quarter_selected ? 'fas-checkbox-checked' : 'fas-checkbox-unchecked'} ${subsection_section_locked ? 'disabled' : ''}`} icon={quarter_selected ? faCheckSquare : faSquare}/>
																						</div>
																						<div>Q{quarter}</div>
																					</div>
																				)
																			})}
																			<div className='edit-section-label'>Days</div>
																			{numDaysInCycle.map(day => {
																				const day_selected = subsection_quarter_days.findIndex(quarter_day => quarter_day.day === day) !== -1;
																				return (
																					<div className={`option-btn option-btn-no-hover small-text ${subsection_section_locked ? 'disabled' : ''}`} key={`${subsection_id}-day-${day}`} onClick={() => !subsection_section_locked && updateQuarterDays(subsection_id, 'day', day)}>
																						<div>
																							<FontAwesomeIcon className={`quarter_day_icon ${day_selected ? 'fas-checkbox-checked' : 'fas-checkbox-unchecked'} ${subsection_section_locked ? 'disabled' : ''}`} icon={day_selected ? faCheckSquare : faSquare}/>
																						</div>
																						<div>Day {day}</div>
																					</div>
																				)
																			})}
																		</div>
																	}
																	<div className='edit-section-advanced-settings-row'>
																		<div>Has separate classroom?</div>
																		<div className='edit-section-toggle-with-label'>
																			<span>No</span>
																			<Toggle toggleOn={subsection_has_separate_classroom} handleChange={(new_state) => toggleSeparateClassroom(subsection_id, new_state)} isSmall={true} disabled={subsection_section_locked}/>
																			<span>Yes</span>
																		</div>
																	</div>
																	{subsection_has_separate_classroom &&
																		<div className='edit-section-subsection-classroom'>
																			<div className='edit-section-label'>Classroom</div>
																			<div className='edit-section-data-row-container'>
																				{!subsection_editing_tracker.classroom ?
																					(
																						<div className={`edit-section-data-row ${subsection_section_locked ? 'disabled' : 'edit-section-editable-row'}`} onClick={() => !subsection_section_locked && toggleEditing(subsection_id, 'classroom')}>
																							<div>{subsection_classroom_id ?
																								<span className={`edit-section-current-data ${subsection_section_locked ? 'disabled' : ''}`}>{subsection_classroom_name}
																									{!subsection_section_locked && <span><FontAwesomeIcon icon={faTimes} className='medium-text gray-to-red-link' onClick={(e) => {e.stopPropagation(); updateData('change', subsection_id, 'classroom_id', null)}}/>
																									</span>}
																								</span> : '--'}
																							</div>
																							{!subsection_section_locked && <div className='blue-link medium-text edit-section-hover-action'>{subsection_classroom_id ? 'Change' : 'Add'}...</div>}
																						</div>
																					) :
																					(
																						<>
																						<div className='search-add-search-container'>
																							<div className='search-add-adding-input-container'>
																								<input id={`${subsection_id}-classroom-input`} className='edit-section-input' placeholder='Search for a classroom' defaultValue={subsection_classroom_name} onChange={(e) => fuzzySearch(e, subsection_id, 'classrooms')} autoFocus={true} onFocus={handleFocus}/>
																								<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
																							</div>
																							<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes}  onClick={() => toggleEditing(subsection_id, 'classroom')}/>
																						</div>
																						{searchResults[subsection_id].classrooms.suggested.course_classroom_restrictions.length !== 0 &&
																							<>
																							<div className='search-add-did-you-mean'>Classroom(s) for this Course:</div>
																							<div className='search-add-search-results'>
																								{searchResults[subsection_id].classrooms.suggested.course_classroom_restrictions.map((classroom, i) => {
																									if(!classroom) return null;
																									const classroom_availability_index = classroomsAvailability.findIndex(classroom_availability => classroom_availability.classroom_id === classroom.classroom_id);
																									const classroom_available = (classroom_availability_index !== -1) ? classroomsAvailability[classroom_availability_index].availability : false;

																									return (
																										<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 80px auto'}}>
																											<div>{classroom.classroom_name}</div>
																						{renderAvailabilityStatus(classroom_available, 'classroom', classroom.classroom_id)}
																											<div>
																												<div className='search-add-add-btn' onClick={() => updateData('change', subsection_id, 'classroom_id', classroom.classroom_id)}>Select</div>
																											</div>
																										</div>
																									)
																								})}
																							</div>
																							</>
																						}
																						{searchResults[subsection_id].classrooms.suggested.teacher_classroom_restrictions.length !== 0 &&
																							<>
																							<div className='search-add-did-you-mean'>Teacher's Preferred Classroom(s):</div>
																							<div className='search-add-search-results'>
																								{searchResults[subsection_id].classrooms.suggested.teacher_classroom_restrictions.map((classroom, i) => {
																									if(!classroom) return null;
																									const classroom_availability_index = classroomsAvailability.findIndex(classroom_availability => classroom_availability.classroom_id === classroom.classroom_id);
																									const classroom_available = (classroom_availability_index !== -1) ? classroomsAvailability[classroom_availability_index].availability : false;

																									return (
																										<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 80px auto'}}>
																											<div>{classroom.classroom_name}</div>
																						{renderAvailabilityStatus(classroom_available, 'classroom', classroom.classroom_id)}
																											<div>
																												<div className='search-add-add-btn' onClick={() => updateData('change', subsection_id, 'classroom_id', classroom.classroom_id)}>Select</div>
																											</div>
																										</div>
																									)
																								})}
																							</div>
																							</>
																						}
																						{searchResults[subsection_id].classrooms.search.length !== 0 &&
																							<>
																							<div className='search-add-did-you-mean'>Search Results:</div>
																							<div className='search-add-search-results'>
																								{searchResults[subsection_id].classrooms.search.map((classroom, i) => {
																									const classroom_availability_index = classroomsAvailability.findIndex(classroom_availability => classroom_availability.classroom_id === classroom.classroom_id);
																									const classroom_available = (classroom_availability_index !== -1) ? classroomsAvailability[classroom_availability_index].availability : false;

																									return (
																										<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 80px auto'}}>
																											<div>{classroom.classroom_name}</div>
																						{renderAvailabilityStatus(classroom_available, 'classroom', classroom.classroom_id)}
																											<div>
																												<div className='search-add-add-btn' onClick={() => updateData('change', subsection_id, 'classroom_id', classroom.classroom_id)}>Select</div>
																											</div>
																										</div>
																									)
																								})}
																							</div>
																							</>
																						}
																						</>
																					)
																				}
																			</div>
																		</div>
																	}
																</div>
															}
														</div>
													</div>
												</div>
											)
										})}
										{!main_section_locked && <div className='edit-section-add-subsection-container'>
											<div className='edit-section-add-new-btn blue-link' onClick={() => addNewSubsection('0')}>
												<FontAwesomeIcon icon={faPlusCircle}/>
												<div>Add Subsection</div>
											</div>
											<div className='edit-section-add-new-btn orange-link' onClick={() => addNewSubsection('1')}>
												<FontAwesomeIcon icon={faPlusCircle}/>
												<div>Add Inclusion Subsection</div>
											</div>
										</div>}
										</>
									}
								</div>
								{sectionType === '1' && canEditStudents &&
									<>
									<div className='edit-section-students-container'>
										<div className={`data-modal-heading edit-section-heading ${section_theme_bg_class} ${all_sections_locked && !studentOnlyEditing ? 'locked' : ''}`}>
											<div>Students</div>
											<div className='edit-section-students-heading-student-count large-text'>
												<div>Total Students:</div>
												<div>{total_student_count}</div>
											</div>
										</div>
										<div className='edit-section-main-section-content'>
											<div className='edit-section-students-inner-parent-container'>
												<div className='edit-section-students-heading first fixed-heading-on-scroll dark-blue-text'>
													<div className={`edit-section-students-heading-label ${section_theme_bg_class} ${main_student_roster_locked ? 'locked' : ''}`}>{main_section_is_lab ? 'Lab' : 'Main Section'}</div>
													<div className='large-text'>{main_course_name}</div>
													<div className='edit-section-students-heading-student-count'>
														<FontAwesomeIcon icon={faUserGraduate}/>
														<div>{sectionsData.main.student_list.length}</div>
													</div>
												</div>
												{sectionsData.main.student_list.length > 0 && !main_student_roster_locked &&
													<div className='select-all-row'>
														<div className={`select-all-container ${main_students_selected && 'blue-text'}`}>
															<div onClick={main_students_selected ? () => toggleSelectAll('main', 'none') : () => toggleSelectAll('main', 'all')}>
																<FontAwesomeIcon icon={main_students_selected ? faMinusSquare : farSquare}/>
															</div>
															<div 
																className='select-all-dropdown-wrapper'
																ref={el => setRef(main_section_dropdown_key, el)}
																onClick={() => toggleSelectAllDropdown(main_section_dropdown_key)}
																>
																<FontAwesomeIcon 
																	className='select-all-arrow' 
																	icon={faSortDown}
																/>
																{openDropdownSections.includes(main_section_dropdown_key) && (
																	<div className='select-all-dropdown'>
																		<div className='dropdown-option' onClick={() => {
																			toggleSelectAll('main', 'all');
																		}}>
																			Select All
																		</div>
																		<div className='dropdown-option' onClick={() => {
																			toggleSelectAll('main', 'unlocked');
																		}}>
																			Select Unlocked Students
																		</div>
																		<div className='dropdown-option' onClick={() => {
																			toggleSelectAll('main', 'locked');
																		}}>
																			Select Locked Students
																		</div>
																	</div>
																)}
															</div>
														</div>
															{main_students_selected &&
																<div className='select-all-actions-container'>
																	<div className='tooltip' data-tooltip-text='Lock students into section' onClick={() => (sectionsSelectedLockedStudents['main'] || []).length ? toggleLockStudents('main', 'unlock') : toggleLockStudents('main', 'lock')}>
																		<FontAwesomeIcon className='select-all-action-btn tooltip' icon={(sectionsSelectedLockedStudents['main'] || []).length ? faLock : faUnlock}/>
																	</div>
																	{canMoveOrCopyStudents && <div className='tooltip' data-tooltip-text={(sectionsSelectedLockedStudents['main'] || []).length ? 'Cannot move locked students' : 'Move students to different section'} onClick={() => (sectionsSelectedLockedStudents['main'] || []).length ? null : showOtherSections('main', 'move')}>
																		<FontAwesomeIcon className={`select-all-action-btn tooltip ${(sectionsSelectedLockedStudents['main'] || []).length ? 'disabled' : ''}`} icon={faPeopleArrows}/>
																	</div>}
																{canMoveOrCopyStudents && <div className='tooltip' data-tooltip-text='Copy students to different section' onClick={() => showOtherSections('main', 'copy')}>
																	<FontAwesomeIcon className='select-all-action-btn tooltip' icon={faUserGroupSimple}/>
																</div>}
																	<div className='tooltip' data-tooltip-text={(sectionsSelectedLockedStudents['main'] || []).length ? 'Cannot remove locked students' : 'Remove students'} onClick={() => (sectionsSelectedLockedStudents['main'] || []).length ? null : bulkRemoveStudents('main')}>
																		<FontAwesomeIcon className={`select-all-action-btn tooltip ${(sectionsSelectedLockedStudents['main'] || []).length ? 'disabled' : ''}`} icon={faTrashAlt}/>
																	</div>
																</div>
															}
													</div>
												}
												<div className={`edit-section-student-list-container ${main_student_roster_locked ? 'locked' : ''}`}>
													{sectionsData.main.student_list.length === 0 &&
														<div className='edit-data-no-data-container'>No students are in this section yet!</div>
													}
													{sectionsData.main.student_list.map((student_id, index) => {
														const student_index = students.findIndex(student => student.student_id === student_id);
														if(student_index === -1) return null;
														const student_info = students[student_index];
															const student_selected = (sectionsSelectedStudents.main || []).includes(student_id);
														const student_locked = sectionsData.main.locked_students.includes(student_id);
														const opposite_locked_type = student_locked ? 'unlock' : 'lock';
														
														return (
															<div className='edit-section-student' key={student_id}>
																{!main_student_roster_locked && <FontAwesomeIcon className={student_selected ? 'blue-text' : 'dark-gray-text'} icon={student_selected ? faCheckSquare : farSquare} onClick={() => toggleSelectStudent(student_id, 'main')}/>}
																<div>
																	<div className={`default-student ${allow_hotlinks ? 'blue-link' : 'dark-blue-text'}`} onClick={allow_hotlinks ? () => saveSection(student_id, [...sectionsData.main.student_list]) : null}>{student_info.last_name}, {student_info.first_name} ({student_info.grade}th)</div>
																	{!main_student_roster_locked && <FontAwesomeIcon icon={student_locked ? faLock : faUnlock} className={`edit-section-lock-student-icon cursor-pointer ${student_locked ? 'bright-yellow-text' : 'gray-text'}`} onClick={() => toggleLockStudents('main', opposite_locked_type, student_id)}/>}
																</div>
																{student_locked || main_student_roster_locked ?
																	(
																		<div></div>
																	):
																	(
																		<div className="x-remove" onClick={() => updateData('remove', 'main', 'student_list', student_id)}><FontAwesomeIcon icon={faTimes}/></div>
																	)
																}
															</div>
														)
													})}
												</div>
												{!main_student_roster_locked && <div className='edit-section-add-new-btn blue-link' onClick={() => toggleEditing('main', 'students')}>
													<FontAwesomeIcon icon={faPlusCircle}/>
													<div>Add Student(s)</div>
												</div>}
												{editingTracker.main.students &&
													<div className='search-add-row-container'>
														<div className='search-add-search-container'>
															<div className='search-add-adding-input-container'>
																<input id='main-students-input' className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'main', 'students')} placeholder='Search for students' autoFocus={true} onFocus={handleFocus}/>
																<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
															</div>
															<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleEditing('main', 'students')}/>
														</div>
														{main_dropdown_visible_students.length > 0 &&
															<div className='select-all-row search-add-select-all-row'>
																<div className={`select-all-container search-add-select-all-container ${main_dropdown_students_selected && 'blue-text'}`} style={{gridTemplateColumns:'auto 1fr auto'}}>
																	<div onClick={() => toggleVisibleStudentsInAddDropdown('main')}>
																		<FontAwesomeIcon icon={main_dropdown_students_selected ? faMinusSquare : farSquare}/>
																	</div>
																	<div className='small-text'>Selected {main_dropdown_selected_students.length}</div>
																	<div
																		className='select-all-dropdown-wrapper'
																		ref={el => setRef(main_search_dropdown_key, el)}
																		onClick={() => toggleSelectAllDropdown(main_search_dropdown_key)}
																	>
																		<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
																		{openDropdownSections.includes(main_search_dropdown_key) && (
																			<div className='select-all-dropdown'>
																				<div className='dropdown-option' onClick={() => selectStudentsInAddDropdown('main', 'visible')}>
																					Select All Visible
																				</div>
																				<div className='dropdown-option' onClick={() => selectStudentsInAddDropdown('main', 'not_in_section')}>
																					Select Students Not In Section
																				</div>
																				<div className='dropdown-option' onClick={() => selectStudentsInAddDropdown('main', 'in_section')}>
																					Select Students In Section
																				</div>
																				<div className='dropdown-option' onClick={() => removeSelectedStudentsForSection('main', main_dropdown_visible_students)}>
																					Clear Visible Selection
																				</div>
																			</div>
																		)}
																	</div>
																</div>
																{main_dropdown_students_selected &&
																	<div className='select-all-actions-container search-add-bulk-actions'>
																		<div className={`select-all-action-link ${main_dropdown_students_to_add.length === 0 ? 'disabled' : ''}`} onClick={() => main_dropdown_students_to_add.length === 0 ? null : bulkUpdateStudentsFromAddDropdown('main', 'add')}>
																			Add Selected ({main_dropdown_students_to_add.length})
																		</div>
																		<div className={`select-all-action-link red-text ${main_dropdown_students_to_remove.length === 0 || main_dropdown_locked_students.length > 0 ? 'disabled' : ''}`} onClick={() => main_dropdown_students_to_remove.length === 0 || main_dropdown_locked_students.length > 0 ? null : bulkUpdateStudentsFromAddDropdown('main', 'remove')}>
																			Remove Selected ({main_dropdown_students_to_remove.length})
																		</div>
																	</div>
																}
															</div>
														}
														{searchResults.main.students.suggested.length !== 0 &&
															<>
															<div className='search-add-did-you-mean'>Students Who Requested This Course:</div>
															<div className='search-add-search-results'>
																{searchResults.main.students.suggested.map((student, i) => {
																	if(searchResults.main.students.suggested_filter_ids.length > 0 && !searchResults.main.students.suggested_filter_ids.includes(student.student_id)) return null;

																		const student_requests = student.student_requests;
																			const student_labels = getVisibleRequestLabels({student_labels: student.student_labels, course_id: sectionsData.main.course_id, course_section_label_map: courseSectionLabelMap, labels_by_id: labelsByID});
																		
																		const request_match = findCourseRequestInStudentRequests(student_requests, sectionsData.main.course_id, getRequestMatchOptionsForSection(sectionsData.main, false));
																		const priority = request_match?.priority || '1';
																		const request_is_inclusion = `${request_match?.request_info?.is_inclusion || '0'}` === '1';

																	const student_in_section_index = sectionsData.main.student_list.findIndex(student_id => student_id === student.student_id);
																	const student_in_section = (student_in_section_index !== -1) ? true : false;

																	const student_availability_index = studentsAvailability.findIndex(student_availability => student_availability.student_id === student.student_id);
																	const student_available = (!student_in_section && student_availability_index !== -1) ? studentsAvailability[student_availability_index].availability : false;

																	const student_match_index = sections.findIndex(section => sectionMatchesRequestContext(section, sectionsData.main.course_id, request_is_inclusion) && section.student_list.includes(student.student_id));
																	const student_matched = (student_in_section || student_match_index !== -1) ? true : false;
																	const student_selected = (sectionsSelectedStudents.main || []).includes(student.student_id);

																	return (
																		<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'auto 1fr 80px 80px auto'}}>
																			<div className={student_selected ? 'blue-text' : 'dark-gray-text'}>
																				<FontAwesomeIcon icon={student_selected ? faCheckSquare : farSquare} onClick={() => toggleSelectStudent(student.student_id, 'main')}/>
																			</div>
																			<div>
																				<div>{student.last_name}, {student.first_name} <span className='small-text purple-text'>{priority !== '1' && "- Alternate"}</span> {request_is_inclusion && <span className='small-text orange-text'>- Inclusion</span>}</div>
																				<div>
																						{student_labels.map((label_info, i) => (
																							<div className={`edit-student-schedule-request-label ${getLabelTextColorClass(label_info)}`} key={i}>{label_info.label}</div>
																						))}
																				</div>
																			</div>
																	{renderAvailabilityStatus(student_available, 'student', student.student_id)}
																			<div className={`small-text ${student_matched ? 'green-text' : 'red-text'}`}>{student_in_section ? 'In this section' : student_matched ? 'Matched' : 'Not Matched'}</div>
																			<div>
																				{student_in_section ? 
																					(
																						<div className='search-add-already-selected'>Selected</div>
																					): 
																					(
																						<div className='search-add-add-btn student-search-btn' onClick={() => updateData('add', 'main', 'student_list', student.student_id)}>Add</div>
																					)
																				}
																			</div>
																		</div>
																	)
																})}
															</div>
															</>
														}
														{searchResults.main.students.search.length !== 0 &&
															<>
															<div className='search-add-did-you-mean'>Search Results:</div>
															<div className='search-add-search-results'>
																{searchResults.main.students.search.map((student, i) => {
																	const student_in_section_index = sectionsData.main.student_list.findIndex(student_id => student_id === student.student_id);
																	const student_in_section = (student_in_section_index !== -1) ? true : false;

																	const student_availability_index = studentsAvailability.findIndex(student_availability => student_availability.student_id === student.student_id);
																	const student_available = (!student_in_section && student_availability_index !== -1) ? studentsAvailability[student_availability_index].availability : false;
																	const student_selected = (sectionsSelectedStudents.main || []).includes(student.student_id);

																	return (
																		<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'auto 1fr 80px 80px auto'}}>
																			<div className={student_selected ? 'blue-text' : 'dark-gray-text'}>
																				<FontAwesomeIcon icon={student_selected ? faCheckSquare : farSquare} onClick={() => toggleSelectStudent(student.student_id, 'main')}/>
																			</div>
																			<div>{student.last_name}, {student.first_name}</div>
																	{renderAvailabilityStatus(student_available, 'student', student.student_id)}
																			<div className={`small-text ${student_in_section ? 'green-text' : 'white-text'}`}>{student_in_section ? 'In this section' : '.'}</div>
																			<div>
																				{student_in_section ? 
																					(
																						<div className='search-add-already-selected'>Selected</div>
																					): 
																					(
																						<div className='search-add-add-btn student-search-btn' onClick={() => updateData('add', 'main', 'student_list', student.student_id)}>Add</div>
																					)
																				}
																			</div>
																		</div>
																	)
																})}
															</div>
															</>
														}
													</div>
												}
												</div>
												{sectionsData['main'].subsections.map((subsection_id, subsection_index) => {
													const subsection_info = sectionsData[subsection_id];
													if(!subsection_info) return null;
													const subsection_editing_tracker = editingTracker[subsection_id] || {course: false, teacher: false, students: false, section_number:false, label_options_open:false, advanced_settings_open:false};
													const subsection_course_id = subsection_info.course_id;
												const subsection_student_list = subsection_info.student_list;
												const subsection_is_inclusion = (subsection_info.is_inclusion === '1');
												
												const subsection_course_info = (subsection_course_id) ? courses.find(course => course.course_id === subsection_course_id) : null;
												const subsection_course_name = (subsection_course_info) ? `${subsection_course_info.name} (${subsection_course_info.course_code})` : '';
												const subsection_student_roster_locked = sectionIsLockedForStudentEditing(subsection_id);

												let is_placeholder_inclusion = false;
												if(scheduleInfo.separate_ICR_codes === '0' && sectionsData.main.course_id === subsection_course_id && subsection_is_inclusion) is_placeholder_inclusion = true;

												const subsection_students_selected = (sectionsSelectedStudents[subsection_id] && sectionsSelectedStudents[subsection_id].filter(student_id => subsection_student_list.includes(student_id)).length > 0);
												const subsection_section_dropdown_key = getSelectAllDropdownKey(subsection_id);
												const subsection_search_dropdown_key = getSelectAllDropdownKey(subsection_id, 'student-search');
												const subsection_dropdown_visible_students = getVisibleStudentsInAddDropdown(subsection_id);
												const subsection_dropdown_selected_students = (sectionsSelectedStudents[subsection_id] || []).filter(student_id => subsection_dropdown_visible_students.includes(student_id));
												const subsection_dropdown_students_selected = subsection_dropdown_selected_students.length > 0;
												const subsection_dropdown_students_to_add = subsection_dropdown_selected_students.filter(student_id => !sectionsData[subsection_id].student_list.includes(student_id));
												const subsection_dropdown_students_to_remove = subsection_dropdown_selected_students.filter(student_id => sectionsData[subsection_id].student_list.includes(student_id));
												const subsection_dropdown_locked_students = subsection_dropdown_students_to_remove.filter(student_id => sectionsData[subsection_id].locked_students.includes(student_id));
												
												return (
													<div className='edit-section-students-inner-parent-container' key={subsection_id}>
														<div className='edit-section-students-heading fixed-heading-on-scroll dark-blue-text'>
															<div className={`edit-section-students-heading-label ${subsection_is_inclusion ? 'yellow' : 'blue'} ${subsection_student_roster_locked ? 'locked' : ''}`}>{subsection_is_inclusion && 'Inclusion'} Subsection</div>
															<div className='large-text'>{!is_placeholder_inclusion && subsection_course_name}</div>
															<div className='edit-section-students-heading-student-count'>
																<FontAwesomeIcon icon={faUserGraduate}/>
																<div>{subsection_student_list.length}</div>
															</div>
														</div>
														{subsection_student_list.length > 0 && !subsection_student_roster_locked &&
															<div className='select-all-row'>
																<div className={`select-all-container ${subsection_students_selected && 'blue-text'}`}>
																	<div onClick={subsection_students_selected ? () => toggleSelectAll(subsection_id, 'none') : () => toggleSelectAll(subsection_id, 'all')}>
																		<FontAwesomeIcon icon={subsection_students_selected ? faMinusSquare : farSquare}/>
																	</div>
																	<div 
																		className='select-all-dropdown-wrapper' 
																		ref={el => setRef(subsection_section_dropdown_key, el)}
																		onClick={() => toggleSelectAllDropdown(subsection_section_dropdown_key)}
																		>
																		<FontAwesomeIcon 
																			className='select-all-arrow' 
																			icon={faSortDown}
																		/>
																		{openDropdownSections.includes(subsection_section_dropdown_key) && (
																			<div className='select-all-dropdown'>
																				<div className='dropdown-option' onClick={() => {
																					toggleSelectAll(subsection_id, 'all');
																				}}>
																					Select All
																				</div>
																				<div className='dropdown-option' onClick={() => {
																					toggleSelectAll(subsection_id, 'unlocked');
																				}}>
																					Select Unlocked Students
																				</div>
																				<div className='dropdown-option' onClick={() => {
																					toggleSelectAll(subsection_id, 'locked');
																				}}>
																					Select Locked Students
																				</div>
																			</div>
																		)}
																	</div>
																</div>
																	{subsection_students_selected &&
																		<div className='select-all-actions-container'>
																			<div className='tooltip' data-tooltip-text='Lock students into section' onClick={() => (sectionsSelectedLockedStudents[subsection_id] || []).length ? toggleLockStudents(subsection_id, 'unlock') : toggleLockStudents(subsection_id, 'lock')}>
																				<FontAwesomeIcon className='select-all-action-btn tooltip' icon={(sectionsSelectedLockedStudents[subsection_id] || []).length ? faLock : faUnlock}/>
																			</div>
																			{canMoveOrCopyStudents && <div className='tooltip' data-tooltip-text={(sectionsSelectedLockedStudents[subsection_id] || []).length ? 'Cannot move locked students' : 'Move students to different section'} onClick={() =>  (sectionsSelectedLockedStudents[subsection_id] || []).length ? null : showOtherSections(subsection_id, 'move')}>
																				<FontAwesomeIcon className={`select-all-action-btn tooltip ${(sectionsSelectedLockedStudents[subsection_id] || []).length ? 'disabled' : ''}`} icon={faPeopleArrows}/>
																			</div>}
																			{canMoveOrCopyStudents && <div className='tooltip' data-tooltip-text='Copy students to different section' onClick={() => showOtherSections(subsection_id, 'copy')}>
																				<FontAwesomeIcon className='select-all-action-btn tooltip' icon={faUserGroupSimple}/>
																			</div>}
																			<div className='tooltip' data-tooltip-text={(sectionsSelectedLockedStudents[subsection_id] || []).length ? 'Cannot remove locked students' : 'Remove students'} onClick={() => (sectionsSelectedLockedStudents[subsection_id] || []).length ? null : bulkRemoveStudents(subsection_id)}>
																				<FontAwesomeIcon className={`select-all-action-btn tooltip ${(sectionsSelectedLockedStudents[subsection_id] || []).length ? 'disabled' : ''}`} icon={faTrashAlt}/>
																			</div>
																		</div>
																	}
															</div>
														}
														<div className={`edit-section-student-list-container ${subsection_student_roster_locked ? 'locked' : ''}`}>
															{sectionsData[subsection_id].student_list.length === 0 &&
																<div className='edit-data-no-data-container'>No students are in this subsection yet!</div>
															}
																{subsection_student_list.map((student_id, index) => {
																	const student_index = students.findIndex(student => student.student_id === student_id);
																	if(student_index === -1) return null;
																	const student_info = students[student_index];
																	const student_selected = (sectionsSelectedStudents[subsection_id] || []).includes(student_id);
																	const student_locked = sectionsData[subsection_id].locked_students.includes(student_id);
																	const opposite_locked_type = student_locked ? 'unlock' : 'lock';
																
																return (
																	<div className='edit-section-student' key={student_id}>
																		{!subsection_student_roster_locked && <FontAwesomeIcon className={student_selected ? 'blue-text' : 'dark-gray-text'} icon={student_selected ? faCheckSquare : farSquare} onClick={() => toggleSelectStudent(student_id, subsection_id)}/>}
																		<div>
																			<div className={`default-student ${allow_hotlinks ? 'blue-link' : 'dark-blue-text'}`} onClick={allow_hotlinks ? () => saveSection(student_id, [...subsection_student_list]) : null}>{student_info.last_name}, {student_info.first_name} ({student_info.grade}th)</div>
																			{!subsection_student_roster_locked && <FontAwesomeIcon icon={student_locked ? faLock : faUnlock} className={`edit-section-lock-student-icon cursor-pointer ${student_locked ? 'bright-yellow-text' : 'gray-text'}`} onClick={() => toggleLockStudents(subsection_id, opposite_locked_type, student_id)}/>}
																		</div>
																		{student_locked || subsection_student_roster_locked ?
																			(
																				<div></div>
																			):
																			(
																				<div className="x-remove" onClick={() => updateData('remove', subsection_id, 'student_list', student_id)}><FontAwesomeIcon icon={faTimes}/></div>
																			)
																		}
																	</div>
																)
															})}
														</div>
														{!subsection_student_roster_locked && <div className='edit-section-add-new-btn blue-link' onClick={() => toggleEditing(subsection_id, 'students')}>
															<FontAwesomeIcon icon={faPlusCircle}/>
															<div>Add Student(s)</div>
														</div>}
														{subsection_editing_tracker.students &&
															<div className='search-add-row-container'>
																<div className='search-add-search-container'>
																	<div className='search-add-adding-input-container'>
																		<input id={`${subsection_id}-students-input`} className='search-add-adding-input' onChange={(e) => fuzzySearch(e, subsection_id, 'students')} placeholder='Search for students' autoFocus={true} onFocus={handleFocus}/>
																		<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
																	</div>
																	<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleEditing(subsection_id, 'students')}/>
																</div>
																{subsection_dropdown_visible_students.length > 0 &&
																	<div className='select-all-row search-add-select-all-row'>
																		<div className={`select-all-container search-add-select-all-container ${subsection_dropdown_students_selected && 'blue-text'}`} style={{gridTemplateColumns:'auto 1fr auto'}}>
																			<div onClick={() => toggleVisibleStudentsInAddDropdown(subsection_id)}>
																				<FontAwesomeIcon icon={subsection_dropdown_students_selected ? faMinusSquare : farSquare}/>
																			</div>
																			<div className='small-text'>Selected {subsection_dropdown_selected_students.length}</div>
																			<div
																				className='select-all-dropdown-wrapper'
																				ref={el => setRef(subsection_search_dropdown_key, el)}
																				onClick={() => toggleSelectAllDropdown(subsection_search_dropdown_key)}
																			>
																				<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
																				{openDropdownSections.includes(subsection_search_dropdown_key) && (
																					<div className='select-all-dropdown'>
																						<div className='dropdown-option' onClick={() => selectStudentsInAddDropdown(subsection_id, 'visible')}>
																							Select All Visible
																						</div>
																						<div className='dropdown-option' onClick={() => selectStudentsInAddDropdown(subsection_id, 'not_in_section')}>
																							Select Students Not In Section
																						</div>
																						<div className='dropdown-option' onClick={() => selectStudentsInAddDropdown(subsection_id, 'in_section')}>
																							Select Students In Section
																						</div>
																						<div className='dropdown-option' onClick={() => removeSelectedStudentsForSection(subsection_id, subsection_dropdown_visible_students)}>
																							Clear Visible Selection
																						</div>
																					</div>
																				)}
																			</div>
																		</div>
																		{subsection_dropdown_students_selected &&
																			<div className='select-all-actions-container search-add-bulk-actions'>
																				<div className={`select-all-action-link ${subsection_dropdown_students_to_add.length === 0 ? 'disabled' : ''}`} onClick={() => subsection_dropdown_students_to_add.length === 0 ? null : bulkUpdateStudentsFromAddDropdown(subsection_id, 'add')}>
																					Add Selected ({subsection_dropdown_students_to_add.length})
																				</div>
																				<div className={`select-all-action-link red-text ${subsection_dropdown_students_to_remove.length === 0 || subsection_dropdown_locked_students.length > 0 ? 'disabled' : ''}`} onClick={() => subsection_dropdown_students_to_remove.length === 0 || subsection_dropdown_locked_students.length > 0 ? null : bulkUpdateStudentsFromAddDropdown(subsection_id, 'remove')}>
																					Remove Selected ({subsection_dropdown_students_to_remove.length})
																				</div>
																			</div>
																		}
																	</div>
																}
																{searchResults[subsection_id].students.suggested.length !== 0 &&
																	<>
																	<div className='search-add-did-you-mean'>Students Who Requested This Course:</div>
																	<div className='search-add-search-results'>
																		{searchResults[subsection_id].students.suggested.map((student, i) => {
																			if(searchResults[subsection_id].students.suggested_filter_ids.length > 0 && !searchResults[subsection_id].students.suggested_filter_ids.includes(student.student_id)) return null;
																			
																				const student_requests = student.student_requests;
																					const student_labels = getVisibleRequestLabels({student_labels: student.student_labels, course_id: sectionsData[subsection_id].course_id, course_section_label_map: courseSectionLabelMap, labels_by_id: labelsByID});
																				
																				const request_match = findCourseRequestInStudentRequests(student_requests, sectionsData[subsection_id].course_id, getRequestMatchOptionsForSection(sectionsData[subsection_id], false));
																				const priority = request_match?.priority || '1';
																				const request_is_inclusion = `${request_match?.request_info?.is_inclusion || '0'}` === '1';

																			const student_in_section_index = sectionsData[subsection_id].student_list.findIndex(student_id => student_id === student.student_id);
																			const student_in_section = (student_in_section_index !== -1) ? true : false;

																			const student_availability_index = studentsAvailability.findIndex(student_availability => student_availability.student_id === student.student_id);
																			const student_available = (!student_in_section && student_availability_index !== -1) ? studentsAvailability[student_availability_index].availability : false;

																			const student_match_index = sections.findIndex(section => sectionMatchesRequestContext(section, sectionsData[subsection_id].course_id, request_is_inclusion) && section.student_list.includes(student.student_id));
																			const student_matched = (student_in_section || student_match_index !== -1) ? true : false;
																			const student_selected = (sectionsSelectedStudents[subsection_id] || []).includes(student.student_id);
				
																			return (
																				<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'auto 1fr 80px 80px auto'}}>
																					<div className={student_selected ? 'blue-text' : 'dark-gray-text'}>
																						<FontAwesomeIcon icon={student_selected ? faCheckSquare : farSquare} onClick={() => toggleSelectStudent(student.student_id, subsection_id)}/>
																					</div>
																					<div>
																						<div>{student.last_name}, {student.first_name} <span className='small-text purple-text'>{priority !== '1' && "- Alternate"}</span> {request_is_inclusion && <span className='small-text orange-text'>- Inclusion</span>}</div>
																						<div>
																								{student_labels.map((label_info, i) => (
																									<div className={`edit-student-schedule-request-label ${getLabelTextColorClass(label_info)}`} key={i}>{label_info.label}</div>
																								))}
																						</div>
																					</div>
																		{renderAvailabilityStatus(student_available, 'student', student.student_id)}
																					<div className={`small-text ${student_matched ? 'green-text' : 'red-text'}`}>{student_in_section ? 'In this section' : student_matched ? 'Matched' : 'Not Matched'}</div>
																					<div>
																						{student_in_section ? 
																							(
																								<div className='search-add-already-selected'>Selected</div>
																							): 
																							(
																								<div className='search-add-add-btn student-search-btn' onClick={() => updateData('add', subsection_id, 'student_list', student.student_id)}>Add</div>
																							)
																						}
																					</div>
																				</div>
																			)
																		})}
																	</div>
																	</>
																}
																{searchResults[subsection_id].students.search.length !== 0 &&
																	<>
																	<div className='search-add-did-you-mean'>Search Results:</div>
																	<div className='search-add-search-results'>
																		{searchResults[subsection_id].students.search.map((student, i) => {
																			const student_in_section_index = sectionsData[subsection_id].student_list.findIndex(student_id => student_id === student.student_id);
																			const student_in_section = (student_in_section_index !== -1) ? true : false;

																			const student_availability_index = studentsAvailability.findIndex(student_availability => student_availability.student_id === student.student_id);
																			const student_available = (!student_in_section && student_availability_index !== -1) ? studentsAvailability[student_availability_index].availability : false;
																			const student_selected = (sectionsSelectedStudents[subsection_id] || []).includes(student.student_id);
				
																			return (
																				<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'auto 1fr 80px 80px auto'}}>
																					<div className={student_selected ? 'blue-text' : 'dark-gray-text'}>
																						<FontAwesomeIcon icon={student_selected ? faCheckSquare : farSquare} onClick={() => toggleSelectStudent(student.student_id, subsection_id)}/>
																					</div>
																					<div>{student.last_name}, {student.first_name}</div>
																		{renderAvailabilityStatus(student_available, 'student', student.student_id)}
																					<div className={`small-text ${student_in_section ? 'green-text' : 'white-text'}`}>{student_in_section ? 'In this section' : '.'}</div>
																					<div>
																						{student_in_section ? 
																							(
																								<div className='search-add-already-selected'>Selected</div>
																							): 
																							(
																								<div className='search-add-add-btn student-search-btn' onClick={() => updateData('add', subsection_id, 'student_list', student.student_id)}>Add</div>
																							)
																						}
																					</div>
																				</div>
																			)
																		})}
																	</div>
																	</>
																}
															</div>
														}
													</div>
												)
											})}
										</div>
									</div>
									</>
								}
							</form>
						}
					</div>
				</div>
			</div>
		</div>
	);
}
